/* ============================================================
   SURREALIUM — GALLERY GOTHIC · theme runtime
   Dependency-free custom elements. Everything degrades:
   without JS the logo renders docked, films show their first
   frame, carousels scroll natively, the cart falls back to
   /cart form posts.
   ============================================================ */

(() => {
  'use strict';

  window.ggRefreshProductMediaSections = async ({ handle } = {}) => {
    if (!handle || location.pathname !== `/products/${encodeURIComponent(handle)}`) return false;
    const sections = [...document.querySelectorAll('.gg-pdp__gallery, .gg-story')]
      .map((el) => el.closest('[id^="shopify-section-"]'))
      .filter((el, index, all) => el && all.indexOf(el) === index);
    if (!sections.length) return false;
    let changed = false;
    for (const current of sections) {
      const sectionId = current.id.replace(/^shopify-section-/, '');
      const url = new URL(location.href);
      url.searchParams.set('section_id', sectionId);
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } });
      if (!response.ok) continue;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.getElementById(current.id);
      if (!fresh || !current.isConnected) continue;
      current.dispatchEvent(new CustomEvent('shopify:section:unload', { bubbles: true, detail: { sectionId } }));
      const replacement = document.importNode(fresh, true);
      current.parentNode.replaceChild(replacement, current);
      replacement.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true, detail: { sectionId } }));
      changed = true;
    }
    return changed;
  };

  // Restore country and dependent province choices before an address is edited.
  function addressFields(root = document) {
    root.querySelectorAll('[data-address-country]').forEach((country) => {
      if (country.dataset.addressReady) return;
      country.dataset.addressReady = 'true';
      const province = document.getElementById(country.dataset.provinceId);
      if (!province) return;
      const saved = country.dataset.default;
      if ([...country.options].some((o) => o.value === saved)) country.value = saved;
      const update = (restore = false) => {
        let options = [];
        try { options = JSON.parse(country.selectedOptions[0]?.dataset.provinces || '[]'); } catch {}
        province.replaceChildren(...options.map(([value, label]) => new Option(label, value)));
        province.closest('[data-address-province]').hidden = !options.length;
        province.disabled = !options.length;
        if (restore && [...province.options].some((o) => o.value === province.dataset.default)) province.value = province.dataset.default;
      };
      update(true);
      country.addEventListener('change', () => update());
    });
  }
  addressFields();
  document.addEventListener('shopify:section:load', (e) => addressFields(e.target));

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ----------------------------------------------------------
     Scroll reveal — IntersectionObserver, reversing on scroll up.
     Element is "in" while its top is above 85% of the viewport
     and its bottom below 12%.
     ---------------------------------------------------------- */
  const revealTargets = new Set();
  const revealSelector = '[data-reveal], #MainContent .gg-card';
  const revealObserver = new IntersectionObserver((entries) => {
    const entering = entries.filter(entry => entry.isIntersecting)
      .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top || a.boundingClientRect.left - b.boundingClientRect.left);
    for (const entry of entries) {
      const sequence = entering.filter(item => item.target.matches('.gg-card') === entry.target.matches('.gg-card'));
      const delay = entry.isIntersecting ? Math.min(sequence.indexOf(entry), 7) * 65 : 0;
      entry.target.style.setProperty('--reveal-delay', `${delay}ms`);
      entry.target.classList.toggle('is-revealed', entry.isIntersecting || reducedMotion.matches);
    }
  }, { rootMargin: '0px 0px -5% 0px', threshold: 0 });

  function observeReveals(root = document) {
    const nodes = [...root.querySelectorAll(revealSelector)];
    if (root.matches?.(revealSelector)) nodes.unshift(root);
    for (const el of nodes) {
      if (revealTargets.has(el)) continue;
      revealTargets.add(el);
      if (el.matches('.gg-card')) el.classList.add('gg-motion-card');
      revealObserver.observe(el);
    }
  }
  observeReveals();
  document.addEventListener('shopify:section:load', (e) => observeReveals(e.target));
  document.addEventListener('collection:ready', () => {
    for (const el of revealTargets) {
      el.classList.remove('is-revealed');
      revealObserver.unobserve(el);
      revealObserver.observe(el);
    }
  });
  reducedMotion.addEventListener('change', () => {
    for (const el of revealTargets) {
      if (reducedMotion.matches) el.classList.add('is-revealed');
      else { revealObserver.unobserve(el); revealObserver.observe(el); }
    }
  });
  const revealScanner = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1) observeReveals(node);
      }
    }
    for (const el of revealTargets) {
      if (!el.isConnected) { revealObserver.unobserve(el); revealTargets.delete(el); }
    }
  });
  revealScanner.observe(document.documentElement, { childList: true, subtree: true });

  /* ----------------------------------------------------------
     <gg-header-dock> — the signature logo dock.
     p = clamp(scrollY / 420, 0, 1)  (240 on a phone)
     logo: translateY(open→0) scale(open→docked width)
     header bg rgba(9,10,12,p) · --gg-dock p · border rgba(255,255,255,.08p)
     hero tagline opacity 1−1.7p · scroll cue opacity 1−3p
     Open, the wordmark starts on the menu's line at the top of the screen,
     and the menu sits in its ears — the space above the short letters,
     either side of the l and the i. Where the ears are too tight to hold
     the menu (a phone) it rises only until its short letters meet the foot
     of the bar, the l and the i reaching up between the icons; where even
     they would touch the menu, it opens under the bar.
     ---------------------------------------------------------- */
  // the wordmark's shape (assets/surrealium_logo_white.png, 1800 × 349): only the l's flag and the i's diamond rise
  // above the points of the short letters, 104px down; the two span 61.6%–72.7% of its width
  const EAR = 104 / 349;
  const TALL = [0.6156, 0.7267];
  class GGHeaderDock extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('[data-dock-bar]');
      this.logo = this.querySelector('[data-dock-logo]');
      this.update = this.update.bind(this);
      let queued = false;
      this.onScroll = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          this.update();
        });
      };
      // Hero detection happens at update time, including the editorial homepage.
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      // the open wordmark is placed by measure: again once its picture and the menu's face have loaded
      window.addEventListener('load', this.onScroll);
      this.logo?.addEventListener('load', this.onScroll);
      document.fonts?.addEventListener?.('loadingdone', this.onScroll);
      // the announcement stands on a film or picture cover (gallery-gothic.css): its height is how far the cover reaches
      // up above the bar
      const marquee = document.querySelector('.gg-section-marquee');
      if (marquee && 'ResizeObserver' in window) {
        const reach = () => document.documentElement.style.setProperty('--gg-marquee-h', `${marquee.offsetHeight}px`);
        reach();
        this.marqueeObserver = new ResizeObserver(reach);
        this.marqueeObserver.observe(marquee);
      }
      if ('ResizeObserver' in window) {
        const widths = new WeakMap();
        this.widthObserver = new ResizeObserver((entries) => {
          let changed = false;
          entries.forEach(({ target, contentRect }) => {
            if (widths.get(target) !== contentRect.width) {
              widths.set(target, contentRect.width);
              changed = true;
            }
          });
          if (changed) this.onScroll();
        });
        this.widthObserver.observe(document.body);
        if (this.bar) this.widthObserver.observe(this.bar);
      }
      const menu = this.querySelector('.gg-mobile-menu');
      if (menu) {
        this.menuObserver = new MutationObserver(this.onScroll);
        this.menuObserver.observe(menu, {attributes:true, attributeFilter:['hidden','class']});
      }
      this.update();
    }

    disconnectedCallback() {
      this.menuObserver?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      window.removeEventListener('load', this.onScroll);
      document.fonts?.removeEventListener?.('loadingdone', this.onScroll);
      this.logo?.removeEventListener('load', this.onScroll);
      this.marqueeObserver?.disconnect();
      this.widthObserver?.disconnect();
    }

    update() {
      // the bar drawn again under the element (the Composer or the Lab refreshing the header): hold the one on the page
      if (!this.bar?.isConnected) this.bar = this.querySelector('[data-dock-bar]');
      if (!this.logo?.isConnected) this.logo = this.querySelector('[data-dock-logo]');
      const menu = this.querySelector('.gg-mobile-menu');
      if (menu && !menu.hidden && this.bar) {
        const box = menu.getBoundingClientRect();
        const scale = box.width / menu.offsetWidth || 1;
        // Opening motion must not become permanent padding after the animation ends.
        const transform = getComputedStyle(menu).transform;
        const shift = transform === 'none' ? 0 : new DOMMatrixReadOnly(transform).m42;
        const origin = box.top - shift * scale;
        menu.style.setProperty('--menu-header-space', `${Math.max(0,(this.bar.getBoundingClientRect().bottom - origin) / scale)}px`);
      }
      const hero = document.querySelector('[data-gg-hero]');
      const hasHero = !!hero;
      const pageW = document.body.clientWidth || window.innerWidth;
      const mobile = pageW < 750;
      const compactCover = mobile && hero?.matches('.gg-cover--cover');
      const p = !hasHero || compactCover ? 1 : clamp(window.scrollY / (mobile ? 240 : 420), 0, 1);
      // a cover that keeps the wordmark in the bar from the start (the title sequence's Maison, Folio and Oxblood): the
      // wordmark stands docked while the bar still clears over the film
      const pm = hero?.hasAttribute('data-mark-docked') ? 1 : p;
      if (this.logo) {
        const gutter = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--gutter'), 10) || 40;
        // the link's own width: on a very wide screen the CSS caps it (1200px, 94vh) short of the page
        const width = this.logo.offsetWidth || pageW - (mobile ? 16 : gutter * 2);
        const dockWidth = mobile ? Math.min(160, Math.max(100, pageW - 180)) : Math.min(220, pageW * .18);
        let startY = 0;
        let land = compactCover ? .48 : 1;
        const h = this.logo.offsetHeight;
        const wrap = this.logo.closest('.gg-header__logo-wrap');
        // Correct any asymmetric header grid gutters without following the icon gap.
        const centreX = mobile && wrap && this.bar
          ? this.bar.getBoundingClientRect().left + this.bar.getBoundingClientRect().width / 2 - wrap.getBoundingClientRect().left - wrap.getBoundingClientRect().width / 2
          : 0;
        if (hasHero && h && wrap && this.bar) {
          // measured from the top of the bar, as it stands before any scroll: the cover begins behind the bar
          const bar = this.bar.getBoundingClientRect();
          const box = wrap.getBoundingClientRect();
          const centre = box.top - bar.top + box.height / 2;
          // on a short window the open wordmark, edge to edge, would crowd the cover's words at the foot: it lands
          // smaller — as large as leaves 120px of air above the words, never under 60%
          const copy = mobile ? null : hero.querySelector('.gg-cover__copy');
          const room = copy ? copy.getBoundingClientRect().top - hero.getBoundingClientRect().top - 120 : Infinity;
          const fit = (top) => clamp((room - top) / h, .6, 1);
          // the menu either side of the tall letters: the right edge of its left group, the left edge of its right one
          // (a phone's icons are 44px targets and carry their own air; the desktop's words keep 16px from the letters)
          const pad = mobile ? 0 : 16;
          const [inL, inR] = [...this.bar.querySelectorAll('.gg-header__nav > *, .gg-header__actions > *')]
            .map((el) => el.getBoundingClientRect()).filter((r) => r.width)
            .reduce(([l, r], m) => (m.left + m.right < 2 * box.left + box.width ? [Math.max(l, m.right), r] : [l, Math.min(r, m.left)]), [-Infinity, Infinity]);
          const clear = (scale) => {
            const w = this.logo.offsetWidth * scale;
            const left = box.left + (box.width - w) / 2;
            return inL + pad <= left + w * TALL[0] && inR - pad >= left + w * TALL[1];
          };
          // the ears (a desktop): the tops of the l and the i on the menu's line — its 10–11px capitals start 3px above
          // the bar's middle — the menu standing in the space above the short letters, clear of the tall ones
          let top = bar.height / 2 - 3;
          land = fit(top);
          if (mobile || !clear(land)) {
            // too tight (a phone): the short letters' points meet the foot of the bar and the l and the i rise into it,
            // between the menu's two sides; where even they would touch the menu, the wordmark opens under the bar
            land = fit(bar.height);
            top = mobile && clear(land) ? bar.height - h * land * EAR : bar.height;
          }
          if (compactCover) {
            let glyphs = [...this.bar.querySelectorAll('.gg-header__icon, .gg-header__burger span')]
              .map((el) => el.getBoundingClientRect()).filter((r) => r.width && r.height);
            if (!glyphs.length) {
              glyphs = [...this.bar.querySelectorAll('.gg-header__nav > a, .gg-shopdd > summary, .gg-header__actions > *')]
                .map((el) => el.getBoundingClientRect()).filter((r) => r.width && r.height);
            }
            const mid = bar.left + bar.width / 2;
            const left = Math.max(bar.left + 12, ...glyphs.filter((r) => r.right <= mid).map((r) => r.right + 8));
            const right = Math.min(bar.right - 12, ...glyphs.filter((r) => r.left >= mid).map((r) => r.left - 8));
            // Keep the wordmark on the page centreline, even with two icons on the right.
            const centredRoom = Math.max(0, 2 * Math.min(mid - left, right - mid));
            land = Math.min(.48, centredRoom / width);
            top = glyphs.length ? Math.min(...glyphs.map((r) => r.top)) - bar.top : (bar.height - 18) / 2;
          }
          if (hero.dataset.logoPlacement === 'bottom') {
            const caption = hero.querySelector('.gg-diptych__caption');
            const announcementHeight = document.querySelector('.gg-section-marquee')?.offsetHeight || 0;
            land = 1;
            const seam = hero.querySelector('.gg-diptych__frame--right');
            top = mobile && seam
              ? seam.offsetTop - announcementHeight - h * .65
              : hero.offsetHeight - (caption?.offsetHeight || 0) - announcementHeight - h - 17;
          }
          startY = top + h * land / 2 - centre;
        }
        const scale = land + (dockWidth / width - land) * pm;
        this.logo.style.transform = `translate(${centreX}px, ${startY * (1 - pm)}px) scale(${scale})`;
        // the same, as a variable: the phone's bar holds its wordmark still (transform: none !important), and a cover that
        // opens it across the screen (the title sequence's Maison, wordmark — gallery-gothic.css) takes the dock's from here
        this.logo.style.setProperty('--gg-dock-tf', this.logo.style.transform);
      }
      const mm = this.querySelector('.gg-header__logo-mobile');
      if (mm) mm.style.opacity = '';
      if (this.bar) {
        this.bar.style.background = compactCover ? '#000' : `rgba(9, 10, 12, ${p})`;
        this.bar.style.borderBottomColor = `color-mix(in srgb, var(--accent) ${11 * p}%, transparent)`;
        this.bar.style.setProperty('--gg-dock', String(p));
      }
      // open over a cover (not yet docked): the Lab lets the pointer through it and keeps its toolbars below it
      this.classList.toggle('is-open', hasHero && p < 1);
      const tagline = document.querySelector('[data-gg-tagline]');
      if (tagline) tagline.style.opacity = String(Math.max(0, 1 - p * 1.7));
      const cue = document.querySelector('[data-gg-scrollcue]');
      if (cue) cue.style.opacity = String(Math.max(0, 1 - p * 3));
    }
  }
  customElements.define('gg-header-dock', GGHeaderDock);

  /* ----------------------------------------------------------
     <gg-film-scrub> — scroll-scrubbed product film.
     q = clamp((scrollY − sectionTop) / (sectionH − stageH), 0, 1)
     · video.currentTime = q × duration (desktop)
     · placeholder pan: translateX(12·dir·(1−q))% scale(1.06),
       or scale(1.3 − 0.24q) when dir = 0
     · product layer: f = clamp((q−0.12)/0.58) → opacity .22+.78f,
       scale 1.18−0.18f, blur (1−f)·8px
     · caption: t = clamp((q−0.42)/0.24) → opacity t, y (1−t)·30px
     · scrubber width q·100% · timecode counts up
     Mobile (<750px): stage is static; the video simply autoplays
     in a loop and the caption is fully visible.
     ---------------------------------------------------------- */
  class GGFilmScrub extends HTMLElement {
    connectedCallback() {
      this.dir = parseFloat(this.dataset.panDirection || '1');
      this.inView = false;
      this.cacheEls();

      this.io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            this.inView = e.isIntersecting;
            if (e.isIntersecting) this.sync();
            this.syncMobileVideo();
          }
        },
        { rootMargin: '80px' }
      );
      this.io.observe(this);

      let queued = false;
      this.onScroll = () => {
        if (!this.inView || queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          this.sync();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.mq = window.matchMedia('(max-width: 749px)');
      this.mq.addEventListener?.('change', () => this.syncMobileVideo());

      // hot reload / theme editor patches the section DOM in place,
      // swapping our children out from under the cached refs — re-cache
      // and re-sync when that happens
      // the stage is a direct child, so childList (no subtree) catches a
      // hot-reload swap without firing on the per-frame timecode update
      this.__mo = new MutationObserver(() => {
        if (!this.stage || !this.stage.isConnected) {
          this.cacheEls();
          this.sync();
        }
      });
      this.__mo.observe(this, { childList: true });

      this.sync();
    }

    cacheEls() {
      this.stage = this.querySelector('[data-film-stage]');
      this.video = this.querySelector('[data-film-video]');
      this.pan = this.querySelector('[data-film-pan]');
      this.product = this.querySelector('[data-film-product]');
      this.caption = this.querySelector('[data-film-caption]');
      this.fill = this.querySelector('[data-film-fill]');
      this.timecode = this.querySelector('[data-film-timecode]');
      if (this.video && !this.video.__ggScrubBound) {
        this.video.__ggScrubBound = true;
        this.video.addEventListener('loadedmetadata', () => this.sync());
      }
    }

    disconnectedCallback() {
      this.io?.disconnect();
      this.__mo?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
    }

    get isMobile() {
      return this.mq.matches;
    }

    syncMobileVideo() {
      if (!this.video) return;
      if (this.isMobile) {
        this.video.loop = true;
        if (this.inView) this.video.play?.().catch(() => {});
        else this.video.pause?.();
      } else {
        this.video.loop = false;
        this.video.pause?.();
      }
    }

    sync() {
      if (this.isMobile || reducedMotion.matches) {
        // static/autoplay presentation — caption fully in, bar full
        if (this.caption) {
          this.caption.style.opacity = '1';
          this.caption.style.transform = 'none';
        }
        if (this.fill) this.fill.style.width = '100%';
        return;
      }
      const rect = this.getBoundingClientRect();
      const stageH = this.stage ? this.stage.clientHeight : window.innerHeight;
      const total = Math.max(1, rect.height - stageH);
      const q = clamp(-rect.top / total, 0, 1);

      if (this.video && this.video.duration) {
        // scrub a muted <video> by scroll progress
        this.video.currentTime = q * this.video.duration;
      }
      if (this.pan) {
        this.pan.style.transform =
          this.dir === 0
            ? `scale(${1.3 - 0.24 * q})`
            : `translateX(${12 * this.dir * (1 - q)}%) scale(1.06)`;
      }
      if (this.product) {
        const f = clamp((q - 0.12) / 0.58, 0, 1);
        this.product.style.opacity = String(0.22 + 0.78 * f);
        this.product.style.transform = `scale(${1.18 - 0.18 * f})`;
        this.product.style.filter = `blur(${(1 - f) * 8}px)`;
      }
      if (this.caption) {
        const t = clamp((q - 0.42) / 0.24, 0, 1);
        this.caption.style.opacity = String(t);
        this.caption.style.transform = `translateY(${(1 - t) * 30}px)`;
      }
      if (this.fill) this.fill.style.width = `${q * 100}%`;
      if (this.timecode) {
        const seconds = q * (this.video?.duration || 24);
        const m = String(Math.floor(seconds / 60)).padStart(2, '0');
        const s = String(Math.floor(seconds % 60)).padStart(2, '0');
        this.timecode.textContent = `${m}:${s}`;
      }
    }
  }
  customElements.define('gg-film-scrub', GGFilmScrub);


  /* ----------------------------------------------------------
     <gg-film> — the one ambient film player. No autoplay attribute
     (mode="ambient"): the film is fetched and started only when it
     nears the viewport, paused when it leaves. Reduced motion or
     Save-Data leave the poster still until the visitor presses
     play. Optional plaque toggle + sound button.
     mode="eager" (hero): the <video> carries autoplay; here we only
     add the off-screen pause and the reduced-motion respect.
     ---------------------------------------------------------- */
  /* ----------------------------------------------------------
     An ambient film plays only while it is what you are looking at: at least half of it (or half
     the screen, for a frame taller than the screen) in view. Below that it pauses at once — no
     grace zone — and resumes where it was when it returns. One rule for the theme's films and the
     Cover Lab's, so what is tested is what ships.
     Pausing is VISIBLE, never a hidden gesture: a round control, lower right, shows pause bars while
     the film runs and a play triangle when it rests. It shows itself for a moment each time the film
     starts, on hover, on a tap, and for as long as the visitor has paused — so a picture stays clean
     and nobody has to guess. A tap on the picture does the same, as a bonus, not as the way in.
     A film the visitor paused HOLDS its frame (it does not fade back to its cover) and stays paused
     when scrolled back to. The page's "Pause motion" stops them all. Page visibility is never a reason to
     refuse a film: embedded browsers misreport it, and a film that never starts is the worse failure.
     ---------------------------------------------------------- */
  /* A film's TRIM — where it begins and ends, without touching the file: data-trim-in / data-trim-out (seconds) on the
     <video>, written by Liquid from the look's presentation or by the Cover Lab. Every way a film plays honours it. */
  window.ggTrimOf = function (video) {
    const d = isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
    let a = parseFloat(video.dataset.trimIn); let b = parseFloat(video.dataset.trimOut);
    a = isFinite(a) && a > 0 ? a : 0; b = isFinite(b) && b > 0 ? b : 0;
    if (d) { a = Math.min(a, Math.max(0, d - 0.2)); b = b ? Math.min(b, d) : d; if (b - a < 0.2) b = Math.min(d, a + 0.2); }
    return { in: a, out: b, on: a > 0 || (b > 0 && (!d || b < d - 0.01)) };
  };
  // a looping film loops inside its trim: checked on every painted frame where the browser can say so.
  // video.__labHold: the Cover Lab's trim tool is scrubbing this film — while it is set, nothing in the engine moves
  // the film (this loop, the autoplay rule, a sequence's rest, scroll-play); the tool keeps its own preview in the span
  window.ggTrimLoop = function (video) {
    if (video.__trimLoop) return video.__trimLoop;
    let alive = true; let handle = 0;
    const check = () => {
      if (video.__labHold) return;
      const t = window.ggTrimOf(video);
      if (!t.on) { if (video.__trimUnlooped) { video.loop = true; video.__trimUnlooped = false; } return; }   // no trim (or cleared): the film's own loop again
      if (video.loop) { video.loop = false; video.__trimUnlooped = true; }   // the native loop returns to zero, not to the in point
      const now = video.currentTime;
      if (now < t.in - 0.04 || (t.out && now >= t.out - 0.03)) { try { video.currentTime = t.in; } catch {} if (video.ended || video.paused) { const q = video.__trimWants ? video.play() : null; if (q && q.catch) q.catch(() => {}); } }
    };
    const tick = () => { if (!alive) return; check(); if (video.requestVideoFrameCallback) handle = video.requestVideoFrameCallback(tick); };
    if (video.requestVideoFrameCallback) handle = video.requestVideoFrameCallback(tick); else video.addEventListener('timeupdate', check);
    const onEnded = () => { const t = window.ggTrimOf(video); if (!t.on) return; try { video.currentTime = t.in; } catch {} const q = video.play(); if (q && q.catch) q.catch(() => {}); };
    const onPlay = () => { video.__trimWants = true; check(); if (video.requestVideoFrameCallback) { try { video.cancelVideoFrameCallback(handle); } catch {} handle = video.requestVideoFrameCallback(tick); } };
    const onPause = () => { video.__trimWants = false; };
    video.addEventListener('ended', onEnded); video.addEventListener('play', onPlay); video.addEventListener('pause', onPause); video.addEventListener('loadedmetadata', check);
    const api = { check, destroy() { alive = false; try { video.cancelVideoFrameCallback && video.cancelVideoFrameCallback(handle); } catch {} video.removeEventListener('timeupdate', check); video.removeEventListener('ended', onEnded); video.removeEventListener('play', onPlay); video.removeEventListener('pause', onPause); video.removeEventListener('loadedmetadata', check); video.__trimLoop = null; } };
    video.__trimLoop = api; return api;
  };

  /* ONE CONTROL PER LOOK. A look may hold several films (a split, a grid); they are one picture, so they have one
     control: top right of the look's frame area — the second frame of a split on a desktop, the first where the
     frames stack on a phone — and it pauses and plays every film in the look together. It is seen only when it is
     wanted (editorial-experience.css): a playing look's pause while the pointer is on the look; a paused look's Play
     while the visitor's pause holds (or a sequence waits for its press); on a touch screen, which has no hover, for a
     moment after the visitor's tap. Never as a film starts. */
  const FILM_GROUPS = new Map();
  function filmGroup(frame) {
    const host = frame.closest('[data-experience-chapter]') || frame;
    let g = FILM_GROUPS.get(host); if (g) return g;
    const ctl = document.createElement('button'); ctl.type = 'button'; ctl.className = 'gg-filmctl'; ctl.innerHTML = '<span class="gg-filmctl__icon" aria-hidden="true"></span>';
    let peekT = 0; let hovers = 0;
    g = {
      host, ctl, members: new Set(),
      peek() { ctl.classList.add('is-peek'); clearTimeout(peekT); peekT = setTimeout(() => ctl.classList.remove('is-peek'), 2600); },
      // a BENCHED film (the Cover Lab has laid its own picture over it) is not part of the look: it neither counts nor is worked
      live() { return [...g.members].filter((m) => !m.state.bench); },
      paint() { const ms = g.live(); const running = ms.some((m) => !m.video.paused && !m.video.ended); const user = ms.some((m) => m.state.user); ctl.hidden = !ms.length; ctl.classList.toggle('is-paused', !running); ctl.classList.toggle('is-user', user); ctl.setAttribute('aria-label', running ? 'Pause the films in this look' : 'Play the films in this look'); ctl.setAttribute('aria-pressed', String(!running)); },
      toggle() { const ms = g.live(); const running = ms.some((m) => !m.video.paused && !m.video.ended); ms.forEach((m) => { m.state.user = running; m.apply(); }); g.paint(); g.peek(); },
      // the frame whose corner is the look's top right: highest first, then furthest right
      place() { const fs = [...g.members].map((m) => m.frame).filter((f) => f.isConnected && f.offsetParent !== null); if (!fs.length) return; const rs = fs.map((f) => ({ f, r: f.getBoundingClientRect() })); const top = Math.min(...rs.map((x) => x.r.top)); const row = rs.filter((x) => x.r.top - top < 12).sort((a, b) => b.r.right - a.r.right); const home = (row[0] || rs[0]).f; if (ctl.parentElement !== home) home.appendChild(ctl); },
      hover(on) { hovers = Math.max(0, hovers + (on ? 1 : -1)); ctl.classList.toggle('is-hover', hovers > 0); },
      add(m) { g.members.add(m); g.place(); g.paint(); },
      remove(m) { g.members.delete(m); if (!g.members.size) { clearTimeout(peekT); ctl.remove(); window.removeEventListener('resize', onResize); FILM_GROUPS.delete(host); } else { g.place(); g.paint(); } },
    };
    let rT = 0; const onResize = () => { clearTimeout(rT); rT = setTimeout(() => g.place(), 150); }; window.addEventListener('resize', onResize);
    ctl.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); g.toggle(); });
    FILM_GROUPS.set(host, g); return g;
  }

  /* WHAT THE VISITOR SEES of a frame: the part inside the window BELOW the site's sticky bars (the header once it sticks,
     a page's index bar, the lookbook's toolbar) — a film scrolled up under them is out of view, not "in view behind them".
     share = seen height / (the frame's height, or the room under the bars if the frame is taller). */
  window.ggChromeTop = function () {
    let b = 0;
    document.querySelectorAll('gg-header-dock .gg-header, .gg-hindex:not([hidden]), [data-experience-toolbar]').forEach((c) => { const r = c.getBoundingClientRect(); if (r.height && r.top <= b + 1 && r.bottom > b) b = r.bottom; });
    return b;
  };
  window.ggSeen = function (frame) {
    const r = frame.getBoundingClientRect(); const vh = window.innerHeight || 1; if (!r.width || !r.height) return 0;
    const top = window.ggChromeTop(); const room = Math.max(1, vh - top);
    return Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, top)) / Math.min(r.height, room);
  };
  // Separate arrival/departure thresholds keep playback steady at viewport edges.
  const FILM_ENTER_SHARE = 0.35;
  const FILM_EXIT_SHARE = 0.15;
  const ALWAYS_FILM = '[data-house-playback="always"], [data-lab-playback="always"], gg-film[data-mode="always"]';
  const ALWAYS_SHARE = 0.2;
  const AMBIENTS = new Set(); let ambRaf = 0;
  const scrollDrivenFilm = video => video.__ambient ? !!video.__ambient.scroll : !!video.closest('gg-film[data-mode="scroll"], [data-lab-playback="scroll"], [data-house-playback="scroll"]');
  const pauseOutsideView = video => {
    // Managed players own their pause/resume intent and the Lab owns its trim preview.
    if (video.__labHold || video.__ambient || video.closest('gg-craft-film') || ['autoplay', 'always'].includes(video.dataset?.cardFilm) || scrollDrivenFilm(video)) return;
    if (!video.paused && window.ggSeen(video) < FILM_EXIT_SHARE) video.pause();
  };
  window.addEventListener('scroll', () => {
    if (ambRaf) return;
    ambRaf = requestAnimationFrame(() => {
      ambRaf = 0;
      AMBIENTS.forEach(a => a.remeasure());
      document.querySelectorAll('video').forEach(pauseOutsideView);
    });
  }, { passive: true });
  document.addEventListener('play', event => {
    if (event.target.tagName === 'VIDEO') pauseOutsideView(event.target);
  }, true);

  window.ggAmbient = function (video, frame, opts) {
    opts = opts || {};
    if (!video || !frame) return null;
    if (video.__ambient) return video.__ambient;
    const state = { user: !!opts.startPaused, share: 0, inView: false, started: false, bench: false };
    const trimLoop = window.ggTrimLoop(video);
    const stopped = () => !!video.closest('[data-motion-paused]');
    const covered = () => !!frame.closest('.is-flipped');   // its card is turned to the pieces: nothing to see, so nothing plays
    // Always-on films start earlier on arrival; all playing films share the departure threshold.
    const always = opts.always != null ? !!opts.always : !!(frame.closest(ALWAYS_FILM) || video.closest(ALWAYS_FILM));
    const allowed = () => !video.error && !state.bench && state.inView && !state.user && !stopped() && !covered();
    const group = opts.control === false ? null : filmGroup(frame);
    const member = { video, frame, state, apply: () => apply() };
    const paint = () => { if (!state.bench) frame.classList.toggle('is-held', state.user && state.started); if (group) group.paint(); };
    const apply = () => {
      state.inView = state.share >= (state.inView ? FILM_EXIT_SHARE : (always ? ALWAYS_SHARE : FILM_ENTER_SHARE));
      if (video.__labHold) { paint(); return; }   // held by the Lab's trim tool: neither started nor stopped from here
      if (allowed()) { if (!video.paused) { paint(); return; } window.ggLoadFilm?.(video); if (video.preload === 'none') video.preload = 'metadata'; const q = video.play(); if (q && q.catch) q.catch(() => {}); }
      else if (!video.paused) video.pause();
      paint();
    };
    const toggle = () => { if (group) group.toggle(); else { state.user = !video.paused; apply(); } };
    // the pointer on the look shows a playing film's pause; a touch is not a hover (a tap pauses or plays the look)
    const onEnter = (e) => { if (group && e.pointerType !== 'touch') group.hover(true); }; const onLeave = (e) => { if (group && e.pointerType !== 'touch') group.hover(false); };
    if (group) { frame.addEventListener('pointerenter', onEnter); frame.addEventListener('pointerleave', onLeave); }
    const onTap = (e) => {
      if (e.target.closest('button, a, input, select, label, [contenteditable="true"], .gg-lab, .gg-lab-hist, .gg-lab-frame')) return;
      const root = document.documentElement; if (root.classList.contains('gg-lab-on') && !root.classList.contains('gg-lab-preview') && !root.classList.contains('gg-editor-hidden')) return; // while editing, a click on a frame means work, not pause
      toggle();
    };
    if (opts.tap !== false) frame.addEventListener('click', onTap);
    const onPlay = () => { state.started = true; paint(); }; const onPause = () => paint();
    video.addEventListener('play', onPlay); video.addEventListener('pause', onPause);
    const steps = []; for (let i = 0; i <= 20; i++) steps.push(i / 20);
    // what the visitor sees of the frame (window.ggSeen: below the sticky bars); the observer says when to look again
    const measure = () => window.ggSeen(frame);
    const io = new IntersectionObserver((es) => es.forEach((en) => { state.share = en.isIntersecting ? measure() : 0; apply(); }), { threshold: steps });
    io.observe(frame);
    const live = { video, remeasure: () => { state.share = measure(); apply(); } }; AMBIENTS.add(live);
    const onDoc = () => { state.share = measure(); apply(); };
    const WAKE = ['visibilitychange', 'pageshow', 'focus', 'resume']; WAKE.forEach((ev) => (ev === 'visibilitychange' || ev === 'resume' ? document : window).addEventListener(ev, onDoc));
    const onFlip = (e) => { if (e.target && e.target.contains && e.target.contains(frame)) setTimeout(apply, 60); }; document.addEventListener('gg-flip', onFlip);
    // a film stopped by something other than this rule (a browser saving power) is started again if it still may play
    let retryT = 0; const onStall = () => { clearTimeout(retryT); retryT = setTimeout(() => { if (video.paused && allowed()) apply(); }, 400); };
    video.addEventListener('pause', onStall);
    state.share = measure();
    const host = video.closest('srlm-experience'); const mo = host ? new MutationObserver(() => { if (!stopped()) state.user = false; apply(); }) : null; // "Play motion" means play everything again
    if (mo) mo.observe(host, { attributes: true, attributeFilter: ['data-motion-paused'] });
    if (group) group.add(member);
    apply(); // from the measured share: a film in view starts now, without waiting on the observer
    const api = {
      toggle, apply, always,
      get userPaused() { return state.user; }, set userPaused(v) { state.user = !!v; apply(); },
      // benched: the Cover Lab has laid its own picture over this film — it rests, and the look's control forgets it
      get benched() { return state.bench; }, set benched(v) { state.bench = !!v; apply(); },
      get share() { return state.share; },
      destroy() { AMBIENTS.delete(live); trimLoop.destroy(); io.disconnect(); if (mo) mo.disconnect(); if (group) { frame.removeEventListener('pointerenter', onEnter); frame.removeEventListener('pointerleave', onLeave); group.remove(member); } frame.removeEventListener('click', onTap); video.removeEventListener('play', onPlay); video.removeEventListener('pause', onPause); video.removeEventListener('pause', onStall); document.removeEventListener('gg-flip', onFlip); clearTimeout(retryT); WAKE.forEach((ev) => (ev === 'visibilitychange' || ev === 'resume' ? document : window).removeEventListener(ev, onDoc)); frame.classList.remove('is-held'); video.__ambient = null; },
    };
    video.__ambient = api; return api;
  };

  /* ----------------------------------------------------------
     SCROLL-PLAY — the classic scroll film, as ONE system for every composition. A look that holds a scroll-play
     film LOCKS as a whole block — full bleed, beside its words, a split, a grid, on a desktop or a phone: when the
     playing frame reaches the top of the screen the look stays there while the scroll plays the film from its first
     frame to its last, then lets go, and the page carries on with the last frame frozen like a cover. Scrolling
     back re-locks and rewinds. Several scroll-play films in one look share one lock and end together.

     THE LOCK is layout, never a hijack: the look itself is position:sticky inside a wrapper (its own block wrapper,
     or one made for it), and the wrapper carries a runway after the look (::after). The sticky look is held by its
     parent's box in every engine — no grid or flex subtleties — so every part of it holds and lets go together,
     whatever the composition. It holds with the playing frame under the bars (centred, if taller than the screen).

     THE ELASTIC RUNWAY: a runway is a distance, and a fast scroll crosses a distance faster than a film can play.
     The scroll is never touched (a browser will not let a page cancel a scroll already under way — on a real
     trackpad none of the wheel events are cancelable — and pulling the scroll position back fights it: the page
     judders and sticks). Instead the scroll CRANKS the film — so many pixels to a second, never asking for more
     than the film can give (0.8s ahead = 2½× speed) — and while any film has not finished, the runway's END moves
     away ahead of the visitor, further than any one scroll step can leap. Everything on screen is held, so a
     runway growing below cannot be seen; the look simply cannot be left early. When the films finish the end comes
     to meet the visitor and the next scroll moves the page on. Going back, a film rewinds in proportion to the way
     left to the top. Only real scroll input stretches it: a jump (the Index, a link, Home/End, the scrollbar,
     find-in-page) maps position to time directly and is never held.

     HOW A FILM GETS to the moment the scroll asks for keeps it smooth, above all on a phone: forward it is really
     PLAYED toward it (¼× to 2½×, with hysteresis) — true frames, not a stutter of seeks; backward it seeks, one
     seek at a time, never a queue (a slow seeker settles once at rest); a flick that outruns it is caught with one
     jump; if play() is refused (low power) it seeks forward too. The film's trim is the span that plays.
     "Pause motion", reduced motion and Save-Data leave the films at rest and the page unlocked.
     ---------------------------------------------------------- */
  const SCROLL_LOCKS = new Map();
  function scrollLock(chapter) {
    let L = SCROLL_LOCKS.get(chapter); if (L) return L;
    const still = () => reducedMotion.matches || !!saveData || !!chapter.closest('[data-motion-paused]');
    const pxPerSecond = () => (window.innerWidth < 750 ? 170 : 240);
    const reach = () => Math.max(700, (window.innerHeight || 800) * 0.9);
    // once the films are done the end comes to meet the visitor, one short scroll away
    const meet = () => 48;
    // the look's own block wrapper carries the runway; a look that stands directly in the list is given one
    /* THE PAGE LOOKS FROZEN. The runway is scroll distance, and it would show as empty space under a held look shorter than
       the screen — the next look arriving only as the runway runs out. So the wrapper's runway is given back as a negative
       margin (the next look's wrapper starts right under this look, overlapping the runway), the next wrapper opens with the
       same distance (::before), and the next look is position:sticky to the BOTTOM — pulled up to sit exactly under the held
       look. It cannot rise above its own wrapper's top, which is this look's foot, so on the way in it simply follows; while
       this look is held it is held beneath it; when the runway is spent it is where it would be anyway, and carries on.
       All of it is the compositor's own sticky positioning: nothing is moved by script, so nothing can judder. */
    const chapterIn = (w) => (w ? (w.matches('[data-experience-chapter]') ? w : w.querySelector(':scope > [data-experience-chapter]')) : null);
    const wrapOf = (ch) => { const w = ch.parentElement; if (w.classList.contains('srlm-pinwrap')) return w; if (w.id !== 'experience-chapters' && w.children.length === 1) { w.classList.add('srlm-pinwrap'); return w; } const d = document.createElement('div'); d.className = 'srlm-pinwrap'; ch.before(d); d.appendChild(ch); return d; };
    const release = () => { const f = L.follower; if (!f) return; f.wrap.classList.remove('is-pulled'); f.wrap.style.removeProperty('--pull-run'); delete f.wrap.dataset.pullRun; f.chapter.style.removeProperty('--pull-bottom'); if (L.fro) L.fro.disconnect(); L.follower = null; };
    const pull = () => {
      const nextCh = L.wrap ? chapterIn(L.wrap.nextElementSibling) : null;
      if (L.follower && L.follower.chapter !== nextCh) release();
      if (!nextCh) return;
      if (!L.follower) { L.follower = { chapter: nextCh, wrap: wrapOf(nextCh) }; L.follower.wrap.classList.add('is-pulled'); if ('ResizeObserver' in window) { L.fro = new ResizeObserver(() => pull()); L.fro.observe(nextCh); } }
      const f = L.follower; f.wrap.style.setProperty('--pull-run', L.run + 'px'); f.wrap.dataset.pullRun = String(L.run);
      f.chapter.style.setProperty('--pull-bottom', Math.round((window.innerHeight || 800) - (L.stick + chapter.offsetHeight) - nextCh.offsetHeight) + 'px');   // its top exactly under this look's foot
    };
    // where this look's wrapper begins, as the look's own place in the page (a wrapper pulled under the look above opens with that look's runway)
    const wrapTop = () => L.wrap.getBoundingClientRect().top + Number(L.wrap.dataset.pullRun || 0);
    const setRun = (px) => { L.run = Math.round(px); if (L.wrap) L.wrap.style.setProperty('--pin-run', L.run + 'px'); if (L.follower) { L.follower.wrap.style.setProperty('--pull-run', L.run + 'px'); L.follower.wrap.dataset.pullRun = String(L.run); } };
    const unpin = () => { if (!L.pinned) return; L.pinned = false; release(); chapter.classList.remove('is-pinned', 'is-film-done'); chapter.style.removeProperty('--pin-stick'); if (L.wrap) { L.wrap.classList.remove('is-pinned'); L.wrap.style.removeProperty('--pin-run'); } L.run = 0; L.pos = null; };
    const live = () => [...L.members].filter((m) => m.dur());
    const layout = () => {
      const ms = live(); if (!ms.length || still()) { unpin(); return; }
      L.wrap = L.wrap || (chapter.parentElement ? wrapOf(chapter) : null); if (!L.wrap) return;
      const vh = window.innerHeight || 1; const bar = document.querySelector('[data-experience-toolbar]');
      // under the bars: the experience toolbar where a page has one; else the site's header, when it stays at the top (the House page)
      const dock = bar ? null : document.querySelector('gg-header-dock'); const dockPos = dock ? getComputedStyle(dock).position : '';
      const head = bar ? (parseFloat(getComputedStyle(bar).top) || 0) + bar.offsetHeight : (dockPos === 'sticky' || dockPos === 'fixed' ? Math.round(dock.getBoundingClientRect().height) : 0);
      // the look holds with its playing frame under the bars: the topmost one, if there are several
      const cr = chapter.getBoundingClientRect(); const focus = ms.map((m) => m.frame.getBoundingClientRect()).sort((x, y) => x.top - y.top)[0];
      const frameTop = Math.min(head, (head + vh - focus.height) / 2);                     // a frame taller than the screen is held centred in what is visible
      L.stick = Math.round(frameTop - (focus.top - cr.top));
      L.baseRun = Math.round(Math.min(Math.max(Math.max(...ms.map((m) => { const sp = m.span(); return (sp.out - sp.in) * pxPerSecond(); })), vh * 0.4), vh * 3));
      if (!L.pinned || !L.run || L.run < L.baseRun) L.run = L.baseRun;                       // a runway stretched under the visitor is left as it is
      L.wrap.style.setProperty('--pin-run', L.run + 'px'); chapter.style.setProperty('--pin-stick', L.stick + 'px');
      L.wrap.classList.add('is-pinned'); chapter.classList.add('is-pinned'); L.pinned = true;
      pull();
    };
    const allDone = () => { const ms = live(); return ms.length > 0 && ms.every((m) => m.filmDone()); };
    const mark = () => { if (!L.pinned) return; const pos = L.stick - wrapTop(); const done = pos > 0 && allDone() && live().every((m) => m.target >= m.span().out - 0.03); chapter.classList.toggle('is-film-done', done); if (done && pos < L.run && L.run - pos > meet() + 1 && L.run > L.baseRun) setRun(Math.max(L.baseRun, pos + meet())); };
    const crank = () => {
      if (!L.pinned) return false;
      const pos = L.stick - wrapTop();                              // how far into the runway the scroll stands
      const before = L.pos; L.pos = pos;
      const scrolling = before != null && performance.now() - L.lastInput <= 320;            // real scroll input, just now
      const d = before == null ? 0 : pos - Math.max(before, 0);
      live().forEach((m) => {
        const sp = m.span(); const density = Math.max(1, L.baseRun / Math.max(0.05, sp.out - sp.in));   // px of scroll to a second of this film: all the look's films end together
        if (pos <= 0) m.target = sp.in;
        else if (!scrolling) m.target = pos >= L.run ? sp.out : sp.in + (pos / L.run) * (sp.out - sp.in);   // a jump, not a scroll: where it lands is when it shows
        else if (d > 0) m.target = Math.min(sp.out, m.target + d / density);             // every pixel scrolled is kept: a hard scroll winds the film further than it can play at once, and it runs on to there
        else if (d < 0) m.target = pos >= L.run ? sp.out : sp.in + (Math.min(m.target, sp.out) - sp.in) * (pos / Math.max(1, Math.min(before, L.run)));
        m.kick();
      });
      if (scrolling && d > 0) {
        // only a visitor who was still being held can be held on: one who is already past the look (arrived below it by a jump, or
        // let go earlier) is never pulled back into it — the single step that leaps over the end from inside is the one case caught
        const wasHeld = before < L.run;
        if (!allDone()) { if (wasHeld && L.run - pos < reach()) setRun(pos + reach()); }     // not finished: the end keeps its distance
        else if (L.run - pos > meet() + 1 && pos < L.run) setRun(Math.max(L.baseRun, pos + meet()));   // finished: the end comes to meet the visitor
      }
      mark();
      return true;
    };
    // a look already held keeps its place in the film when its size changes (its pieces opening, a resize): only a fresh lock reads the position
    const relayout = () => { const was = L.pinned; layout(); if (L.pinned && !was) { L.pos = null; crank(); } };   // layout() also re-seats the look that follows
    const markInput = () => { L.lastInput = performance.now(); };
    const onKeyMark = (e) => { if (/^(ArrowDown|ArrowUp|PageDown|PageUp| )$/.test(e.key)) markInput(); };
    const onScroll = () => { if (L.pinned && [...L.members].some((m) => m.near())) crank(); };
    let ro = null, mo = null;
    L = {
      chapter, members: new Set(), pinned: false, run: 0, baseRun: 0, stick: 0, pos: null, lastInput: 0, wrap: null,
      crank, relayout, mark,
      add(m) { L.members.add(m); if (L.members.size === 1) { const listEl = document.getElementById('experience-chapters'); if (listEl && 'MutationObserver' in window) { L.lo = new MutationObserver(() => { clearTimeout(L.loT); L.loT = setTimeout(relayout, 120); }); L.lo.observe(listEl, { childList: true }); } window.addEventListener('scroll', onScroll, { passive: true }); window.addEventListener('resize', relayout); window.addEventListener('wheel', markInput, { passive: true }); window.addEventListener('touchmove', markInput, { passive: true }); window.addEventListener('keydown', onKeyMark); if ('ResizeObserver' in window) { ro = new ResizeObserver(() => relayout()); ro.observe(chapter); } const host = chapter.closest('srlm-experience'); if (host) { mo = new MutationObserver(relayout); mo.observe(host, { attributes: true, attributeFilter: ['data-motion-paused'] }); } } relayout(); },
      remove(m) { L.members.delete(m); if (L.members.size) { relayout(); return; } unpin(); window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', relayout); window.removeEventListener('wheel', markInput); window.removeEventListener('touchmove', markInput); window.removeEventListener('keydown', onKeyMark); if (ro) ro.disconnect(); if (mo) mo.disconnect(); if (L.lo) L.lo.disconnect(); SCROLL_LOCKS.delete(chapter); },
    };
    SCROLL_LOCKS.set(chapter, L); return L;
  }

  window.ggScrollPlay = function (video, frame, opts) {
    opts = opts || {};
    if (!video || !frame) return null;
    if (video.__ambient) return video.__ambient;
    const still = () => reducedMotion.matches || !!saveData || !!video.closest('[data-motion-paused]');
    const st = { near: false, raf: 0, lastMove: 0, target: 0, canPlay: true, reverseOk: true, seekT0: 0, seekAvg: 0, seeks: 0, watch: false, shift: 0, lastM: null, lastScroll: 0 };
    video.loop = false; video.muted = true; video.playsInline = true; video.removeAttribute('autoplay');
    try { video.pause(); } catch {}
    const dur = () => (isFinite(video.duration) && video.duration > 0 ? video.duration : 0);
    const span = () => { const t = window.ggTrimOf(video); const d = dur(); return { in: t.in, out: Math.max(t.in + 0.1, (t.out || d) - 0.1) }; };
    const filmDone = () => video.ended || video.currentTime >= span().out - 0.04;
    const chapter = opts.pin === false ? null : frame.closest('[data-experience-chapter]');
    const lock = chapter ? scrollLock(chapter) : null;
    /* HOVER TO WATCH — a framed film that scrolls with the page, on a device that can hover (the user, 2026-09-23: "resume
       playing on hover so people can hover to watch then scroll effect resumes on scroll"): a pointer that really moves
       onto the frame plays the film at its own pace, round its span, for as long as it stays. The next scroll, or the
       pointer leaving, hands it back to the scroll, which picks it up from that very frame and eases back into step with
       the page: the gap closes over the next second and a half of film, so nothing jumps. A locked film (the film band, a
       look that holds) and a frame as wide as the window have none: there the pointer is always on the film. */
    const canWatch = !lock && opts.watch !== false && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    // outside a look (or with the lock off): the film plays as its frame crosses the screen
    const measure = () => {
      const d = dur(); if (!d) return 0; const sp = span();
      const r = frame.getBoundingClientRect(); const vh = window.innerHeight || 1;
      const startLine = vh * 0.85; const journey = r.height + vh * 0.7;
      const atTop = Math.max(0, startLine - (r.top + window.scrollY));
      const travel = Math.min(Math.max(0, startLine - r.top - atTop), Math.max(1, journey - atTop));
      const density = Math.max(window.innerWidth < 750 ? 170 : 240, (journey - atTop) / (sp.out - sp.in));
      return Math.min(sp.out, sp.in + travel / density);
    };
    // where the page puts the film, plus what is left of the gap a watch opened (it closes as the scroll goes on)
    const aim = () => {
      const m = measure(); if (!dur()) return m; const sp = span();
      if (st.shift && st.lastM != null) { st.shift *= Math.exp(-Math.abs(m - st.lastM) / 1.5); if (Math.abs(st.shift) < 0.03) st.shift = 0; }
      st.lastM = m;
      return Math.min(sp.out, Math.max(sp.in, m + st.shift));
    };
    const gauge = () => { const sp = span(); frame.style.setProperty('--scroll-play', sp.out > sp.in ? Math.min(1, Math.max(0, (video.currentTime - sp.in) / (sp.out - sp.in))).toFixed(4) : '0'); };
    const setRate = (r) => { r = Math.round(r * 4) / 4; if (video.playbackRate !== r) { try { video.playbackRate = r; } catch {} } };
    const seekTo = (t) => { if (video.seeking) return; st.seekT0 = performance.now(); try { video.currentTime = Math.max(0, t); } catch {} };
    const onSeeked = () => { if (!st.seekT0) return; const dt = performance.now() - st.seekT0; st.seekT0 = 0; st.seeks++; st.seekAvg = st.seekAvg ? st.seekAvg * 0.7 + dt * 0.3 : dt; if (st.seeks >= 4 && st.seekAvg > 220) st.reverseOk = false; gauge(); };
    video.addEventListener('seeked', onSeeked);
    const hold = () => { if (!video.paused) { try { video.pause(); } catch {} } };
    const step = () => {
      st.raf = 0;
      if (video.__labHold) return;   // the Lab's trim tool is scrubbing this film: the scroll waits
      if (st.watch) return;   // the visitor is watching it: the scroll waits for the next scroll
      if (!st.near || still() || !dur()) { hold(); frame.classList.remove('is-winding'); return; }
      const now = performance.now(); const moving = now - st.lastMove < 140;
      const lead = st.target - video.currentTime;
      // a little hysteresis, so a slow scroll does not flutter between play and pause: start at 90ms of lead, stop at 20
      const locked = !!(lock && lock.pinned);
      if (lead > (video.paused ? 0.09 : 0.02)) {
        if (lead > 2.6 && !locked) { hold(); seekTo(st.target - 0.25); }                   // crossing the screen unlocked, a flick that outruns the film is caught with one jump
        else if (st.canPlay) {
          // held by its lock the film never jumps: it runs to where the scroll wound it — up to 2½× while the scroll is still coming, at its own pace once the scroll rests
          setRate(Math.min(locked && !moving ? 1 : 2.5, Math.max(0.25, lead / 0.32)));
          if (video.paused && !video.seeking) { const q = video.play(); if (q && q.catch) q.catch(() => { st.canPlay = false; }); }
        } else seekTo(st.target);
      } else if (lead < -0.06) {
        hold();
        if (st.reverseOk || !moving) seekTo(st.target);
      } else hold();
      gauge();
      if (lock) lock.mark();
      frame.classList.toggle('is-winding', moving || !video.paused);
      if (moving || !video.paused || video.seeking || st.target - video.currentTime > 0.09 || video.currentTime - st.target > 0.06) st.raf = requestAnimationFrame(step);
      else frame.classList.remove('is-winding');
    };
    const kick = () => { st.lastMove = performance.now(); if (!st.raf) st.raf = requestAnimationFrame(step); };
    const member = { video, frame, dur, span, filmDone, kick, near: () => st.near, get target() { return st.target; }, set target(v) { st.target = v; } };
    const onScroll = () => { st.lastScroll = performance.now(); if (st.watch) unwatch(); if (!st.near) return; if (lock && lock.pinned) return; st.target = aim(); kick(); };   // a locked look is cranked by its lock
    const relayout = () => { if (lock) lock.relayout(); if (!lock || !lock.pinned) { st.target = aim(); kick(); } };
    const watchLoop = () => {
      if (!st.watch || video.__labHold) return;
      const sp = span(); if (video.currentTime >= sp.out - 0.04) { try { video.currentTime = sp.in; } catch {} }
      gauge(); if (video.requestVideoFrameCallback) video.requestVideoFrameCallback(watchLoop);
    };
    const onEnded = () => { if (!st.watch || video.__labHold) return; try { video.currentTime = span().in; } catch {} const q = video.play(); if (q && q.catch) q.catch(() => {}); if (video.requestVideoFrameCallback) video.requestVideoFrameCallback(watchLoop); };
    const onWatchTime = () => { if (st.watch && !video.requestVideoFrameCallback) watchLoop(); };
    const watch = () => {
      if (st.watch || !canWatch || !st.near || still() || video.__labHold || !dur() || video.readyState < 2) return;
      if (frame.getBoundingClientRect().width >= (document.body.clientWidth || window.innerWidth || 1) * 0.94) return;
      st.watch = true; frame.classList.add('is-watching', 'is-winding');
      if (st.raf) { cancelAnimationFrame(st.raf); st.raf = 0; }
      setRate(1);
      const sp = span(); if (video.currentTime >= sp.out - 0.05) { try { video.currentTime = sp.in; } catch {} }
      const q = video.play(); if (q && q.catch) q.catch(() => unwatch());
      if (video.requestVideoFrameCallback) video.requestVideoFrameCallback(watchLoop);
    };
    const unwatch = () => {
      if (!st.watch) return;
      st.watch = false; frame.classList.remove('is-watching');
      if (video.__labHold) return;
      hold();
      // the scroll picks the film up from here: the page's moment and the film's differ by this much
      const m = measure(); st.lastM = m; st.shift = video.currentTime - m; st.target = video.currentTime;
      kick();
    };
    const onMove = (e) => {
      if (e.pointerType === 'touch') return;
      if (e.movementX === 0 && e.movementY === 0) return;   // a pointer the page scrolled under has not moved: that is no hover
      if (performance.now() - st.lastScroll < 250) return;   // the page is still being scrolled
      watch();
    };
    const onOut = () => unwatch();
    if (canWatch) { frame.addEventListener('pointermove', onMove); frame.addEventListener('pointerleave', onOut); video.addEventListener('ended', onEnded); video.addEventListener('timeupdate', onWatchTime); }
    const ready = () => { frame.classList.add('is-held'); const sp = span(); if (video.currentTime < sp.in) { try { video.currentTime = sp.in; } catch {} } relayout(); };
    const wake = () => {
      window.ggLoadFilm?.(video);
      if (video.preload !== 'auto') video.preload = 'auto';
      if (video.readyState === 0 && video.networkState !== 2) { try { video.load(); } catch {} }
      if (video.readyState >= 2) ready(); else video.addEventListener('loadeddata', ready, { once: true });
    };
    const io = new IntersectionObserver((es) => es.forEach((en) => { st.near = en.isIntersecting; if (st.near) { wake(); if (lock && lock.pinned) lock.crank(); else onScroll(); } else { unwatch(); st.shift = 0; st.lastM = null; hold(); frame.classList.remove('is-winding'); } }), { rootMargin: '60% 0px 60% 0px', threshold: 0 });
    io.observe(frame);
    // a locked look must know its runway before the visitor arrives: the duration is enough, so ask for the metadata early
    if (lock && video.preload === 'none') video.preload = 'metadata';
    window.addEventListener('scroll', onScroll, { passive: true });
    video.addEventListener('loadedmetadata', relayout);
    const onDoc = () => { if (document.hidden) { unwatch(); hold(); } else relayout(); }; document.addEventListener('visibilitychange', onDoc);
    frame.classList.add('is-scrollplay');
    if (lock) lock.add(member); else relayout();
    const api = {
      scroll: true,   // the scroll winds this film: it is never "pressed" (the scene rule below), whatever its frame's is-held says
      apply() { unwatch(); relayout(); }, toggle() {}, get userPaused() { return false; }, set userPaused(v) { relayout(); },
      get share() { return st.near ? 1 : 0; }, get state() { return { target: +st.target.toFixed(3), time: +video.currentTime.toFixed(3), watch: st.watch, shift: +st.shift.toFixed(3), canPlay: st.canPlay, reverseOk: st.reverseOk, seekAvg: Math.round(st.seekAvg), near: st.near, pinned: !!(lock && lock.pinned), run: lock ? lock.run : 0, baseRun: lock ? lock.baseRun : 0, stick: lock ? lock.stick : 0 }; },
      destroy() { st.watch = false; io.disconnect(); cancelAnimationFrame(st.raf); window.removeEventListener('scroll', onScroll); document.removeEventListener('visibilitychange', onDoc); video.removeEventListener('loadedmetadata', relayout); video.removeEventListener('seeked', onSeeked); frame.removeEventListener('pointermove', onMove); frame.removeEventListener('pointerleave', onOut); video.removeEventListener('ended', onEnded); video.removeEventListener('timeupdate', onWatchTime); frame.classList.remove('is-scrollplay', 'is-winding', 'is-held', 'is-watching'); frame.style.removeProperty('--scroll-play'); if (lock) lock.remove(member); video.__ambient = null; },
    };
    video.__ambient = api; return api;
  };

  /* THE REST COVER — a film that plays only when asked (Play on hover, Play sequence) rests on its cover: whenever it is
     not playing, its poster lies over it — the frame's photograph, the film's own still, the Lab's cover — and lifts the
     moment the film moves (the user, 2026-09-23: "when not playing it should return to the cover image"). An autoplay
     film freezes on its frame instead. The cover keeps its own aspect (--ar), so a framed frame lays it as it lays the
     film. The Cover Lab's own films answer to the same rule with their own cover layer (editorial-experience.css). */
  function restCover(video) {
    const box = video.closest('gg-film') || video.parentElement;
    let rest = null; let rolling = false;
    const paint = () => {
      const p = video.getAttribute('poster') || '';
      if (!p) { if (rest) { rest.remove(); rest = null; } return; }
      if (!rest) {
        rest = document.createElement('img'); rest.className = 'gg-film__rest'; rest.alt = ''; rest.decoding = 'async'; rest.setAttribute('aria-hidden', 'true');
        const r = rest; r.addEventListener('load', () => { if (r.naturalWidth && r.naturalHeight) r.style.setProperty('--ar', (r.naturalWidth / r.naturalHeight).toFixed(4)); });
        box.appendChild(rest);
      }
      if (rest.getAttribute('src') !== p) rest.src = p;
      rest.classList.toggle('is-gone', rolling);
    };
    const onPlaying = () => { rolling = true; if (rest) rest.classList.add('is-gone'); };
    const onStop = () => { rolling = false; if (rest) rest.classList.remove('is-gone'); };
    const EV = [['playing', onPlaying], ['pause', onStop], ['ended', onStop], ['emptied', onStop], ['error', onStop]];
    EV.forEach(([e, f]) => video.addEventListener(e, f));
    // it follows the film's poster (the Lab sets a new one without a reload)
    const mo = new MutationObserver(paint); mo.observe(video, { attributes: true, attributeFilter: ['poster'] });
    paint();
    return { has: () => !!rest, destroy() { EV.forEach(([e, f]) => video.removeEventListener(e, f)); mo.disconnect(); if (rest) rest.remove(); rest = null; } };
  }

  /* PLAY SEQUENCE — a film that waits behind its cover for the visitor's press, on the same rule and with the same
     round control as every other film: it starts at rest (the control shows Play), a press plays it; paused — by the
     visitor, or after most of the frame has left the view — it goes back under its cover and WAITS (it does not carry
     on by itself when the frame returns), and the next press plays it on from where it stopped; at its end (or its
     trim's out point) it rests again, from the top. Used by <gg-film data-mode="sequence"> (snippets/film.liquid) and
     by the Cover Lab's frames on the House page. */
  window.ggSequence = function (video, frame, opts) {
    if (!video || !frame) return null;
    if (video.__ambient) return video.__ambient;
    video.loop = false; video.removeAttribute('autoplay');
    // the ambient rule's trim loop would play the film round again at its out point; a sequence ends there instead
    if (!video.__trimLoop) video.__trimLoop = { check() {}, destroy() {} };
    const amb = window.ggAmbient(video, frame, Object.assign({ startPaused: true, control: true, tap: true }, opts || {}));
    if (!amb) return null;
    const cover = restCover(video);
    const trim = () => window.ggTrimOf(video);
    const rest = () => { try { video.pause(); } catch {} amb.userPaused = true; try { video.currentTime = trim().in; } catch {} frame.classList.remove('is-held', 'is-rolled'); const f = video.closest('gg-film'); if (f) f.removeAttribute('data-started'); };   // at rest again: its cover over it
    const onPause = () => { if (video.__labHold) return; if (!amb.userPaused && amb.share < FILM_EXIT_SHARE) amb.userPaused = true; };   // scrolled away: hold, and wait for the next press
    const onPlay = () => { const t = trim(); if (video.currentTime < t.in - 0.04) { try { video.currentTime = t.in; } catch {} } };
    const onTime = () => { if (video.__labHold) return; const t = trim(); if (t.out && !video.paused && video.currentTime >= t.out - 0.05) rest(); };
    const onEnded = () => { if (!video.__labHold) rest(); };
    video.addEventListener('pause', onPause); video.addEventListener('play', onPlay); video.addEventListener('timeupdate', onTime); video.addEventListener('ended', onEnded);
    const destroy = amb.destroy;
    amb.destroy = () => { video.removeEventListener('pause', onPause); video.removeEventListener('play', onPlay); video.removeEventListener('timeupdate', onTime); video.removeEventListener('ended', onEnded); cover.destroy(); frame.classList.remove('is-sequence'); destroy(); };
    frame.classList.add('is-sequence');
    amb.apply();
    return amb;
  };

  /* PLAY ON HOVER — the film rests on its cover (THE REST COVER) and plays while the pointer is over its frame; when the
     pointer leaves it pauses, its cover comes back over it, and under the cover it goes back to its in-point, so the next
     hover starts it from the top. At rest one small line where the round control would sit says how to wake it — "Hover
     to play" (the user, 2026-09-23: the round Play was too much here) — the only words ever laid on a picture. It fetches
     ahead as its frame nears the view, so a hover starts at once. A device that cannot hover (a phone) plays it by the
     ambient rule — while at least half in view. While the Cover Lab holds the film (__labHold) nothing here moves it; a
     film the Lab has covered with its own picture is benched: it neither plays nor shows its line. */
  window.ggHover = function (video, frame, opts) {
    opts = opts || {};
    if (!video || !frame) return null;
    if (video.__ambient) return video.__ambient;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return window.ggAmbient(video, frame, opts);
    video.removeAttribute('autoplay');
    const trimLoop = window.ggTrimLoop(video);
    const cover = restCover(video);
    const state = { over: false, near: false, bench: false };
    let hint = null;
    if (opts.control !== false) { hint = document.createElement('span'); hint.className = 'gg-hoverhint'; hint.setAttribute('aria-hidden', 'true'); hint.textContent = 'Hover to play'; frame.appendChild(hint); }
    const paintHint = () => { if (hint) { hint.hidden = state.bench; hint.classList.toggle('is-off', state.over); } };
    let backT = 0;
    const apply = () => {
      paintHint();
      if (video.__labHold) return;
      clearTimeout(backT);
      if (state.over && !state.bench && !video.error) { window.ggLoadFilm?.(video); if (video.preload !== 'auto') video.preload = 'auto'; const q = video.play(); if (q && q.catch) q.catch(() => {}); return; }
      if (!video.paused) video.pause();
      if (state.bench) return;
      // back to its in-point once the cover is over it again (at once without a cover: its first frame is its cover)
      backT = setTimeout(() => { if (state.over || !video.paused || video.__labHold) return; const t = window.ggTrimOf(video); if (Math.abs(video.currentTime - t.in) > 0.05) { try { video.currentTime = t.in; } catch {} } }, cover.has() ? 1000 : 0);
    };
    const onEnter = () => { state.over = true; apply(); };
    const onLeave = () => { state.over = false; apply(); };
    frame.addEventListener('pointerenter', onEnter); frame.addEventListener('pointerleave', onLeave);
    const io = new IntersectionObserver((es) => es.forEach((en) => { state.near = en.isIntersecting; if (state.near && video.preload === 'none') video.preload = 'metadata'; if (!state.near && state.over) onLeave(); }), { rootMargin: '300px 0px' });
    // scrolled past with the pointer resting: under fifteen percent in view it stops and goes back under its cover, as it does when the pointer leaves
    const live = { video, remeasure: () => { if (state.over && window.ggSeen(frame) < FILM_EXIT_SHARE) onLeave(); } }; AMBIENTS.add(live);
    io.observe(frame);
    paintHint(); frame.classList.add('is-hover-film');
    const api = {
      apply, toggle() { state.over = !state.over; apply(); },
      get userPaused() { return !state.over; }, set userPaused(v) { if (v) { state.over = false; apply(); } },
      get benched() { return state.bench; }, set benched(v) { state.bench = !!v; if (state.bench) state.over = false; apply(); },
      get share() { return state.near ? 1 : 0; },
      destroy() { clearTimeout(backT); AMBIENTS.delete(live); cover.destroy(); trimLoop.destroy(); io.disconnect(); frame.removeEventListener('pointerenter', onEnter); frame.removeEventListener('pointerleave', onLeave); if (hint) hint.remove(); frame.classList.remove('is-hover-film'); video.__ambient = null; },
    };
    video.__ambient = api; return api;
  };

  /* A TOUCH SCREEN HAS NO HOVER: where a playing film's pause would show under the pointer, there it shows for a
     moment after a tap on the film (data-peek on the host, 2.6 s); a film at rest keeps its Play up (the CSS). */
  const NO_HOVER = window.matchMedia('(hover: none), (pointer: coarse)');
  window.ggTapPeek = function (host, area) {
    area = area || host; let t = 0;
    const on = () => { if (!NO_HOVER.matches) return; host.setAttribute('data-peek', ''); clearTimeout(t); t = setTimeout(() => host.removeAttribute('data-peek'), 2600); };
    area.addEventListener('click', on);
    return () => { clearTimeout(t); area.removeEventListener('click', on); host.removeAttribute('data-peek'); };
  };

  // Attach deferred sources once, just before a film approaches the viewport or is requested.
  window.ggLoadFilm = function(video) {
    if (!video || !video.querySelector('source[data-film-src]')) return;
    const sources = [...video.querySelectorAll('source[data-film-src]')];
    const nativeHLS = !!video.canPlayType('application/vnd.apple.mpegurl');
    const mp4 = sources.filter(s => s.type === 'video/mp4' && +s.dataset.width > 0)
      .sort((a,b) => +a.dataset.width - +b.dataset.width);
    // Keep native adaptive streaming. On MP4-only browsers, use an existing rendition
    // sized for this cell rather than automatically choosing the largest source.
    let chosen = null;
    if (!nativeHLS && mp4.length > 1) {
      const target = Math.min(1920, Math.max(480, video.clientWidth * Math.min(devicePixelRatio || 1, 1.5)));
      chosen = mp4.find(s => +s.dataset.width >= target) || mp4[mp4.length - 1];
    }
    sources.forEach(source => {
      if (!chosen || source.type !== 'video/mp4' || source === chosen) source.src = source.dataset.filmSrc;
      source.removeAttribute('data-film-src');
    });
    video.preload = 'metadata';
    video.load();
  };

  const saveData = navigator.connection && navigator.connection.saveData;
  class GGFilm extends HTMLElement {
    connectedCallback() {
      if (this._listeners) return;
      this.video = this.querySelector('video');
      if (!this.video) return;
      this._listeners = [];
      if (this.video.readyState === 0) { this.removeAttribute('data-started'); this.removeAttribute('data-playing'); this.removeAttribute('data-error'); this.loading(false); }
      const listen = (node, event, fn) => { node.addEventListener(event, fn); this._listeners.push(() => node.removeEventListener(event, fn)); };
      this.eager = this.dataset.mode === 'eager';
      const still = reducedMotion.matches || !!saveData;
      this.querySelectorAll('[data-film-toggle]').forEach(button => { button.hidden = this.dataset.mode === 'still'; });
      if (still && this.eager) { this.video.removeAttribute('autoplay'); this.video.pause(); }
      listen(this.video, 'play', () => { this.sync(true); if (!this.hasAttribute('data-started')) this.loading(true); });
      listen(this.video, 'pause', () => { this.sync(false); if (!this.hasAttribute('data-error')) this.loading(false); });
      const firstFrame = () => { this.setAttribute('data-started', ''); this.removeAttribute('data-error'); this.loading(false); this.sync(!this.video.paused); };
      listen(this.video, 'playing', firstFrame);
      listen(this.video, 'waiting', () => this.loading(true));
      listen(this.video, 'emptied', () => { this.removeAttribute('data-started'); this.sync(false); });
      listen(this.video, 'error', () => {
        this.setAttribute('data-error', ''); this.removeAttribute('data-started'); this.loading(false); this.sync(false);
        if (this.amb) this.amb.userPaused = true;
        const message = this.querySelector('[data-film-status]');
        if (message) { message.textContent = this.dataset.errorLabel || 'Video unavailable.'; message.hidden = false; }
      });
      const loadFilm = () => window.ggLoadFilm(this.video);
      const loadOnIntent = () => { if (this.dataset.mode !== 'still') loadFilm(); };
      this.addEventListener('click', loadOnIntent, true);
      this._listeners.push(() => this.removeEventListener('click', loadOnIntent, true));
      if (!still && this.dataset.mode !== 'still' && this.dataset.mode !== 'sequence') {
        if ('IntersectionObserver' in window) {
          this._loadObserver = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) { loadFilm(); this._loadObserver.disconnect(); }
          }, {rootMargin: '300px 0px'});
          this._loadObserver.observe(this);
        } else loadFilm();
      }
      const frame = this.closest('.srlm-media') || this;
      const onCover = !!this.closest('.srlm-experience__cover-media');
      if (this.dataset.mode === 'still') { this.video.removeAttribute('autoplay'); this.video.pause(); this.sync(false); return; }
      const hoverByButton = this.hasAttribute('data-promo-film') && this.dataset.mode === 'hover' && (still || !window.matchMedia('(hover: hover) and (pointer: fine)').matches);
      this.amb = this.dataset.mode === 'scroll' ? window.ggScrollPlay(this.video, frame)
        : this.dataset.mode === 'sequence' || hoverByButton ? window.ggSequence(this.video, frame, { control: frame !== this, tap: frame !== this })
        : this.dataset.mode === 'hover' ? window.ggHover(this.video, frame, { startPaused: still, control: frame !== this && !onCover, tap: frame !== this && !onCover })
        : window.ggAmbient(this.video, frame, { startPaused: still, control: frame !== this && !onCover, tap: frame !== this && !onCover, always: this.dataset.mode === 'always' || !!this.closest('[data-house-playback="always"]') });
      this.querySelectorAll('[data-film-toggle]').forEach((button) => listen(button, 'click', (event) => {
        event.preventDefault(); event.stopPropagation();
        if (this.hasAttribute('data-error')) { this.removeAttribute('data-error'); this.loading(true); this.video.load(); }
        if (this.amb) this.amb.toggle();
      }));
      if (frame === this && this.querySelector('[data-film-toggle]')) this.unpeek = window.ggTapPeek(this);
      this.querySelectorAll('[data-film-sound]').forEach((button) => listen(button, 'click', () => {
        this.video.muted = !this.video.muted;
        button.setAttribute('aria-pressed', String(!this.video.muted));
        button.setAttribute('aria-label', this.video.muted ? 'Unmute film' : 'Mute film');
        this.toggleAttribute('data-sound-on', !this.video.muted);
      }));
      this.sync(false);
      if (!this.video.paused && this.video.readyState >= 2 && this.video.requestVideoFrameCallback) this._firstFrame = this.video.requestVideoFrameCallback(firstFrame);
    }
    get userPaused() { return this.amb ? this.amb.userPaused : false; }
    set userPaused(value) { if (this.amb && !value) this.amb.userPaused = false; }
    start() { if (this.amb) this.amb.apply(); }
    disconnectedCallback() {
      this._loadObserver?.disconnect(); this._loadObserver = null;
      if (this.amb) this.amb.destroy(); this.amb = null;
      if (this.unpeek) { this.unpeek(); this.unpeek = null; }
      if (this._firstFrame != null && this.video.cancelVideoFrameCallback) this.video.cancelVideoFrameCallback(this._firstFrame); this._firstFrame = null;
      if (this._listeners) this._listeners.forEach(off => off()); this._listeners = null;
    }
    loading(active) {
      const show = active && !this.hasAttribute('data-started') && !this.hasAttribute('data-error');
      this.toggleAttribute('data-loading', show);
      const message = this.querySelector('[data-film-status]');
      if (message && !this.hasAttribute('data-error')) { message.textContent = show ? this.dataset.loadingLabel || 'Loading video…' : ''; message.hidden = !show; }
    }
    sync(playing) {
      this.toggleAttribute('data-playing', playing);
      this.querySelectorAll('[data-film-toggle]').forEach(button => {
        button.setAttribute('aria-label', playing ? this.dataset.pauseLabel || 'Pause video' : this.dataset.playLabel || 'Play video');
        button.setAttribute('aria-pressed', String(playing));
      });
    }
  }
  customElements.define('gg-film', GGFilm);

  /* ----------------------------------------------------------
     [data-pieces-open] → the "pieces in this look" dialog inside
     the same [data-pieces-scope]. Native <dialog>: Escape closes,
     backdrop click closes, focus returns to the opener.
     ---------------------------------------------------------- */
  /* [data-flip] — a look card turns over to show its pieces (the cover: its pieces slide in as a sheet) */
  document.addEventListener('click', (ev) => {
    const flip = ev.target.closest('[data-flip]');
    if (!flip) return;
    const tile = flip.closest('.gg-ctile, [data-flip-host]');
    if (!tile) return;
    const on = !tile.classList.contains('is-flipped');
    tile.classList.toggle('is-flipped', on);
    tile.querySelectorAll('[data-flip][aria-expanded]').forEach((b) => b.setAttribute('aria-expanded', String(on)));
    const back = tile.querySelector('.gg-flip__back');
    if (back) back.setAttribute('aria-hidden', String(!on));
    if (on) setTimeout(() => back?.querySelector('a, button')?.focus({ preventScroll: true }), 500);
  });
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') document.querySelectorAll('.gg-ctile.is-flipped, [data-flip-host].is-flipped').forEach((t) => t.querySelector('[data-flip]')?.click());
  });

  /* ---- THE HOUSE DOOR'S BOX: a grid-stretched frame has no definite height for cq units, so the box is given its
     height explicitly — the words' column beside it, never under its own floor (min-height) — and the framed picture
     covers it whatever the window does. ---- */
  const sizeDoors = () => {
    document.querySelectorAll('.gg-hpromo').forEach((sec) => {
      const media = sec.querySelector('.gg-hpromo__media'); const text = sec.querySelector('.gg-hpromo__text'); if (!media) return;
      const cols = getComputedStyle(sec).gridTemplateColumns.split(' ').filter((c) => c && c !== 'none').length;
      const floor = parseFloat(getComputedStyle(media).minHeight) || 0;
      // a door with a chosen ratio (Enter the House · picture ratio) is that shape at its own width, whatever the words do
      const ar = (getComputedStyle(media).getPropertyValue('--door-ar') || '').split('/').map((n) => parseFloat(n));
      if (media.classList.contains('gg-hpromo__media--ratio') && ar[0] > 0 && ar[1] > 0) {
        // the ROW is the picture's shape: the words sit inside it, and the picture covers the whole row (no band above or under it)
        const h = Math.round(media.offsetWidth * ar[1] / ar[0]);
        // the words' own height, read with the picture at its shape (never the row it stretched, or it only ever grows)
        if (media.style.height !== h + 'px') media.style.height = h + 'px';
        const words = cols > 1 && text ? Math.ceil([...text.children].reduce((y, c) => Math.max(y, c.getBoundingClientRect().bottom), 0) - text.getBoundingClientRect().top + parseFloat(getComputedStyle(text).paddingBottom)) : 0;
        const px = Math.max(h, words) + 'px'; if (media.style.height !== px) media.style.height = px; return;
      }
      const want = Math.max(floor, cols > 1 && text ? text.offsetHeight : 0);
      const px = Math.round(want) + 'px'; if (media.style.height !== px) media.style.height = px;
    });
  };
  sizeDoors();
  window.addEventListener('resize', sizeDoors);
  if (window.ResizeObserver) { const ro = new ResizeObserver(sizeDoors); document.querySelectorAll('.gg-hpromo__text').forEach((t) => ro.observe(t)); }
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(sizeDoors);

  /* ---- SCENES (snippets/scene-marks, docs/PIECES.md) ----
     A GROUP mark opens to the pieces in that area; one open at a time; a press elsewhere or Escape closes it. The list
     leans away from the frame's edges so it never runs out of the picture. */
  let markSerial = 0;
  const closeMark = (mark, restoreFocus = false) => {
    mark.classList.remove('is-open');
    const button = mark.querySelector('.gg-mark__open');
    if (button) button.setAttribute('aria-expanded', 'false');
    const popover = mark.querySelector('[data-mark-popover], .gg-mark__pop');
    if (popover) popover.hidden = true;
    if (restoreFocus && button) button.focus({ preventScroll: true });
  };
  const closeMarks = (except, restoreFocus = false) => document.querySelectorAll('.gg-mark.is-open').forEach(mark => { if (mark !== except) closeMark(mark, restoreFocus); });
  const positionMark = mark => {
    const popover = mark.querySelector('[data-mark-popover]'); if (!popover) return;
    const host = mark.closest('[data-scene-host], .srlm-media, .gg-look__media') || mark.parentElement;
    const bounds = host.getBoundingClientRect(), anchor = mark.getBoundingClientRect();
    const left = Math.max(12, bounds.left + 12), right = Math.min(window.innerWidth - 12, bounds.right - 12);
    const top = Math.max(12, bounds.top + 12);
    let bottom = Math.min(window.innerHeight - 12, bounds.bottom - 12);
    // Keep the whole product card above the editor's floating footer.
    const toolbar = document.documentElement.classList.contains('gg-editor-hidden') ? null : document.querySelector('.gg-lab-bar');
    const toolbarBounds = toolbar?.getBoundingClientRect();
    if (toolbarBounds?.height > 0 && toolbarBounds.right > left && toolbarBounds.left < right && toolbarBounds.top > top) bottom = Math.min(bottom, toolbarBounds.top - 12);
    popover.classList.toggle('is-compact', bottom - top < 320);
    popover.style.setProperty('--mark-card-width', Math.max(160, Math.min(320, right - left)) + 'px');
    popover.style.setProperty('--mark-card-height', Math.max(120, bottom - top) + 'px');
    const box = popover.getBoundingClientRect();
    const x = Math.max(left, Math.min(anchor.left + anchor.width / 2 - box.width / 2, right - box.width));
    let y = anchor.bottom + 8;
    if (y + box.height > bottom) y = anchor.top - box.height - 8;
    y = Math.max(top, Math.min(y, bottom - box.height));
    popover.style.left = (x - anchor.left) + 'px'; popover.style.top = (y - anchor.top) + 'px';
  };
  const openMark = (mark, keyboard = false) => {
    closeMarks(mark);
    const button = mark.querySelector('.gg-mark__open'), popover = mark.querySelector('[data-mark-popover], .gg-mark__pop');
    if (!button || !popover) return;
    if (!popover.id) popover.id = 'gg-mark-details-' + (++markSerial);
    button.setAttribute('aria-controls', popover.id); button.setAttribute('aria-expanded', 'true');
    mark.classList.add('is-open'); popover.hidden = false; positionMark(mark);
    if (keyboard) popover.querySelector('button, a')?.focus({ preventScroll: true });
  };
  window.ggMarks = { open: openMark, close: mark => mark ? closeMark(mark) : closeMarks() };
  document.addEventListener('click', ev => {
    const close = ev.target.closest('[data-mark-close]');
    if (close) { ev.preventDefault(); ev.stopPropagation(); const mark = close.closest('.gg-mark'); if (mark) closeMark(mark, true); return; }
    const button = ev.target.closest('.gg-mark__open');
    if (!button) return;
    ev.preventDefault(); ev.stopPropagation();
    const mark = button.closest('.gg-mark'); if (!mark) return;
    if (mark.classList.contains('is-open')) closeMark(mark, true); else openMark(mark, ev.detail === 0);
  });
  document.addEventListener('pointerdown', ev => { if (!ev.target.closest('.gg-mark.is-open')) closeMarks(); });
  document.addEventListener('focusin', ev => { if (!ev.target.closest('.gg-mark.is-open')) closeMarks(); });
  document.addEventListener('keydown', ev => {
    if (ev.key !== 'Escape' || !document.querySelector('.gg-mark.is-open')) return;
    ev.preventDefault(); ev.stopImmediatePropagation(); closeMarks(null, true);
  }, true);
  window.addEventListener('resize', () => document.querySelectorAll('.gg-mark.is-open').forEach(positionMark));

  /* THE FILM RULE on a scene host: while its film plays on a press — a sequence, the round button, a hover — the marks
     and the words step off the picture (gg-scene-rolling); a film that simply plays on (autoplay / eager) keeps them.
     And a pressed film with less than fifteen percent still in view rests: it pauses, goes back to its first frame under its cover,
     and the marks and words return. */
  const sceneHosts = () => document.querySelectorAll('[data-scene-host]');
  // a film plays on a press when its rule is the sequence or the hover (the theme's mode, the engine's own class on the frame, the House frame's setting, or the Lab's choice) — or when the round button woke a resting film (is-held).
  // Never a Scroll-play film: the scroll winds it (its engine marks its frame is-held once the film can play), and one that
  // was still catching up with the scroll as its frame left full view was sent back to its first frame — the House's film
  // band and the home's film bands rewound as they let go (found 2026-09-30, headless, the hold at release)
  const pressed = (video) => { if (video.__ambient && video.__ambient.scroll) return false; const f = video.closest('gg-film'); const mode = f ? f.dataset.mode : ''; if (mode === 'eager') return !!video.closest('.is-sequence, .is-hover-film'); return mode === 'sequence' || mode === 'hover' || !!video.closest('.is-sequence, .is-hover-film, .is-held, [data-house-playback="sequence"], [data-house-playback="hover"], [data-lab-playback="sequence"], [data-lab-playback="hover"]'); };
  const armScene = (host) => {
    if (host.__scene) return; host.__scene = true;
    // the film may be swapped under the host (the Lab re-mounts it): listen on the host, in capture, and ask for the video each time
    const vid = () => host.querySelector('video');
    const paint = () => { const video = vid(); host.classList.toggle('gg-scene-rolling', !!video && !video.paused && !video.ended && pressed(video)); };
    ['play', 'playing', 'pause', 'ended', 'emptied'].forEach((e) => host.addEventListener(e, paint, true));
    const rest = () => { const video = vid(); if (!video || video.paused || !pressed(video)) return; try { video.pause(); } catch {} const a = video.__ambient; if (a && 'userPaused' in a) a.userPaused = true; const t = window.ggTrimOf ? window.ggTrimOf(video) : { in: 0 }; try { video.currentTime = t.in || 0; } catch {} host.querySelectorAll('.is-held, .is-rolled').forEach((n) => n.classList.remove('is-held', 'is-rolled')); host.classList.remove('is-held', 'is-rolled'); paint(); };
    // Scene controls return only when the film has substantially left the view.
    const io = new IntersectionObserver(() => { if (window.ggSeen(host) < FILM_EXIT_SHARE) rest(); }, { threshold: [0, FILM_EXIT_SHARE, FILM_ENTER_SHARE, 1] });
    io.observe(host);
    paint();
  };
  sceneHosts().forEach(armScene);
  new MutationObserver(() => sceneHosts().forEach((h) => { if (!h.__scene) armScene(h); })).observe(document.body, { childList: true, subtree: true });

  document.addEventListener('click', (ev) => {
    const opener = ev.target.closest('[data-pieces-open]');
    if (opener) {
      const scope = opener.closest('[data-pieces-scope]') || document;
      const dlg = scope.querySelector('dialog.gg-pieces');
      if (!dlg || typeof dlg.showModal !== 'function') return;
      dlg.__opener = opener;
      // desktop: a card in the tile's corner, the page stays; phone: a sheet
      if (window.innerWidth >= 750 && dlg.closest('[data-pieces-scope]')) {
        if (dlg.open) dlg.close(); else { dlg.classList.add('gg-pieces--inline'); dlg.show(); }
      } else {
        dlg.classList.remove('gg-pieces--inline');
        dlg.showModal();
      }
      return;
    }
    const closer = ev.target.closest('[data-pieces-close]');
    if (closer) closer.closest('dialog')?.close();
  });
  document.addEventListener('click', (ev) => {
    // backdrop click: the dialog itself is the target, its panel is not
    if (ev.target instanceof HTMLDialogElement && ev.target.classList.contains('gg-pieces')) ev.target.close();
    // an inline card closes when the click lands outside its tile
    document.querySelectorAll('dialog.gg-pieces--inline[open]').forEach((d) => {
      const scope = d.closest('[data-pieces-scope]');
      if (scope && !scope.contains(ev.target)) d.close();
    });
  });
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') document.querySelectorAll('dialog.gg-pieces--inline[open]').forEach((d) => d.close());
  });
  document.addEventListener('close', (ev) => {
    const dlg = ev.target;
    if (dlg instanceof HTMLDialogElement && dlg.__opener) dlg.__opener.focus();
  }, true);

  /* ----------------------------------------------------------
     <gg-lookbook> — chapter navigation for the lookbook page.
     · the index rail + counter follow the look in view
     · prev / next buttons and ← → keys move one look
     · the address bar carries #look-<handle> (deep links)
     ---------------------------------------------------------- */
  class GGLookbook extends HTMLElement {
    connectedCallback() {
      this.looks = Array.from(this.querySelectorAll('[data-look]'));
      if (!this.looks.length) return;
      this.links = Array.from(this.querySelectorAll('[data-look-link]'));
      this.current = this.querySelector('[data-look-current]');
      this.active = 0;
      this.querySelector('[data-look-prev]')?.addEventListener('click', () => this.go(this.active - 1));
      this.querySelector('[data-look-next]')?.addEventListener('click', () => this.go(this.active + 1));
      this.onKey = (e) => {
        if (e.target instanceof Element && e.target.closest('input, textarea, select, [contenteditable]')) return;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); this.go(this.active + 1); }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); this.go(this.active - 1); }
      };
      document.addEventListener('keydown', this.onKey);
      this.io = new IntersectionObserver(
        (entries) => {
          // the look owning the most viewport wins
          let best = null;
          entries.forEach((e) => { if (e.isIntersecting && (!best || e.intersectionRatio > best.intersectionRatio)) best = e; });
          if (best) this.setActive(this.looks.indexOf(best.target), false);
        },
        { threshold: [0.25, 0.5, 0.75] }
      );
      this.looks.forEach((l) => this.io.observe(l));
      // swipe (touch) — horizontal swipe changes chapter
      let x0 = null;
      this.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      this.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        x0 = null;
        if (Math.abs(dx) > 70) this.go(this.active + (dx < 0 ? 1 : -1));
      }, { passive: true });
      const initial = this.looks.findIndex((look) => `#${look.id}` === window.location.hash);
      this.setActive(initial < 0 ? 0 : initial, false);
    }
    disconnectedCallback() {
      this.io?.disconnect();
      document.removeEventListener('keydown', this.onKey);
    }
    go(i) {
      i = clamp(i, 0, this.looks.length - 1);
      this.looks[i].scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      this.setActive(i);
    }
    setActive(i, updateHash = true) {
      this.active = i;
      const look = this.looks[i];
      this.links.forEach((a, n) => {
        if (n === i) a.setAttribute('aria-current', 'true');
        else a.removeAttribute('aria-current');
      });
      if (this.current) this.current.textContent = String(i + 1).padStart(2, '0');
      if (updateHash && look?.id && history.replaceState) history.replaceState(null, '', `#${look.id}`);
      this.toggleAttribute('data-at-start', i === 0);
      this.toggleAttribute('data-at-end', i === this.looks.length - 1);
    }
  }
  customElements.define('gg-lookbook', GGLookbook);


  /* ----------------------------------------------------------
     <gg-search> — the full-screen search room. Opens from any
     [data-search-open] or the "/" key; Esc / scrim closes. Types
     go to the Predictive Search API (debounced), results render
     from the section's <template>. ↑ ↓ walk the results, Enter
     opens the focused one (or submits the query).
     ---------------------------------------------------------- */
  // predictive-search URLs: same-site paths or http(s) only; anything else (javascript:, data:, //host) becomes '#'
  const safeUrl = (u) => {
    const v = String(u == null ? '' : u).trim();
    if (/^\/(?!\/)/.test(v)) return v;
    try { const x = new URL(v, location.href); return x.protocol === 'https:' || x.protocol === 'http:' ? x.href : '#'; } catch (e) { return '#'; }
  };
  const sizedImg = (u, w) => { const v = safeUrl(u); return v === '#' ? v : `${v}${v.includes('?') ? '&' : '?'}width=${w}`; };
  class GGSearch extends HTMLElement {
    connectedCallback() {
      this.workspace = this.querySelector('gg-asearch');
      if (this.workspace) { this.connectWorkspace(); return; }

      this.input = this.querySelector('[data-search-input]');
      this.zero = this.querySelector('[data-search-zero]');
      this.results = this.querySelector('[data-search-results]');
      this.status = this.querySelector('[data-search-status]');
      this.tpl = this.querySelector('[data-search-template]');
      if (!this.input) return;
      this.querySelector('.gg-search__advanced')?.addEventListener('click', (event) => {
        const url = new URL(event.currentTarget.href);
        const query = this.input.value.trim();
        if (query) url.searchParams.set('q', query);
        event.currentTarget.href = url.pathname + url.search;
      });
      this.url = this.dataset.url || '/search/suggest';
      this.searchUrl = this.dataset.searchUrl || '/search';
      this.money = (window.GG && window.GG.moneyFormat) || null;
      this.querySelectorAll('[data-search-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      this.querySelectorAll('[data-search-term]').forEach((b) =>
        b.addEventListener('click', () => { this.input.value = b.dataset.searchTerm; this.input.focus(); this.query(); })
      );
      let t = null;
      this.input.addEventListener('input', () => { clearTimeout(t); t = setTimeout(() => this.query(), 160); });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
        if (e.key === 'Tab') { this.trap(e); return; }
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); this.walk(e.key === 'ArrowDown' ? 1 : -1); }
      });
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-search-open]');
        if (opener) { e.preventDefault(); this.open(); }
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === '/' && !e.metaKey && !e.ctrlKey && !(e.target instanceof Element && e.target.closest('input, textarea, select, [contenteditable]'))) {
          e.preventDefault(); this.open();
        }
      });
      // the search field in the Shop menu (snippets/search-field): Enter opens the room with the words already typed
      // and the live results under them, and the menu closes behind it; without this script the form submits
      document.addEventListener('submit', (e) => {
        const form = e.target.closest && e.target.closest('[data-search-field]'); if (!form) return;
        e.preventDefault();
        const q = (form.querySelector('input[name="q"]') || {}).value || '';
        const dd = form.closest('details[data-gg-shopdd]'); if (dd) dd.removeAttribute('open');
        const menu = document.querySelector('[data-menu-toggle][aria-expanded="true"]'); if (menu) menu.click();
        this.open(); this.input.value = q; this.query();
      });
    }
    connectWorkspace() {
      this.input = this.workspace.querySelector('[data-as-q]');
      this.querySelectorAll('[data-search-close]').forEach(button => button.addEventListener('click', () => this.close()));
      document.addEventListener('keydown', event => {
        if (this.hidden) return;
        if (event.key === 'Escape') {
          event.preventDefault(); event.stopPropagation();
          if (this.workspace.open) this.workspace.toggle(null); else this.close();
        }
        if (event.key === 'Tab') this.trap(event);
      });
      document.addEventListener('click', event => {
        if (event.target.closest('[data-search-open]')) { event.preventDefault(); this.hidden ? this.open() : this.close(); }
      });
      document.addEventListener('click', event => {
        if (!this.hidden && event.target.closest('.gg-header [data-menu-toggle], .gg-header [data-drawer-toggle], .gg-header [data-gg-shopdd] > summary')) this.close({restoreFocus:false});
      }, true);
      document.addEventListener('keydown', event => {
        if (event.key === '/' && !event.metaKey && !event.ctrlKey && !event.target.closest('input, textarea, select, [contenteditable]')) { event.preventDefault(); this.open(); }
      });
      document.addEventListener('submit', event => {
        const form = event.target.closest('[data-search-field]');
        if (!form) return;
        event.preventDefault();
        const query = form.querySelector('[name="q"]')?.value || '';
        form.closest('details[data-gg-shopdd]')?.removeAttribute('open');
        document.querySelector('[data-menu-toggle][aria-expanded="true"]')?.click();
        this.open();
        const target = document.querySelector('gg-asearch:not([data-as-embedded])') || this.workspace;
        target.q.value = query; target.ask({q:query});
      });
    }
    open() {
      const pageSearch = document.querySelector('gg-asearch:not([data-as-embedded]) [data-as-q]');
      if (pageSearch) { pageSearch.scrollIntoView({ block: 'start' }); if (!matchMedia('(max-width:749px), (pointer:coarse)').matches) pageSearch.focus({preventScroll:true}); return; }
      if (this.hidden) this.lastFocus = document.activeElement;
      document.querySelector('[data-menu-toggle][aria-expanded="true"]')?.click();
      document.querySelectorAll('details[data-gg-shopdd][open]').forEach(menu => menu.removeAttribute('open'));
      this.hidden = false;
      this.workspace?.load();
      document.body.classList.add('gg-search-open');
      this.syncHeader(true);
      this.workspace?.updateViewport();
      this.workspace?.onViewport();
      const focusTarget = matchMedia('(max-width:749px), (pointer:coarse)').matches ? document.querySelector('.gg-header [data-search-open]') : this.input;
      focusTarget?.focus({preventScroll:true});
      // the advanced search's index, read while the words are typed: the facets they name are offered as doors
      if (!this.index && this.dataset.indexUrl && window.ggSearchIndex) {
        window.ggSearchIndex.load(this.dataset.indexUrl).then((items) => { this.index = items; this.fillFacets(); }).catch(() => {});
      }
    }
    syncHeader(open) {
      document.querySelectorAll('.gg-header [data-search-open]').forEach(button => {
        if (!button.dataset.searchLabel) button.dataset.searchLabel = button.getAttribute('aria-label') || 'Search';
        button.setAttribute('aria-expanded', String(open));
        button.setAttribute('aria-controls', 'GGSearchPanel');
        button.setAttribute('aria-label', open ? 'Close search' : button.dataset.searchLabel);
      });
    }
    // Metal · Sterling silver 121 — every facet value the words name (a word of three letters or more starting one of
    // its words, or the whole of what is typed inside it), most pieces first, each a door into the advanced search
    facetMatches(q) {
      const S = window.ggSearchIndex;
      if (!this.index || !S) return [];
      const text = q.toLowerCase().trim();
      const toks = text.split(/\s+/).filter((t) => t.length >= 3);
      if (!this.facetCounts) {
        this.facetCounts = new Map();
        for (const it of this.index) for (const f of S.facets) for (const v of it.f[f.key]) { const k = `${f.key}\u0000${v}`; this.facetCounts.set(k, (this.facetCounts.get(k) || 0) + 1); }
      }
      const out = [];
      for (const [k, n] of this.facetCounts) {
        const [key, v] = k.split('\u0000');
        const name = (key === 'dept' ? S.depts[v] || v : v).toLowerCase();
        const words = name.split(/[\s&-]+/);
        if (name.includes(text) || toks.some((t) => words.some((w) => w.startsWith(t)))) out.push({ key, v, n, label: key === 'dept' ? S.depts[v] || v : v, facet: (S.facets.find((f) => f.key === key) || {}).label });
      }
      return out.sort((a, b) => b.n - a.n).slice(0, 6);
    }
    fillFacets(root = this.results) {
      const list = root && root.querySelector('[data-t="facets"]');
      const label = root && root.querySelector('[data-t="facets-label"]');
      if (!list) return;
      const found = this.facetMatches(this.input.value || '');
      list.innerHTML = found.map((m) => `<li><a href="${esc(this.searchUrl)}?${encodeURIComponent(m.key)}=${encodeURIComponent(m.v)}"><span class="gg-search__facet">${esc(m.facet)}</span> ${esc(m.label)} <span class="gg-search__n">${m.n}</span></a></li>`).join('');
      if (label) label.hidden = !found.length;
    }
    close({restoreFocus = true} = {}) {
      this.ctl?.abort();
      this.ctl = null;
      this.removeAttribute('aria-busy');
      if (this.status) this.status.hidden = true;
      this.input?.blur();
      this.hidden = true;
      document.body.classList.remove('gg-search-open');
      this.syncHeader(false);
      const back = this.lastFocus; this.lastFocus = null;
      if (restoreFocus && back && back.isConnected) back.focus?.({preventScroll:true});
    }
    // Keep keyboard navigation in the open search and its persistent site header.
    trap(e) {
      const room = this.querySelector('[aria-modal="true"]') || this;
      const selector = 'a[href], button:not([disabled]), summary, input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      const items = [...(this.workspace ? document.querySelectorAll('.gg-header a[href], .gg-header button:not([disabled]), .gg-header summary') : []), ...room.querySelectorAll(selector)]
        .filter((n) => n.offsetParent || n.getClientRects().length);
      if (!items.length) { e.preventDefault(); return; }
      const first = items[0], last = items[items.length - 1], i = items.indexOf(document.activeElement);
      if (e.shiftKey && (i <= 0)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (i < 0 || i === items.length - 1)) { e.preventDefault(); first.focus(); }
    }
    walk(dir) {
      const items = [...this.querySelectorAll('.gg-search__live a, .gg-search__zero a, .gg-search__zero button')].filter((a) => a.offsetParent);
      if (!items.length) return;
      const i = items.indexOf(document.activeElement);
      const next = i < 0 ? (dir > 0 ? 0 : items.length - 1) : (i + dir + items.length) % items.length;
      items[next].focus();
    }
    async query() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.ctl?.abort();
        this.ctl = null;
        this.removeAttribute('aria-busy');
        if (this.status) this.status.hidden = true;
        this.results.hidden = true; this.zero.hidden = false; this.input.setAttribute('aria-expanded', 'false');
        return;
      }
      const ctl = new AbortController();
      this.ctl?.abort(); this.ctl = ctl;
      this.setAttribute('aria-busy', 'true');
      if (this.status) { this.status.textContent = 'Searching…'; this.status.hidden = false; }
      let data;
      try {
        const r = await fetch(`${this.url}.json?q=${encodeURIComponent(q)}&resources[type]=product,collection,query&resources[limit]=8&resources[limit_scope]=each&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,vendor,tag,variants.title`, { signal: ctl.signal });
        if (!r.ok) throw new Error(`predictive search ${r.status}`);
        data = (await r.json()).resources.results;
      } catch (e) {
        if (ctl === this.ctl) {
          this.removeAttribute('aria-busy');
          if (this.status) { this.status.textContent = 'Search unavailable. Press Enter to search the catalog.'; this.status.hidden = false; }
        }
        return;
      }
      if (ctl !== this.ctl) return;
      this.removeAttribute('aria-busy');
      const frag = this.tpl.content.cloneNode(true);
      const el = (n) => frag.querySelector(`[data-t="${n}"]`);
      let products = (data.products || []).filter((p) => !(p.tags || []).includes('ghost'));
      // a search about jewelry: every other place a new jewelry piece — the suggestions' own, then the index's new
      // jewelry that answers the words (searchIndex · asEveryOtherNew); without the index the suggestions stand as given
      const S = window.ggSearchIndex;
      if (this.index && S && S.everyOtherNew) {
        const byHandle = this.indexByHandle || (this.indexByHandle = new Map(this.index.map((it) => [it.h, it])));
        const tokens = S.tokens(q);
        const known = products.map((p) => byHandle.get(p.handle)).filter(Boolean);
        if (S.jewelryAsked(tokens, null, known)) {
          const isNew = (p) => S.isNewJewel(byHandle.get(p.handle));
          const have = new Set(products.map((p) => p.handle));
          const wanted = Math.ceil(products.length / 2) - products.filter(isNew).length;
          const extra = wanted > 0 ? this.index.filter((it) => !have.has(it.h) && S.isNewJewel(it) && S.score(it, tokens))
            .map((it) => [it, S.score(it, tokens)]).sort((a, b) => b[1] - a[1] || a[0].i - b[0].i).slice(0, wanted)
            .map(([it]) => ({ handle: it.h, url: `/products/${it.h}`, title: it.t, price: (it.p || 0) / 100, featured_image: { url: ((/<img[^>]+src="([^"]+)"/.exec(it.card) || [])[1] || '').replace(/&amp;/g, '&').replace(/^\/\//, 'https://') } })) : [];
          const rest = products.filter((p) => !isNew(p));
          products = S.everyOtherNew([...products.filter(isNew), ...extra, ...rest], isNew).slice(0, Math.max(products.length, 8));
        }
      }
      el('products').innerHTML = products.map((p) => `
        <li><a href="${esc(safeUrl(p.url))}" class="gg-search__product">
          <span class="gg-search__product-media">${p.featured_image?.url && safeUrl(p.featured_image.url) !== '#' ? `<img src="${esc(sizedImg(p.featured_image.url, 720))}" alt="" loading="lazy" width="360" height="450">` : ''}</span>
          <span class="gg-search__product-name">${esc(p.title)}</span>
          <span class="gg-search__product-price">${esc(this.formatMoney(p.price))}</span>
        </a></li>`).join('');
      el('products-label').hidden = !products.length;
      el('collections').innerHTML = (data.collections || []).map((c) => `<li><a href="${esc(safeUrl(c.url))}">${esc(c.title)}</a></li>`).join('');
      el('collections-label').hidden = !(data.collections || []).length;
      const queries = (data.queries || []).map((x) => x.text).filter((x) => x.toLowerCase() !== q.toLowerCase());
      el('queries').innerHTML = queries.map((x) => `<li><a href="${esc(this.searchUrl)}?q=${encodeURIComponent(x)}">${esc(x)}</a></li>`).join('');
      el('queries-label').hidden = !queries.length;
      el('all').href = `${this.searchUrl}?q=${encodeURIComponent(q)}&type=product`;
      this.fillFacets(frag);
      el('none').hidden = !!(products.length || (data.collections || []).length || !el('facets-label').hidden);
      this.results.replaceChildren(frag);
      this.results.hidden = false; this.zero.hidden = true;
      if (this.status) { this.status.textContent = `${products.length} ${products.length === 1 ? 'piece' : 'pieces'} found`; this.status.hidden = false; }
      this.input.setAttribute('aria-expanded', 'true');
    }
    formatMoney(v) {
      // predictive search returns the price as a decimal string in the shop currency
      const n = Number(v);
      if (!isFinite(n)) return v;
      const fmt = this.money || '{{amount_no_decimals}} kr';
      const whole = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 }).format(n);
      return fmt.replace(/\{\{\s*amount[^}]*\}\}/, whole);
    }
  }
  customElements.define('gg-search', GGSearch);


  /* ----------------------------------------------------------
     <gg-carousel> — editorial carousel / racks with arrows.
     Arrows advance exactly one viewport: scrollBy(±clientWidth).
     Counter shows "01 — 03" style current/total.
     ---------------------------------------------------------- */
  class GGCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.counterCurrent = this.querySelector('[data-carousel-current]');
      if (!this.track) return;
      this.querySelectorAll('[data-carousel-prev]').forEach((b) =>
        b.addEventListener('click', () => this.slide(-1))
      );
      this.querySelectorAll('[data-carousel-next]').forEach((b) =>
        b.addEventListener('click', () => this.slide(1))
      );
      const sync = () => {
        this.syncCounter();
        this.syncFades();
      };
      this.track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      sync();
    }

    slide(dir) {
      const t = this.track;
      const max = t.scrollWidth - t.clientWidth;
      if (dir > 0 && t.scrollLeft >= max - 8) {
        // past the end — loop back to the start
        t.scrollTo({ left: 0, behavior: 'smooth' });
      } else if (dir < 0 && t.scrollLeft <= 8) {
        // before the start — loop around to the end
        t.scrollTo({ left: max, behavior: 'smooth' });
      } else {
        t.scrollBy({ left: dir * t.clientWidth, behavior: 'smooth' });
      }
    }

    syncCounter() {
      if (!this.counterCurrent) return;
      const index = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
      this.counterCurrent.textContent = String(index + 1).padStart(2, '0');
    }

    /* edge dissolves appear only where more content exists */
    syncFades() {
      const t = this.track;
      t.toggleAttribute('data-fade-left', t.scrollLeft > 8);
      t.toggleAttribute('data-fade-right', t.scrollLeft + t.clientWidth < t.scrollWidth - 8);
    }
  }
  customElements.define('gg-carousel', GGCarousel);

  /* ----------------------------------------------------------
     <gg-craft-film> — the sitewide atelier craft film. Muted
     loop started by the IntersectionObserver (no autoplay attr,
     so off-screen pages never fetch the film), paused when it
     leaves the viewport, with a plaque play/pause toggle. When
     the visitor prefers reduced motion it stays still until
     they press play.
     ---------------------------------------------------------- */
  class GGCraftFilm extends HTMLElement {
    connectedCallback() {
      if (this._listeners) return;
      this.video = this.querySelector('video');
      if (!this.video) return;
      this._listeners = [];
      const listen = (node, event, fn) => {
        node.addEventListener(event, fn);
        this._listeners.push(() => node.removeEventListener(event, fn));
      };
      // Set the properties as well as the attributes before any play request (iOS).
      this.video.defaultMuted = true;
      this.video.muted = true;
      this.video.playsInline = true;
      this.video.setAttribute('muted', '');
      this.video.setAttribute('playsinline', '');
      this.userPaused = reducedMotion.matches;
      this._playPending = false;
      this._autoplayBlocked = false;
      this._hasStarted = false;
      this._inView = false;
      const allowed = () => {
        this._inView = window.ggSeen(this) >= (this._inView ? FILM_EXIT_SHARE : FILM_ENTER_SHARE);
        return this._inView && !document.hidden && !this.userPaused &&
          !this.video.error && !this.closest('[data-motion-paused]');
      };
      this.remeasure = () => {
        if (!allowed()) { if (!this.video.paused) this.video.pause(); return; }
        if (!this.video.paused || this._playPending) return;
        this._playPending = true;
        const settle = () => { this._playPending = false; if (!this.isConnected || !allowed()) this.video.pause(); };
        const request = this.video.play();
        if (request && request.then) request.then(() => {
          this._autoplayBlocked = false;
          settle();
        }, error => {
          this._autoplayBlocked = error?.name === 'NotAllowedError';
          settle();
          this.syncToggle(false);
        });
        else settle();
      };
      listen(this.video, 'play', () => { this._hasStarted = true; this.syncToggle(true); });
      listen(this.video, 'pause', () => this.syncToggle(false));
      listen(this.video, 'canplay', () => this.remeasure());
      this.querySelectorAll('[data-film-toggle]').forEach(button => listen(button, 'click', event => {
        event.preventDefault();
        event.stopPropagation();
        // A scroll may pause the native video between pointerdown and click. Toggle
        // the running intent as well, so pressing Pause cannot accidentally restart it.
        const running = !this.userPaused && !this._autoplayBlocked && this._hasStarted &&
          (!this.video.paused || this._playPending);
        this.userPaused = running;
        this.remeasure();
        this.syncToggle(!this.video.paused);
      }));
      if (this.querySelector('[data-film-toggle]')) this.unpeek = window.ggTapPeek(this, this.closest('.gg-hhero') || this);
      // Measure the fortress with the other visible films, including while scrolling.
      this._ambientEntry = { video: this.video, remeasure: () => this.remeasure() };
      AMBIENTS.add(this._ambientEntry);
      this.io = new IntersectionObserver(() => this.remeasure(), { threshold: [0, FILM_EXIT_SHARE, FILM_ENTER_SHARE, 1] });
      this.io.observe(this);
      ['visibilitychange', 'resume'].forEach(event => listen(document, event, () => this.remeasure()));
      ['pageshow', 'focus'].forEach(event => listen(window, event, () => this.remeasure()));
      // If the browser blocks muted autoplay, retry only on an actual interaction.
      const retryBlocked = () => { if (this._autoplayBlocked) this.remeasure(); };
      listen(document, 'pointerup', retryBlocked);
      listen(document, 'keydown', retryBlocked);
      listen(reducedMotion, 'change', () => { if (reducedMotion.matches) this.userPaused = true; this.remeasure(); });
      this.syncToggle(!this.video.paused);
      this.remeasure();
    }

    disconnectedCallback() {
      this.io?.disconnect();
      if (this._ambientEntry) AMBIENTS.delete(this._ambientEntry);
      this._ambientEntry = null;
      this._listeners?.forEach(off => off());
      this._listeners = null;
      this.video?.pause();
      if (this.unpeek) { this.unpeek(); this.unpeek = null; }
    }

    syncToggle(playing) {
      this.toggleAttribute('data-playing', playing);
      this.querySelectorAll('[data-film-toggle]').forEach(button => {
        button.setAttribute('aria-label', playing ? 'Pause film' : 'Play film');
        button.setAttribute('aria-pressed', String(playing));
      });
    }
  }
  customElements.define('gg-craft-film', GGCraftFilm);

  /* ----------------------------------------------------------
     <gg-gallery> — PDP single-image gallery.
     Prev/next wrap around; cross bullets jump; active bullet
     full opacity + scale(1.3); counter "NN / 0N".
     ---------------------------------------------------------- */
  class GGGallery extends HTMLElement {
    connectedCallback() {
      /* THE NOTE'S PICTURE LEAVES THE CAROUSEL: the picture the note under the piece shows large
         ([data-story-media-id], sections/product-story-3d) never stands here as well. The product
         section leaves it out itself when its "The note below shows" setting says which one it is;
         this catches a template where that was not said. Same exceptions as there: the last
         picture stays, and so does one a variant is shown by (choosing the variant must be able to
         bring it on stage). */
      const taken = document.querySelector('[data-story-media-id]')?.dataset.storyMediaId;
      const twin = taken ? this.querySelector(`[data-gallery-slide][data-media-id="${taken}"]:not([data-variant-media]):not([data-cp-media-type="model"])`) : null;
      if (twin && this.querySelectorAll('[data-gallery-slide]').length > 1) {
        twin.remove();
        const spare = [...this.querySelectorAll('[data-gallery-bullet]')].pop();
        if (spare) spare.remove();
        const left = this.querySelectorAll('[data-gallery-slide]').length;
        const total = this.querySelector('[data-gallery-total]');
        if (total) total.textContent = String(left).padStart(2, '0');
        this.classList.toggle('is-single', left < 2);
      }
      this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
      this.bullets = [...this.querySelectorAll('[data-gallery-bullet]')];
      this.counter = this.querySelector('[data-gallery-current]');
      this.index = 0;
      // hidden attrs are the no-JS state; with JS we crossfade via class
      this.slides.forEach((s) => {
        s.removeAttribute('hidden');
        s.classList.add('gg-fade-slide');
      });
      this.querySelectorAll('[data-gallery-prev]').forEach((b) =>
        b.addEventListener('click', () => this.go(this.index - 1))
      );
      this.querySelectorAll('[data-gallery-next]').forEach((b) =>
        b.addEventListener('click', () => this.go(this.index + 1))
      );
      this.bullets.forEach((b, i) => b.addEventListener('click', () => this.go(i)));
      // swipe between slides on touch
      const stage = this.querySelector('[data-gallery-stage]') || this;
      let x0 = null, y0 = null;
      stage.addEventListener('touchstart', (e) => { x0 = e.touches.length === 1 ? e.touches[0].clientX : null; y0 = e.touches[0].clientY; }, { passive: true });
      stage.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        const dy = e.changedTouches[0].clientY - y0;
        x0 = null;
        if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.25) this.go(this.index + (dx < 0 ? 1 : -1));
      }, { passive: true });
      // zoom: the active image, large, in a native dialog
      this.lightbox = this.querySelector('[data-gallery-lightbox]');
      this.querySelectorAll('[data-gallery-zoom]').forEach((b) => b.addEventListener('click', () => this.zoom()));
      this.slides.forEach((s) => this.bindZoom(s.querySelector('img')));
      this.querySelectorAll('[data-gallery-lightbox-close]').forEach((b) => b.addEventListener('click', () => this.lightbox?.close()));
      this.lightbox?.addEventListener('click', (e) => { if (e.target === this.lightbox) this.lightbox.close(); });
      this.go(0);
    }

    /* a VARIANT'S PICTURE ON STAGE (gg-variant-picker): the slide that holds media `id`, else the one at product
       position `position`; when the carousel does not hold it (the Curator's Note took it, a template that predates
       the variant rule), it is added from `pic` ({ src, srcset, alt }) as a slide of its own, after the others, with
       a bullet if the gallery has bullets. Answers whether the picture is now on stage; nothing else moves. */
    showMedia({ id, position, pic } = {}) {
      if (!this.slides) return false;
      let slide = (id && this.slides.find((s) => s.dataset.mediaId === String(id)))
        || (position && this.slides.find((s) => s.dataset.mediaPosition === String(position)));
      if (!slide && pic?.src) slide = this.addSlide(id, position, pic);
      if (!slide) return false;
      this.go(this.slides.indexOf(slide));
      return true;
    }

    addSlide(id, position, pic) {
      const stage = this.querySelector('[data-gallery-stage]');
      if (!stage) return null;
      const slide = document.createElement('div');
      slide.className = 'gg-pdp__slide gg-pdp__slide--fill gg-fade-slide';
      slide.setAttribute('data-gallery-slide', '');
      slide.setAttribute('data-variant-media', '');
      slide.setAttribute('data-variant-added', '');
      if (id) slide.dataset.mediaId = String(id);
      if (position) slide.dataset.mediaPosition = String(position);
      const img = document.createElement('img');
      img.src = pic.src;
      if (pic.srcset) { img.srcset = pic.srcset; img.sizes = '(max-width: 749px) 100vw, 50vw'; }
      img.alt = pic.alt || '';
      img.width = 1080;
      img.height = 1080;
      img.decoding = 'async';
      this.bindZoom(img);
      slide.appendChild(img);
      const last = this.slides[this.slides.length - 1];
      if (last && last.parentNode === stage) last.after(slide); else stage.prepend(slide);
      this.slides.push(slide);
      const bullets = this.querySelector('.gg-pdp__bullets');
      const model = this.bullets[this.bullets.length - 1];
      if (bullets && model) {
        const b = model.cloneNode(true);
        b.classList.remove('is-active', 'is-off', 'is-edge');
        const k = this.bullets.length;
        b.addEventListener('click', () => this.go(k));
        bullets.appendChild(b);
        this.bullets.push(b);
      }
      const total = this.querySelector('[data-gallery-total]');
      if (total) total.textContent = String(this.slides.length).padStart(2, '0');
      return slide;
    }

    bindZoom(img) {
      if (!img) return;
      img.setAttribute('role', 'button');
      img.setAttribute('aria-label', this.lightbox?.getAttribute('aria-label') || img.alt);
      img.addEventListener('click', () => this.zoom());
      img.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.zoom(); }
      });
    }

    zoom() {
      const img = this.slides[this.index]?.querySelector('img');
      if (!img || !this.lightbox || typeof this.lightbox.showModal !== 'function') return;
      const big = this.lightbox.querySelector('[data-gallery-lightbox-img]');
      // ask for the largest rendition the srcset offers
      const largest = (img.getAttribute('srcset') || '').split(',').map((s) => s.trim().split(' ')[0]).filter(Boolean).pop();
      big.src = largest || img.currentSrc || img.src;
      big.alt = img.alt || '';
      big.width = img.naturalWidth || img.width || 1600;
      big.height = img.naturalHeight || img.height || 2000;
      this.lightbox.showModal();
    }

    go(i) {
      const n = this.slides.length;
      if (!n) return;
      this.index = ((i % n) + n) % n;
      this.slides.forEach((s, k) => {
        const active = k === this.index;
        s.classList.toggle('is-active', active);
        s.inert = !active;
        s.setAttribute('aria-hidden', String(!active));
        const image = s.querySelector('img[role="button"]');
        if (image) image.tabIndex = active ? 0 : -1;
        // only the slide on stage plays its film
        const v = s.querySelector('video');
        if (v) { if (active && !reducedMotion.matches) v.play().catch(() => {}); else v.pause(); }
      });
      this.bullets.forEach((b, k) => b.classList.toggle('is-active', k === this.index));
      // more than five pictures: five bullets, the window following the one on stage, its ends small where more lie beyond
      const shown = 5, m = this.bullets.length;
      // a gallery that asks for a narrower window (data-bullet-window, the Ledger product page: 3) shows that many at full
      // size around the one on stage, and one faded star on each side where more lie beyond
      const core = +(this.dataset.bulletWindow || 0);
      if (core && m > core) {
        const from = Math.min(Math.max(this.index - Math.floor(core / 2), 0), m - core), to = from + core - 1;
        this.bullets.forEach((b, k) => {
          b.classList.toggle('is-off', k < from - 1 || k > to + 1);
          b.classList.toggle('is-edge', k === from - 1 || k === to + 1);
        });
      } else if (m > shown) {
        const from = Math.min(Math.max(this.index - 2, 0), m - shown), to = from + shown - 1;
        this.bullets.forEach((b, k) => {
          b.classList.toggle('is-off', k < from || k > to);
          b.classList.toggle('is-edge', k !== this.index && ((k === from && from > 0) || (k === to && to < m - 1)));
        });
      }
      if (this.counter) this.counter.textContent = String(this.index + 1).padStart(2, '0');
    }
  }
  customElements.define('gg-gallery', GGGallery);

  /* ----------------------------------------------------------
     Folds — the product page's collapsibles
     (<details class="gg-pdp__accordion">) open and close as one
     movement instead of snapping: the row's height glides, the
     lines inside rise into place one after another, the mark
     turns from + to – (CSS, keyed on [open] and .is-closing).
     One drawer at a time: opening one closes the one that was
     open.

     On desktop the rows are a SHEET standing on the foot of the
     info cell (CSS sets --sheet: 1 on .gg-pdp__accordions): the
     cell keeps its first-screen height whatever is open. The
     rows' place in the cell is locked to their closed height and
     an opening row rises out of it — over the promises, over the
     label if it must — instead of pushing the page down; a veil
     quiets what stands behind, and a click on it, or Escape,
     closes. A body taller than the cell scrolls inside itself.
     Below desktop the rows are in the page's flow and open
     downward, and a drawer that opens below the fold brings
     itself into the window. Reduced motion: the same states,
     without the movement.
     ---------------------------------------------------------- */
  (() => {
    const still = window.matchMedia('(prefers-reduced-motion: reduce)');
    const GLIDE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
    const pace = (o) => (still.matches ? { ...o, duration: 0, delay: 0 } : o);
    const OPEN = { duration: 640, easing: GLIDE };
    const SHUT = { duration: 460, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' };
    const folds = new WeakMap();
    const fold = (d) => { let f = folds.get(d); if (!f) { f = { anim: null, lines: [] }; folds.set(d, f); } return f; };
    const bodyOf = (d) => d.querySelector(':scope > .gg-pdp__accordion-body');
    const headOf = (d) => d.querySelector(':scope > summary');
    const px = (name) => parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name)) || 0;
    const tall = (el) => el.getBoundingClientRect().height;

    /* the sheet a row belongs to, when its rows stand on the foot of their cell (desktop) — else null */
    const groupOf = (d) => d.closest('.gg-pdp__accordions');
    const sheetOf = (d) => { const g = groupOf(d); return g && getComputedStyle(g).getPropertyValue('--sheet').trim() === '1' ? g : null; };
    const rowsOf = (g) => [...g.querySelectorAll(':scope > .gg-pdp__accordion')];
    const closedHeight = (g) => rowsOf(g).reduce((n, r) => n + tall(headOf(r)), 0);
    const lock = (g) => {
      if (!g.classList.contains('is-raised')) {
        const quiet = !rowsOf(g).some((r) => r.open);
        g.style.height = (quiet ? tall(g) : closedHeight(g)) + 'px';
        g.classList.add('is-raised');
      }
      g.closest('.gg-pdp__info')?.classList.add('is-reading');
    };
    const release = (g) => { g.style.height = ''; g.classList.remove('is-raised'); g.closest('.gg-pdp__info')?.classList.remove('is-reading'); };
    const reading = (g) => rowsOf(g).some((r) => r.open && !r.classList.contains('is-closing'));

    /* what rises into place: a table's rows, a list's items, else the paragraphs — else the body itself */
    const linesOf = (body) => {
      const rows = [...body.querySelectorAll('.gg-spec__row:not([hidden])')];
      if (rows.length) return rows;
      const out = [];
      [...body.children].forEach((k) => {
        if (k.hidden) return;
        if (k.matches('ul, ol')) out.push(...k.children); else out.push(k);
      });
      return out.length ? out : [body];
    };

    /* drop a running movement, and say how tall the row stands at this instant. The row is PINNED at that height until
       it rests: the page must never be laid out with the row at its full height before the movement has begun — on a
       phone, where the rows are in the page's flow, the browser's scroll anchoring answers that jump by scrolling the
       page away from the row that was just tapped. */
    const stop = (d) => {
      const f = fold(d);
      const h = tall(d);
      d.style.height = h + 'px';
      if (f.anim) { f.anim.onfinish = null; f.anim.cancel(); f.anim = null; }
      f.lines.forEach((a) => a.cancel());
      f.lines = [];
      return h;
    };
    const rest = (d) => { fold(d).anim = null; d.style.height = ''; d.classList.remove('is-folding', 'is-closing'); };

    const open = (d) => {
      const g = sheetOf(d);
      if (g) lock(g);
      const from = stop(d);
      d.classList.remove('is-closing');
      d.classList.add('is-folding');
      d.open = true;
      const body = bodyOf(d);
      if (body) {
        /* a sheet never outgrows its cell: a body taller than the room left above the rows scrolls inside itself */
        body.style.maxHeight = ''; body.style.overflowY = '';
        const cell = g && g.closest('.gg-pdp__info');
        if (cell) {
          const room = cell.clientHeight - closedHeight(g);
          if (room > 120 && body.scrollHeight > room) { body.style.maxHeight = room + 'px'; body.style.overflowY = 'auto'; }
        }
      }
      /* where it is going: its title and its body, measured inside the pinned row */
      const to = tall(headOf(d)) + (body ? tall(body) : 0);
      const f = fold(d);
      f.anim = d.animate({ height: [from + 'px', to + 'px'] }, pace(OPEN));
      f.anim.onfinish = () => rest(d);
      if (body && !still.matches) {
        f.lines = linesOf(body).map((el, i) => el.animate(
          [{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }],
          { duration: 620, delay: 140 + Math.min(i, 12) * 55, easing: GLIDE, fill: 'backwards' }
        ));
      }
      return to;
    };

    const shut = (d) => {
      const from = stop(d);
      const to = tall(headOf(d));
      d.classList.add('is-folding', 'is-closing');
      const g = sheetOf(d);
      if (g && !reading(g)) g.closest('.gg-pdp__info')?.classList.remove('is-reading');
      const f = fold(d);
      const body = bodyOf(d);
      if (body && !still.matches) f.lines = [body.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 240, easing: 'ease', fill: 'forwards' })];
      f.anim = d.animate({ height: [from + 'px', to + 'px'] }, pace(SHUT));
      f.anim.onfinish = () => {
        d.open = false;
        f.lines.forEach((a) => a.cancel());
        f.lines = [];
        rest(d);
        const group = groupOf(d);
        if (group && group.classList.contains('is-raised') && !rowsOf(group).some((r) => r.open)) release(group);
      };
      return from - to;
    };
    const shutAll = (scope) => scope.querySelectorAll('.gg-pdp__accordion[open]:not(.is-closing)').forEach((o) => shut(o));

    /* keep what opens in the window. A sheet rises from the foot of its cell: if its head would go under the header,
       the page goes up to meet it. In the page's flow a drawer opens downward: if its foot would hang below the window
       the page comes down to it — never so far that its own title goes under the header. `lift` is what the rows
       above give back as they close. */
    const bring = (d, to, lift) => {
      const head = px('--header-height') + 16;
      const room = window.innerHeight - px('--gg-pbar');
      const g = sheetOf(d);
      let top; let bottom;
      if (g) {
        bottom = g.getBoundingClientRect().bottom;
        top = bottom - (closedHeight(g) + to - tall(headOf(d)));
      } else {
        top = d.getBoundingClientRect().top - lift;
        bottom = top + to + 28;
      }
      let by = 0;
      if (bottom > room) by = Math.min(bottom - room, top - head);
      else if (top < head) by = top - head;
      if (Math.abs(by) > 6) window.scrollBy({ top: by, behavior: still.matches ? 'auto' : 'smooth' });
    };

    document.addEventListener('click', (e) => {
      const veil = e.target.closest?.('[data-fold-veil]');
      if (veil) { shutAll(veil.closest('.gg-pdp__info') || document); return; }
      const s = e.target.closest?.('.gg-pdp__accordion > summary');
      if (!s || e.defaultPrevented) return;
      /* the Composer edits a title in place: a click into it is not a toggle, and stays the browser's business */
      if (e.target.closest('[contenteditable="true"]')) return;
      const d = s.parentElement;
      if (typeof d.animate !== 'function') return;
      e.preventDefault();
      if (d.open && !d.classList.contains('is-closing')) { shut(d); return; }
      let lift = 0;
      const group = groupOf(d);
      if (group) {
        rowsOf(group).forEach((o) => {
          if (o === d || !o.open || o.classList.contains('is-closing')) return;
          const gives = shut(o);
          if (o.compareDocumentPosition(d) & Node.DOCUMENT_POSITION_FOLLOWING) lift += gives;
        });
      }
      bring(d, open(d), lift);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const cell = document.querySelector('.gg-pdp__info.is-reading');
      if (cell) shutAll(cell);
    });

    /* a window that changes under an open sheet: its rows' closed height changes with it — or it stops being a sheet */
    window.addEventListener('resize', () => {
      document.querySelectorAll('.gg-pdp__accordions.is-raised').forEach((g) => {
        if (getComputedStyle(g).getPropertyValue('--sheet').trim() === '1') g.style.height = closedHeight(g) + 'px';
        else release(g);
      });
    });
  })();

  /* ----------------------------------------------------------
     THE HOUSE — two components.

     <gg-house-areas>: the Atelier / Laboratorium switch. Tabs
     (role=tab, arrow keys) over panels; a switch fades the leaving
     panel down a breath and lets the arriving one rise into place,
     the lit rule of the switch glides to the chosen tab. Films need
     nothing: a hidden panel is out of view, so its film pauses by the
     one rule; the arriving panel's film plays by its own mode.
     #atelier / #laboratorium in the URL open that area.

     folds (snippets/fold): words folded until asked for — the height
     glides, the lines rise; one open at a time in a group.

     <gg-house-careers>: the roles' ledger; a role's advert opens in the
     sheet, a panel from the right (the bag drawer's grammar), so the
     band's height never moves; #role-<handle> opens one from a link.
     ---------------------------------------------------------- */
  (() => {
    const still = window.matchMedia('(prefers-reduced-motion: reduce)');
    const GLIDE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
    const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

    class GGHouseAreas extends HTMLElement {
      connectedCallback() {
        this.tabs = [...this.querySelectorAll('[data-area-tab]')];
        this.panels = [...this.querySelectorAll('[data-area-panel]')];
        this.glide = this.querySelector('.gg-switch__glide');
        if (!this.tabs.length) return;
        this.tabs.forEach((t) => t.addEventListener('click', () => this.show(t.dataset.areaTab, true)));
        this.addEventListener('keydown', (e) => {
          const i = this.tabs.indexOf(document.activeElement); if (i < 0) return;
          const n = this.tabs.length; let k = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') k = (i + 1) % n;
          if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') k = (i - 1 + n) % n;
          if (e.key === 'Home') k = 0; if (e.key === 'End') k = n - 1;
          if (k === null) return; e.preventDefault(); this.tabs[k].focus(); this.show(this.tabs[k].dataset.areaTab, true);
        });
        const fromHash = () => { const id = slug(location.hash.slice(1)); if (id && this.tabs.some((t) => t.dataset.areaTab === id)) this.show(id, false); };
        window.addEventListener('hashchange', fromHash);
        window.addEventListener('resize', () => this.seat());
        fromHash();
        requestAnimationFrame(() => this.seat());
        // the glide seats itself once the fonts have settled the tabs' widths
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.seat());
      }
      current() { return this.tabs.find((t) => t.classList.contains('is-active')) || this.tabs[0]; }
      // the lit rule under the chosen tab
      seat() {
        const t = this.current(); if (!this.glide || !t) return;
        const box = this.glide.parentElement.getBoundingClientRect(); const r = t.getBoundingClientRect();
        this.glide.style.setProperty('--x', Math.round(r.left - box.left) + 'px'); this.glide.style.setProperty('--w', Math.round(r.width) + 'px');
        this.glide.classList.add('is-seated');
      }
      show(id, byUser) {
        const t = this.tabs.find((x) => x.dataset.areaTab === id); if (!t || t.classList.contains('is-active')) return;
        const from = this.panels.find((p) => !p.hidden); const to = this.panels.find((p) => p.dataset.areaPanel === id); if (!to) return;
        this.tabs.forEach((x) => { const on = x === t; x.classList.toggle('is-active', on); x.setAttribute('aria-selected', String(on)); x.tabIndex = on ? 0 : -1; });
        this.seat();
        const move = !still.matches && typeof to.animate === 'function';
        const swap = () => {
          if (from) { from.hidden = true; from.classList.remove('is-active'); }
          to.hidden = false; to.classList.add('is-active');
          to.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
          if (move) {
            to.animate([{ opacity: 0, transform: 'translateY(14px)' }, { opacity: 1, transform: 'none' }], { duration: 640, easing: GLIDE });
            const frame = to.querySelector('.srlm-media'); const words = to.querySelector('.gg-hareas__label');
            if (frame) frame.animate([{ clipPath: 'inset(0 0 6% 0)' }, { clipPath: 'inset(0)' }], { duration: 900, easing: GLIDE });
            if (words) [...words.children].forEach((el, i) => el.animate([{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }], { duration: 620, delay: 120 + i * 70, easing: GLIDE, fill: 'backwards' }));
          }
          this.dispatchEvent(new CustomEvent('gg-area-change', { bubbles: true, detail: { id } }));
        };
        if (move && from) { const a = from.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(-10px)' }], { duration: 280, easing: 'ease' }); a.onfinish = swap; }
        else swap();
        if (byUser && history.replaceState) history.replaceState(null, '', '#' + id);
      }
    }
    customElements.define('gg-house-areas', GGHouseAreas);

    /* folds — words that stand folded until asked for (snippets/fold + fold-toggle). A [data-fold-toggle] opens the
       [data-fold] its aria-controls names: the height glides (grid rows 0fr → 1fr, in the CSS) and the lines inside rise
       in turn; closed, the fold is inert. Inside a [data-fold-group] one fold stands open at a time. Escape closes the
       fold the focus is in; #<fold-id> in the URL opens that fold (a role's advert from a link, say). */
    const folds = {
      togglesOf(fold) { return [...document.querySelectorAll('[data-fold-toggle][aria-controls="' + fold.id + '"]')]; },
      set(fold, open) {
        if (!fold || fold.classList.contains('is-open') === open) return;
        if (open) { const g = fold.closest('[data-fold-group]'); if (g) g.querySelectorAll('[data-fold].is-open').forEach((f) => { if (f !== fold) folds.set(f, false); }); }
        fold.classList.toggle('is-open', open); fold.setAttribute('aria-hidden', String(!open)); try { fold.inert = !open; } catch (e) { /* older engines */ }
        folds.togglesOf(fold).forEach((t) => t.setAttribute('aria-expanded', String(open)));
        if (open && !still.matches) { const body = fold.querySelector('.gg-fold__body'); if (body) [...body.children].forEach((el, i) => el.animate([{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }], { duration: 620, delay: 160 + Math.min(i, 8) * 70, easing: GLIDE, fill: 'backwards' })); }
        fold.dispatchEvent(new CustomEvent('gg-fold', { bubbles: true, detail: { open } }));
      }
    };
    document.querySelectorAll('[data-fold]:not(.is-open)').forEach((f) => { try { f.inert = true; } catch (e) { /* older engines */ } });
    document.addEventListener('click', (e) => {
      const t = e.target.closest('[data-fold-toggle]'); if (!t) return;
      const f = document.getElementById(t.getAttribute('aria-controls') || ''); if (!f) return;
      e.preventDefault(); folds.set(f, !f.classList.contains('is-open'));
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const f = document.activeElement && document.activeElement.closest('[data-fold].is-open'); if (!f) return;
      folds.set(f, false); const t = folds.togglesOf(f)[0]; if (t) t.focus({ preventScroll: true });
    });
    // #<fold-id> opens the fold; the browser lands its top edge at the window's top, so the row above it (its toggle) is
    // brought back under the site's bars
    const foldFromHash = () => {
      const id = location.hash.slice(1); const f = id && document.getElementById(id); if (!f || !f.matches('[data-fold]')) return;
      folds.set(f, true);
      const t = folds.togglesOf(f)[0]; if (!t) return;
      requestAnimationFrame(() => { const head = (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 72) + 72; const r = t.getBoundingClientRect(); if (r.top < head) window.scrollBy(0, r.top - head); });
    };
    window.addEventListener('hashchange', foldFromHash); foldFromHash();
    window.ggFolds = folds;

    /* <gg-house-careers>: the roles' ledger and THE SHEET — a press on a role opens the advert in a panel from the
       right, in the bag drawer's grammar (a veil, the panel sliding in, the words rising in turn), so the band's height
       never moves and the frame keeps playing behind; prev / next (and ← →) walk the roles; × or Escape close;
       #role-<handle> opens one from a link. Without the script the adverts stand under the ledger. */
    class GGHouseCareers extends HTMLElement {
      connectedCallback() {
        this.sheet = this.querySelector('[data-role-sheet]');
        this.adverts = [...this.querySelectorAll('[data-role-advert]')];
        if (!this.sheet || !this.adverts.length) return;
        this.scroller = this.sheet.querySelector('[data-role-scroll]') || this.sheet;
        this.classList.add('is-ready');
        this.adverts.forEach((a) => { a.hidden = true; });
        this.addEventListener('click', (e) => {
          const open = e.target.closest('[data-role-open]'); if (open) { e.preventDefault(); this.open(open.dataset.roleOpen, true); return; }
          if (e.target.closest('[data-role-close]') || e.target.matches('[data-role-veil]')) { this.close(true); return; }
          const step = e.target.closest('[data-role-step]'); if (step) { this.step(Number(step.dataset.roleStep) || 1); }
        });
        document.addEventListener('keydown', (e) => {
          if (!this.sheet.classList.contains('is-open')) return;
          if (e.key === 'Escape') { e.preventDefault(); this.close(true); }
          else if (e.key === 'ArrowRight') this.step(1);
          else if (e.key === 'ArrowLeft') this.step(-1);
          else if (e.key === 'Tab') { // the focus stays in the sheet
            const f = [...this.sheet.querySelectorAll('a[href], button, [tabindex]:not([tabindex="-1"])')].filter((el) => el.offsetParent);
            if (!f.length) return; const first = f[0], last = f[f.length - 1];
            if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
          }
        });
        const fromHash = () => { const m = /^#role-(.+)$/.exec(location.hash); if (m && this.adverts.some((a) => a.dataset.roleAdvert === m[1])) this.open(m[1], false); };
        window.addEventListener('hashchange', fromHash); fromHash();
      }
      // the advert shown, the count, the neighbours named at the foot, the rows' state
      show(id) {
        const i = this.adverts.findIndex((a) => a.dataset.roleAdvert === id); if (i < 0) return -1;
        const n = this.adverts.length;
        this.adverts.forEach((a, k) => { a.hidden = k !== i; });
        this.querySelectorAll('[data-role-index]').forEach((el) => { el.textContent = String(i + 1).padStart(2, '0'); });
        this.querySelectorAll('[data-role-total]').forEach((el) => { el.textContent = String(n).padStart(2, '0'); });
        const prev = this.querySelector('[data-role-prev-name]'), next = this.querySelector('[data-role-next-name]');
        if (prev) prev.textContent = this.adverts[(i - 1 + n) % n].dataset.roleTitle || '';
        if (next) next.textContent = this.adverts[(i + 1) % n].dataset.roleTitle || '';
        this.querySelectorAll('[data-role-open]').forEach((b) => b.setAttribute('aria-expanded', String(b.dataset.roleOpen === id)));
        return i;
      }
      open(id, byUser) {
        const i = this.show(id); if (i < 0) return;
        const was = this.sheet.classList.contains('is-open');
        if (!was) { this.lastFocus = document.activeElement; this.sheet.classList.add('is-open'); document.body.classList.add('gg-drawer-open'); }
        this.scroller.scrollTop = 0;
        const words = this.adverts[i].querySelector('[data-role-words]');
        if (words && !still.matches) [...words.children].forEach((el, k) => el.animate([{ opacity: 0, transform: 'translateY(12px)' }, { opacity: 1, transform: 'none' }], { duration: 620, delay: (was ? 40 : 220) + Math.min(k, 8) * 70, easing: GLIDE, fill: 'backwards' }));
        const close = this.sheet.querySelector('[data-role-close]'); if (close && !was) setTimeout(() => close.focus({ preventScroll: true }), 60);
        if (byUser && history.replaceState) history.replaceState(null, '', '#role-' + id);
      }
      step(d) {
        const cur = this.adverts.findIndex((a) => !a.hidden); const n = this.adverts.length;
        this.open(this.adverts[((cur < 0 ? 0 : cur) + d + n) % n].dataset.roleAdvert, true);
      }
      close(byUser) {
        if (!this.sheet.classList.contains('is-open')) return;
        this.sheet.classList.remove('is-open'); document.body.classList.remove('gg-drawer-open');
        this.querySelectorAll('[data-role-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
        const back = this.lastFocus && this.lastFocus.isConnected ? this.lastFocus : this.querySelector('[data-role-open]'); if (back) back.focus({ preventScroll: true });
        if (byUser && history.replaceState && /^#role-/.test(location.hash)) history.replaceState(null, '', '#careers');
      }
    }
    customElements.define('gg-house-careers', GGHouseCareers);

    /* the page's index: every band's numeral and name ([data-house-band] / [data-house-name] on the sections), the one
       in view lit; a press glides the page to the band */
    class GGHouseIndex extends HTMLElement {
      connectedCallback() {
        if (this.built) return;
        const bands = [...document.querySelectorAll('[data-house-band]')].filter((b) => b.id && b.dataset.houseName);
        if (bands.length < 2) return;
        this.built = true;
        // a bar of its own under the cover, and sticky under the header for the rest of the page — so it leaves its
        // section (a sticky bar holds only within its own box) and stands among the sections, right after the cover's
        const wrap = this.closest('.shopify-section') || this.closest('section');
        if (wrap && wrap.parentElement) wrap.parentElement.insertBefore(this, wrap.nextSibling);
        // the way back, first, on a page that belongs to the House (the artist's): it stands outside the scrolling row
        const back = this.dataset.backHref && this.dataset.backLabel ? `<a class="gg-hindex__back" href="${this.dataset.backHref}"><span aria-hidden="true">←</span> ${this.dataset.backLabel}</a>` : '';
        this.innerHTML = back + '<span class="gg-hindex__items">' + bands.map((b, i) => `<a class="gg-hindex__item" href="#${b.id}" data-house-to="${b.id}"><span class="gg-hindex__n">${b.dataset.houseBand}</span><span class="gg-hindex__name">${b.dataset.houseName}</span></a>${i < bands.length - 1 ? '<span class="gg-hindex__sep" aria-hidden="true"></span>' : ''}`).join('') + '</span><span class="gg-hindex__progress" aria-hidden="true"></span>';
        this.hidden = false;
        const bar = this.querySelector('.gg-hindex__progress'); const last = bands[bands.length - 1];
        // how far through the page's bands the reading stands, as a hairline along the bar's foot
        const gauge = () => { const start = this.getBoundingClientRect().bottom; const end = last.getBoundingClientRect().bottom; const vh = window.innerHeight; const p = end <= start ? 1 : Math.min(1, Math.max(0, (vh - start) / (end - start))); bar.style.transform = 'scaleX(' + p.toFixed(4) + ')'; };
        const items = new Map(bands.map((b) => [b, this.querySelector(`[data-house-to="${b.id}"]`)]));
        // the row stands centred and fits the bar where there is room; where there is not (a narrow window) it is a
        // carousel — it scrolls sideways, fades at both edges say there is more, and the lit band is carried into view
        const row = this.querySelector('.gg-hindex__items');
        const edges = () => { this.classList.toggle('is-start', row.scrollLeft <= 1); this.classList.toggle('is-end', row.scrollLeft + row.clientWidth >= row.scrollWidth - 1); };
        const fit = () => { this.classList.toggle('is-overflow', row.scrollWidth > row.clientWidth + 1); edges(); };
        row.addEventListener('scroll', edges, { passive: true });
        window.addEventListener('resize', fit);
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
        fit();
        const carry = (a) => { if (!this.classList.contains('is-overflow')) return; row.scrollTo({ left: a.offsetLeft - (row.clientWidth - a.offsetWidth) / 2, behavior: still.matches ? 'auto' : 'smooth' }); };
        const light = (band) => items.forEach((a, b) => { const on = b === band; if (on && !a.classList.contains('is-current')) carry(a); a.classList.toggle('is-current', on); if (on) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current'); });
        // the band whose top has passed the middle of the window, last of those
        const pick = () => { const mid = window.innerHeight / 2; let cur = bands[0]; bands.forEach((b) => { if (b.getBoundingClientRect().top <= mid) cur = b; }); light(cur); };
        let t = 0; window.addEventListener('scroll', () => { if (t) return; t = requestAnimationFrame(() => { t = 0; pick(); gauge(); }); }, { passive: true });
        window.addEventListener('resize', gauge);
        pick(); gauge();
        this.addEventListener('click', (e) => { const a = e.target.closest('[data-house-to]'); if (!a) return; const band = document.getElementById(a.dataset.houseTo); if (!band) return; e.preventDefault(); const y = band.getBoundingClientRect().top + window.scrollY - (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 72) - this.offsetHeight + 1; window.scrollTo({ top: y, behavior: still.matches ? 'auto' : 'smooth' }); if (history.replaceState) history.replaceState(null, '', '#' + band.id); });
      }
    }
    customElements.define('gg-house-index', GGHouseIndex);

  })();

  /* ----------------------------------------------------------
     Cart — AJAX drawer over the store's real cart.
     Any form[data-gg-atb] posts to /cart/add.js with the
     Section Rendering API, swaps the drawer, updates Bag (N)
     and opens the drawer. Falls back to normal form post
     without JS.
     ---------------------------------------------------------- */
  const drawer = () => document.getElementById('GGCartDrawer');

  function updateCartUI(sectionsHtml) {
    const host = drawer();
    if (!host || !sectionsHtml) return;
    const parsed = new DOMParser().parseFromString(sectionsHtml, 'text/html');
    const fresh = parsed.querySelector('[data-drawer-inner]');
    const current = host.querySelector('[data-drawer-inner]');
    if (fresh && current) current.replaceWith(fresh);
    const count = host.querySelector('[data-drawer-inner]')?.dataset.itemCount ?? '0';
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
    });
  }

  function openDrawer() {
    const host = drawer();
    if (!host) return;
    host.classList.add('is-open');
    document.body.classList.add('gg-drawer-open');
    host.querySelector('[data-drawer-close]')?.focus();
  }

  function closeDrawer() {
    const host = drawer();
    if (!host) return;
    host.classList.remove('is-open');
    document.body.classList.remove('gg-drawer-open');
  }

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-drawer-close]') || e.target.matches('[data-drawer-overlay]')) {
      closeDrawer();
    }
    const toggle = e.target.closest('[data-drawer-toggle]');
    if (toggle) {
      e.preventDefault();
      openDrawer();
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeDrawer();
  });

  async function requestCart(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: 'cart-drawer' }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Cart error');
    updateCartUI(data.sections?.['cart-drawer']);
    return data;
  }

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-gg-atb]');
    if (!form) return;
    e.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const label = button?.querySelector('[data-atb-label]') || button;
    const original = label?.textContent;
    if (button) button.disabled = true;
    if (label && window.ggStrings) label.textContent = window.ggStrings.adding;
    try {
      const fd = new FormData(form);
      await requestCart(window.ggRoutes.cart_add_url + '.js', {
        id: fd.get('id'),
        quantity: Number(fd.get('quantity') || 1),
      });
      openDrawer();
    } catch (err) {
      console.error('[gallery-gothic] add to bag failed:', err);
      form.submit(); // fall back to the native cart page
      return;
    } finally {
      if (button) button.disabled = false;
      if (label && original) label.textContent = original;
    }
  });

  document.addEventListener('click', async (e) => {
    const change = e.target.closest('[data-cart-change]');
    if (!change) return;
    e.preventDefault();
    const line = Number(change.dataset.line);
    const quantity = Number(change.dataset.quantity);
    change.disabled = true;
    try {
      await requestCart(window.ggRoutes.cart_change_url + '.js', { line, quantity });
    } catch (err) {
      console.error('[gallery-gothic] cart change failed:', err);
      window.location.href = window.ggRoutes.cart_url;
    }
  });

  /* ----------------------------------------------------------
     <gg-variant-picker> — option chips drive the selected variant.
     Updates the form's variant id, all price / compare / ATB
     labels on the page (main button + mobile sticky bar), the
     URL's ?variant=, and jumps the gallery to the variant's
     featured media.
     ---------------------------------------------------------- */
  // how many of the option grid's columns a value takes: its word and a 12px breath, never fewer than one column nor
  // more than the row (tests/pdp-variant-grid.test.mjs)
  function optionSpan(wordWidth, cellWidth, cols, gap = 12) {
    if (!(cellWidth > 0)) return 1;
    return Math.min(cols, Math.max(1, Math.ceil((wordWidth + gap) / cellWidth - 0.001)));
  }

  class GGVariantPicker extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variant-data]');
      if (!json) return;
      try {
        this.variants = JSON.parse(json.textContent);
      } catch {
        return;
      }
      this.addEventListener('change', () => this.sync());
      this.mark();
      this.grid();
      if (typeof ResizeObserver === 'function') {
        let w = 0;
        new ResizeObserver(([e]) => { const x = Math.round(e.contentRect.width); if (x !== w) { w = x; this.grid(); } }).observe(this);
      }
      document.fonts?.ready?.then(() => this.grid());
      // a link that names its variant (?variant=) opens on that variant's picture
      const named = new URL(window.location.href).searchParams.get('variant');
      const current = this.currentVariant;
      if (named && current && String(current.id) === named) {
        customElements.whenDefined('gg-gallery').then(() => requestAnimationFrame(() => this.showVariant(current)));
      }
      // a variant whose picture the carousel does not hold is answered from the product's own record: read it now, so
      // the choice is instant
      if (this.variants.some((v) => (v.media_id || v.media_position) && !v.media_src && !this.galleryHolds(v))) {
        setTimeout(() => this.productPictures(), 600);
      }
    }

    /* EVERY OPTION ON ONE GRID: the values of every option stand on the same columns — as many as the longest option
       has values (a ring's nine sizes) — so a metal's words start where the sizes start and line up under them; a word
       wider than a column takes as many columns as it needs. Drawn by gallery-gothic.css ("THE OPTIONS, RULED"). */
    grid() {
      const sets = [...this.querySelectorAll('.gg-pdp__option')];
      if (!sets.length) return;
      const cols = Math.min(12, Math.max(2, ...sets.map((s) => s.querySelectorAll('.gg-pdp__chip').length)));
      this.style.setProperty('--cols', String(cols));
      sets.forEach((set) => {
        const row = set.querySelector('.gg-pdp__chips');
        const width = row?.clientWidth || 0;
        if (!width) return;
        const rowCols = matchMedia('(max-width: 749px)').matches ? Math.min(cols, Math.max(1, Math.floor(width / 48))) : cols;
        row.style.setProperty('--cols', String(rowCols));
        row.querySelectorAll('.gg-pdp__chip').forEach((chip) => {
          const word = chip.querySelector('span');
          chip.style.gridColumn = `span ${optionSpan(word ? word.scrollWidth : 0, width / rowCols, rowCols)}`;
        });
      });
    }

    get currentVariant() {
      const selected = this.selectedOptions;
      return this.variants.find((v) => v.options.every((opt, i) => opt === selected[i])) || null;
    }

    galleryHolds(v) {
      const g = document.querySelector('gg-gallery');
      return !!g?.querySelector(v.media_id ? `[data-gallery-slide][data-media-id="${v.media_id}"]` : `[data-gallery-slide][data-media-position="${v.media_position}"]`);
    }

    /* the product's own record (/products/<handle>.js), read once: each variant's picture by variant id */
    productPictures() {
      if (this.pictures) return this.pictures;
      const handle = this.dataset.productHandle || (window.location.pathname.match(/\/products\/([^/?#]+)/) || [])[1];
      if (!handle) return (this.pictures = Promise.resolve(new Map()));
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const sized = (src, w) => { const u = new URL(src, window.location.origin); u.searchParams.set('width', String(w)); return u.href; };
      this.pictures = fetch(`${root}products/${handle}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => new Map((p?.variants || []).filter((v) => v.featured_media?.preview_image?.src).map((v) => {
          const m = v.featured_media; const src = m.preview_image.src;
          return [String(v.id), { id: m.id, position: m.position, alt: m.alt || p.title || '', src: sized(src, 1080), srcset: `${sized(src, 540)} 540w, ${sized(src, 1080)} 1080w, ${sized(src, 1620)} 1620w` }];
        })))
        .catch(() => new Map());
      return this.pictures;
    }

    /* the chosen variant's own picture comes on stage, the carousel's bullets and number following; a variant with no
       picture of its own leaves the stage as it is */
    showVariant(variant) {
      const gallery = document.querySelector('gg-gallery');
      if (!variant || !gallery || typeof gallery.showMedia !== 'function') return;
      this.wanted = String(variant.id);
      if (!variant.media_id && !variant.media_position) return;
      const pic = variant.media_src ? { src: variant.media_src, srcset: variant.media_srcset, alt: variant.media_alt } : null;
      if (gallery.showMedia({ id: variant.media_id, position: variant.media_position, pic })) return;
      this.productPictures().then((map) => {
        const p = map.get(String(variant.id));
        if (p && this.wanted === String(variant.id)) gallery.showMedia({ id: p.id, position: p.position, pic: p });
      });
    }

    // each label says what is chosen, and a value no variant of the OTHER choices sells is struck through
    mark() {
      const selected = this.selectedOptions;
      this.querySelectorAll('fieldset[data-option-position]').forEach((set) => {
        const at = Number(set.dataset.optionPosition) - 1;
        const chosen = set.querySelector('input:checked');
        const readout = set.querySelector('[data-option-chosen]');
        if (readout && chosen) readout.textContent = chosen.value;
        set.querySelectorAll('input[type="radio"]').forEach((input) => {
          const sells = this.variants.some((v) => v.available && v.options[at] === input.value && v.options.every((o, i) => i === at || selected[i] === undefined || o === selected[i]));
          input.closest('.gg-pdp__chip')?.toggleAttribute('data-unavailable', !sells);
        });
      });
    }

    get selectedOptions() {
      const byPosition = new Map();
      this.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
        byPosition.set(Number(input.dataset.optionPosition), input.value);
      });
      return [...byPosition.entries()].sort((a, b) => a[0] - b[0]).map(([, v]) => v);
    }

    sync() {
      this.mark();
      const selected = this.selectedOptions;
      const variant =
        this.variants.find((v) => v.options.every((opt, i) => opt === selected[i])) || null;
      const form = document.getElementById(this.dataset.formId);
      if (!variant || !form) return;

      form.querySelector('[data-variant-id]').value = variant.id;

      document.querySelectorAll('[data-pdp-price]').forEach((el) => {
        el.textContent = variant.price;
      });
      document.querySelectorAll('[data-pdp-compare]').forEach((el) => {
        el.toggleAttribute('hidden', !variant.compare);
        if (variant.compare) el.textContent = variant.compare;
      });

      // LIMITED EDITION gives way to ONLY ONE AVAILABLE on a variant with one left (or a piece tagged onlyone)
      document.querySelectorAll('[data-pdp-only-one]').forEach((el) => {
        const one = el.dataset.pdpOnlyOne.includes(`,${variant.id},`);
        const label = el.querySelector('.srlm-tailoring__label');
        const tip = el.querySelector('[role="tooltip"]');
        if (label) label.textContent = one ? el.dataset.onlyLabel : el.dataset.editionLabel;
        if (tip) tip.textContent = one ? el.dataset.onlyTip : el.dataset.editionTip;
      });

      const label = variant.available ? window.ggStrings.addToBag : window.ggStrings.soldOut;
      document.querySelectorAll(`button[type="submit"][form="${this.dataset.formId}"], #${this.dataset.formId} button[type="submit"]`)
        .forEach((btn) => {
          btn.disabled = !variant.available;
          const span = btn.querySelector('[data-atb-label]');
          // the page's own button names the action only (the price stands under the title); the phone's sticky bar keeps the price, it appears when the title has scrolled away
          if (span) span.textContent = variant.available && span.hasAttribute('data-atb-price') ? `${label} · ${variant.price}` : label;
        });

      this.showVariant(variant);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }
  customElements.define('gg-variant-picker', GGVariantPicker);

  /* ----------------------------------------------------------
     <gg-recommendations> — fills "You may also like" from the
     product recommendations endpoint via the Section Rendering API.
     ---------------------------------------------------------- */
  class GGRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded) return;
      const url = this.dataset.url;
      if (!url) return;
      const load = async () => {
        this.dataset.loaded = '1';
        try {
          const response = await fetch(url);
          const text = await response.text();
          const fresh = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('gg-recommendations');
          if (fresh?.innerHTML.trim()) {
            this.innerHTML = fresh.innerHTML;
            observeReveals(this);
          }
        } catch (err) {
          console.error('[gallery-gothic] recommendations failed:', err);
        }
      };
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            observer.disconnect();
            load();
          }
        },
        { rootMargin: '400px' }
      );
      observer.observe(this);
      // safety net: hidden/prerendered documents never intersect
      setTimeout(() => {
        if (!this.dataset.loaded) {
          observer.disconnect();
          load();
        }
      }, 2500);
    }
  }
  customElements.define('gg-recommendations', GGRecommendations);

  /* ----------------------------------------------------------
     <details data-gg-dd> — toolbar dropdowns (sort, …).
     Close on outside click and Escape.
     ---------------------------------------------------------- */
  /* the shop rail leaves with a short fade; other menus close at once */
  const closeDD = (dd) => {
    if (!dd || !dd.open) return;
    if (dd.hasAttribute('data-gg-shopdd') && !dd.classList.contains('is-closing')) {
      dd.classList.add('is-closing');
      setTimeout(() => { dd.classList.remove('is-closing'); dd.removeAttribute('open'); }, 240);
    } else if (!dd.classList.contains('is-closing')) dd.removeAttribute('open');
  };
  document.addEventListener('click', (e) => {
    document.querySelectorAll('details[data-gg-dd][open]').forEach((dd) => {
      if (!dd.contains(e.target)) closeDD(dd);
    });
  });
  // a heading the Composer is editing inside a dropdown's key (the collection switcher): a click in it places the
  // caret, never opens the list
  document.addEventListener('click', (e) => {
    if (e.target.closest?.('summary [contenteditable="true"]')) e.preventDefault();
  }, true);
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('details[data-gg-dd][open]').forEach((dd) => {
      // the keyboard goes back to the key that opened it, never to the page's top
      if (dd.contains(document.activeElement)) dd.querySelector('summary')?.focus();
      closeDD(dd);
    });
  });

  /* ----------------------------------------------------------
     <details data-gg-shopdd> — the header Shop menu.
     Hover-intent open on fine pointers; click/Enter stays native
     details behavior. Outside-click + Escape close come from the
     [data-gg-dd] handlers above; here we add hover, aria-expanded
     (JS owns it — the React header would clobber a JSX-rendered
     value on re-render) and focus return on Escape.
     ---------------------------------------------------------- */
  (() => {
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
    let closeTimer = null;
    // hover intent: a pointer crossing the bar on its way elsewhere never flashes the menu open — it opens after a
    // short rest on Shop, and leaves after a longer one, so the pointer can wander across the doors
    let openTimer = null;

    document.addEventListener('pointerover', (e) => {
      if (!fine.matches) return;
      const dd = e.target.closest?.('details[data-gg-shopdd]');
      if (!dd) return;
      clearTimeout(closeTimer);
      if (dd.classList.contains('is-closing') || dd.open || openTimer) return;
      openTimer = setTimeout(() => { openTimer = null; if (!dd.open && !dd.classList.contains('is-closing')) dd.setAttribute('open', ''); }, 90);
    });

    document.addEventListener('pointerout', (e) => {
      if (!fine.matches) return;
      const dd = e.target.closest?.('details[data-gg-shopdd]');
      if (!dd || (e.relatedTarget && dd.contains(e.relatedTarget))) return;
      clearTimeout(openTimer); openTimer = null;
      if (!dd.open) return;
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => closeDD(dd), 260);
    });

    // toggle doesn't bubble — capture phase
    document.addEventListener('toggle', (e) => {
      const dd = e.target;
      if (!dd?.hasAttribute?.('data-gg-shopdd')) return;
      dd.querySelector('summary')?.setAttribute('aria-expanded', String(dd.open));
    }, true);

    // capture: refocus the summary before the bubbling close handler
    // above removes [open] (focus would otherwise fall to <body>)
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const dd = e.target.closest?.('details[data-gg-shopdd][open]');
      if (dd) dd.querySelector('summary')?.focus();
    }, true);
  })();

  /* ----------------------------------------------------------
     <gg-collection> — the collection page architecture.
     Facets, sort and pagination re-render through the Section
     Rendering API: every [data-cc-target] region from the fetched
     section replaces its counterpart in place, the URL is pushed,
     and back/forward re-render. Grid density persists per browser.
     Without JS the facet form GET-submits and links navigate.
     ---------------------------------------------------------- */
  class GGCollection extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-cc-form]');
      this.debounceTimer = null;
      this.abort = null;

      // grid density (persisted per browser)
      try {
        const cols = localStorage.getItem('gg:grid-cols');
        if (cols && this.querySelector('[data-cc-cols]')) this.setCols(cols, false);
      } catch {}

      this.addEventListener('click', (e) => {
        const colsBtn = e.target.closest('[data-cc-cols]');
        if (colsBtn) {
          this.setCols(colsBtn.dataset.ccCols, true);
          return;
        }
        const toggle = e.target.closest('[data-cc-filter-toggle]');
        if (toggle) {
          this.togglePanel();
          if (toggle.dataset.ccFocus === 'sort') {
            const sort = this.querySelector('.gg-refine__group--sort');
            sort?.scrollIntoView({ block: 'start', behavior: 'auto' });
          }
          return;
        }
        // another collection — a department, an art collection, Collections itself: the same page, seamlessly
        const go = e.target.closest('[data-cc-go]');
        if (go && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0) {
          e.preventDefault();
          this.closeList(go);
          this.go(go.href);
          return;
        }
        const chip = e.target.closest('[data-cc-chip]');
        if (chip) {
          e.preventDefault();
          this.closeList(chip);
          this.render(chip.href);
          return;
        }
        const page = e.target.closest('[data-cc-page]');
        if (page) {
          e.preventDefault();
          this.render(page.href, true, true);
        }
      });

      // facet + sort inputs (sort radios associate via form="")
      this.addEventListener('change', (e) => {
        const input = e.target;
        if (!this.form || input.form !== this.form) return;
        if (input.name === 'sort_by') {
          const label = this.querySelector('[data-cc-sort-label]');
          if (label) label.textContent = input.closest('label')?.querySelector('.gg-refine__label')?.textContent.trim() || input.closest('label')?.textContent.trim() || '';
          input.closest('details[data-gg-dd]')?.removeAttribute('open');
        }
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.render(this.buildUrl()), 300);
      });

      this.form?.addEventListener('submit', (e) => {
        e.preventDefault();
        clearTimeout(this.debounceTimer);
        this.render(this.buildUrl());
        this.togglePanel(false);
      });

      // Back / Forward: within this collection the grid re-renders; to another collection the page goes there as it
      // went — seamlessly, back to where it was scrolled
      this.onPop = (e) => {
        const here = new URL(window.location.href).pathname;
        const mine = new URL(this.dataset.url || window.location.pathname, window.location.origin).pathname;
        if (here !== mine) this.go(window.location.href, false, e.state && e.state.ggScroll);
        else this.render(window.location.href, false);
      };
      window.addEventListener('popstate', this.onPop);
      // a collection is fetched the moment the hand heads for it, so the change is already here when it lands
      this.onIntent = (e) => { const a = e.target.closest?.('[data-cc-go]'); if (a) GGCollection.prefetch(a.href); };
      this.addEventListener('pointerover', this.onIntent);
      this.addEventListener('focusin', this.onIntent);
      this.addEventListener('touchstart', this.onIntent, { passive: true });

      // the wall never ends on an empty box: after layout (and on every
      // resize / re-render) any hole the spanning tiles leave is closed
      // with a quiet monogram plate
      this.onResize = () => {
        clearTimeout(this.fillTimer);
        this.fillTimer = setTimeout(() => { this.fill(); this.markRow(); }, 120);
      };
      window.addEventListener('resize', this.onResize, { passive: true });
      this.onPromoLayout = (event) => { if (!event.detail?.tile || this.contains(event.detail.tile)) this.onResize(); };
      window.addEventListener('gg-promo-layout-change', this.onPromoLayout);
      requestAnimationFrame(() => this.fill());
      // a chip row wider than its room scrolls sideways: the edge with more beyond it fades, so a chip cut there
      // reads as more to come, never as a clipped name
      this.onRowScroll = () => this.markRow();
      this.addEventListener('scroll', this.onRowScroll, true);
      requestAnimationFrame(() => { this.keepLit(this.querySelector('[data-cc-target="categories"]')); this.markRow(); });

      this.onKey = (e) => {
        if (e.key === 'Escape') this.togglePanel(false);
      };
      document.addEventListener('keydown', this.onKey);
      const seed = new DOMParser().parseFromString(this.outerHTML, 'text/html');
      this.render(window.location.href, false, false, seed);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPop);
      window.removeEventListener('gg-promo-layout-change', this.onPromoLayout);
      this.removeEventListener('pointerover', this.onIntent);
      this.removeEventListener('focusin', this.onIntent);
      this.removeEventListener('touchstart', this.onIntent);
      window.removeEventListener('resize', this.onResize);
      this.removeEventListener('scroll', this.onRowScroll, true);
      document.removeEventListener('keydown', this.onKey);
    }

    // the line that scrolls: the chip row's chips
    static SCROLLERS = [['.gg-categories__links', '[aria-current]']];
    scrollsOf(row) {
      const at = {};
      for (const [sel] of GGCollection.SCROLLERS) { const sc = row?.querySelector(sel); if (sc) at[sel] = sc.scrollLeft; }
      return at;
    }
    // the chip in use stays on screen in whichever line scrolls, from where that line was — a line drawn again never
    // jumps back to its start, and a category chosen at the far end of its line is not left out of view
    keepLit(row, at = {}, smooth = false) {
      for (const [sel, lit] of GGCollection.SCROLLERS) {
        const sc = row?.querySelector(sel);
        if (!sc) continue;
        if (at[sel] != null) sc.scrollLeft = at[sel];
        const chip = sc.querySelector(lit);
        if (!chip) continue;
        const s = sc.getBoundingClientRect(), c = chip.getBoundingClientRect();
        if (c.left < s.left || c.right > s.right) sc.scrollTo({ left: sc.scrollLeft + c.left - s.left - 12, behavior: smooth ? 'smooth' : 'auto' });
      }
    }

    // a choice in the breadcrumb's switcher: its list closes at once and the keyboard stays on the switcher, which
    // every collection has
    closeList(link) {
      const list = link.closest('details[data-dept-switch][open]');
      if (!list) return;
      const key = list.querySelector('summary');
      if (list.contains(document.activeElement)) key?.focus({ preventScroll: true });
      list.removeAttribute('open');
    }

    markRow() {
      this.querySelectorAll('[data-cc-target="categories"] .gg-categories__links').forEach((el) => {
        el.toggleAttribute('data-more-left', el.scrollLeft > 2);
        el.toggleAttribute('data-more-right', el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
      });
    }

    fill() {
      this.querySelectorAll('[data-cc-grid] .gg-cgrid__filler').forEach((tile) => tile.remove());
    }

    buildUrl() {
      const params = new URLSearchParams();
      if (this.form) {
        for (const [key, value] of new FormData(this.form).entries()) {
          if (String(value).trim() !== '') params.append(key, value);
        }
        // a category row that gathers several of Shopify's (Tops: shirts, tees, blouses…) sends each of them
        this.form.querySelectorAll('input[data-cc-values]:checked').forEach((input) => {
          input.dataset.ccValues.split('|').forEach((value) => { if (value) params.append('filter.p.t.category', value); });
        });
      }
      const qs = params.toString();
      return (this.dataset.url || window.location.pathname) + (qs ? '?' + qs : '');
    }

    async cardPage(doc, url, signal) {
      const root = doc.querySelector('gg-collection');
      const grid = root?.querySelector('[data-cc-grid]');
      if (!grid || this.cardPagingFailed || document.documentElement.classList.contains('gg-lab-on') || new URL(url, location.origin).searchParams.has('composer')) return doc;
      const base = new URL(url, location.origin);
      const page = Math.max(1, Number(base.searchParams.get('page')) || 1);
      base.searchParams.delete('page');
      base.searchParams.delete('section_id');
      base.searchParams.sort();
      const section = root.dataset.sectionId;
      const key = `${section}:${base.href}`;
      const caches = GGCollection.cardPages || (GGCollection.cardPages = new Map());
      let cache = caches.get(key);
      if (!cache || Date.now() - cache.at > 120000) {
        cache = { at: Date.now(), chunks: new Map() };
        caches.set(key, cache);
        if (caches.size > 6) caches.delete(caches.keys().next().value);
      }
      const sourcePage = Number(root.dataset.cpPageNumber) || 1;
      if (!cache.chunks.has(sourcePage)) cache.chunks.set(sourcePage, doc.cloneNode(true));
      const size = Math.max(12, Math.ceil((Number(root.dataset.cpPerPage) || 24) / 12) * 12);
      const cards = [];
      let complete = false;
      let shell = doc;
      for (let source = 1; cards.length <= page * size && !complete; source++) {
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
        let chunk = cache.chunks.get(source);
        if (!chunk) {
          const endpoint = new URL(base);
          endpoint.searchParams.set('page', source);
          endpoint.searchParams.set('section_id', section);
          const response = await fetch(endpoint, { signal });
          if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
          chunk = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (!chunk.querySelector('gg-collection')) throw new Error('Missing collection response');
          if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
          cache.chunks.set(source, chunk);
        }
        const cells = [...chunk.querySelectorAll('[data-cc-grid] > .gg-cgrid__cell')];
        if (cells.some(cell => cell.classList.contains('gg-ctile'))) return doc;
        cards.push(...cells);
        complete = !chunk.querySelector('.gg-cpagination__next[data-cc-page]');
        if (source === 1) shell = chunk;
      }
      const result = doc.cloneNode(true);
      const resultRoot = result.querySelector('gg-collection');
      resultRoot.querySelector('[data-cc-target="results"]').replaceWith(shell.querySelector('[data-cc-target="results"]').cloneNode(true));
      const resultGrid = resultRoot.querySelector('[data-cc-grid]');
      if (!resultGrid) return doc;
      const lastPage = Math.max(1, Math.ceil(cards.length / size));
      const displayPage = complete ? Math.min(page, lastPage) : page;
      resultGrid.replaceChildren(...cards.slice((displayPage - 1) * size, displayPage * size).map(cell => cell.cloneNode(true)));
      resultRoot.dataset.cpPageNumber = displayPage;
      resultRoot.dataset.cpPerPage = size;
      const nav = resultRoot.querySelector('.gg-cpagination');
      if (nav) {
        nav.hidden = complete && lastPage === 1;
        for (const [direction, destination, enabled] of [['prev', displayPage - 1, displayPage > 1], ['next', displayPage + 1, cards.length > displayPage * size || !complete]]) {
          const old = nav.querySelector(`.gg-cpagination__${direction}`);
          const link = result.createElement(enabled ? 'a' : 'span');
          link.className = old.className.replace(/\bis-off\b/g, '').trim();
          link.innerHTML = old.innerHTML;
          if (enabled) {
            const target = new URL(base);
            if (destination > 1) target.searchParams.set('page', destination);
            link.href = target.pathname + target.search;
            link.setAttribute('data-cc-page', '');
          } else {
            link.classList.add('is-off');
            link.setAttribute('aria-hidden', 'true');
          }
          old.replaceWith(link);
        }
        const folio = nav.querySelector('.gg-cpagination__folio');
        folio.replaceChildren();
        const current = result.createElement('span');
        current.className = 'is-current';
        current.textContent = String(displayPage).padStart(2, '0');
        folio.append(current);
        // the total, always (asked 2026-10-09: "add / total so we can see how many pages we have to browse"): exact once
        // every page has been read; until then the rest of the wall is read in the background and the total written in
        if (complete) this.writeFolioTotal(folio, lastPage, result);
        else this.countCardPages(cache, base, section, size, cards.length).then(total => {
          if (!total || this.cardPagesKey !== key) return;
          // the page this belongs to may still be on its way into the document: written now and once more after it lands
          const write = () => { const live = this.querySelector('.gg-cpagination__folio'); if (this.cardPagesKey === key && live && !live.querySelector('.gg-cpagination__sep')) this.writeFolioTotal(live, total, document); };
          write(); setTimeout(write, 400);
        }).catch(() => {});
        this.cardPagesKey = key;
      }
      return result;
    }

    writeFolioTotal(folio, total, doc) {
      const separator = doc.createElement('span');
      separator.className = 'gg-cpagination__sep';
      separator.textContent = '/';
      folio.append(separator, String(total).padStart(2, '0'));
    }

    // how many card pages the whole wall makes: the source pages not yet read are fetched once into the same cache
    async countCardPages(cache, base, section, size, seen) {
      let cells = 0;
      for (let source = 1; source < 60; source++) {
        let chunk = cache.chunks.get(source);
        if (!chunk) {
          const endpoint = new URL(base);
          endpoint.searchParams.set('page', source);
          endpoint.searchParams.set('section_id', section);
          const response = await fetch(endpoint);
          if (!response.ok) return 0;
          chunk = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (!chunk.querySelector('gg-collection')) return 0;
          cache.chunks.set(source, chunk);
        }
        cells += chunk.querySelectorAll('[data-cc-grid] > .gg-cgrid__cell').length;
        if (!chunk.querySelector('.gg-cpagination__next[data-cc-page]')) return Math.max(1, Math.ceil(Math.max(cells, seen) / size));
      }
      return 0;
    }

    async render(url, push = true, scroll = false, seed = null) {
      this.abort?.abort();
      const abort = (this.abort = new AbortController());
      this.classList.add('is-loading');
      const fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', this.dataset.sectionId);
      try {
        let doc = seed;
        if (!doc) {
          const response = await fetch(fetchUrl, { signal: abort.signal });
          if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
          doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        }
        doc = await this.cardPage(doc, url, abort.signal);
        if (abort.signal.aborted) return;
        const freshRoot = doc.querySelector('gg-collection');
        this.dataset.cpPageNumber = freshRoot.dataset.cpPageNumber;
        this.dataset.cpPerPage = freshRoot.dataset.cpPerPage;
        const focusId = document.activeElement?.id;
        // the chip row re-forms as it does between collections (the lit chip's rule grows under it); every other
        // region is replaced
        let row = null;
        doc.querySelectorAll('[data-cc-target]').forEach((fresh) => {
          const current = this.querySelector(`[data-cc-target="${fresh.dataset.ccTarget}"]`);
          if (!current) return;
          if (fresh.dataset.ccTarget === 'categories') row = [current, fresh];
          else current.replaceWith(fresh);
        });
        if (row) await this.morphRow(row[0], document.adoptNode(row[1]), !reducedMotion.matches && document.visibilityState === 'visible');
        if (abort !== this.abort) return;
        try {
          const cols = localStorage.getItem('gg:grid-cols');
          if (cols && this.querySelector('[data-cc-cols]')) this.setCols(cols, false);
        } catch {}
        if (push) history.pushState({ ggCollection: true }, '', url);
        if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
        if (scroll) this.scrollToToolbar();
        this.classList.remove('is-loading');
        // the wall was drawn again in place: whatever armed its cards (the Composer's drag) arms the new ones
        this.dispatchEvent(new CustomEvent('gg-collection-render', { bubbles: true }));
        requestAnimationFrame(() => { this.fill(); this.markRow(); });
      } catch (err) {
        if (err.name === 'AbortError') return; // superseded by a newer request
        console.error('[gallery-gothic] collection render failed:', err);
        this.classList.remove('is-loading');
        this.cardPagingFailed = true;
        if (!seed) window.location.href = url; // fall back to full navigation
      }
    }

    /* ---- the departments, seamlessly (snippets/collection-categories.liquid): another collection's page is fetched
       whole (its sections differ by template, so the Section Rendering API cannot name them) and becomes this page:
       the chip row re-forms — the chips that stay slide to their new places, the ones that leave fade, the new
       department's categories come in beside it — the results cross-fade, the header, the facets and the sections
       below the grid are the new page's, the address and the title follow, and Back returns to where it was. The
       Lab and the Composer arm a page when it loads, so with either open this is an ordinary navigation. ---- */
    static prefetch(url) {
      const key = new URL(url, window.location.origin).href;
      const cache = GGCollection.pages || (GGCollection.pages = new Map());
      const hit = cache.get(key);
      if (hit && Date.now() - hit.at < 120000) return hit.page;
      const page = fetch(key, { credentials: 'same-origin', headers: { Accept: 'text/html' } }).then((r) => {
        if (!r.ok) throw new Error(`Collection request failed: ${r.status}`);
        return r.text();
      });
      cache.set(key, { at: Date.now(), page });
      page.catch(() => cache.delete(key));
      if (cache.size > 8) cache.delete(cache.keys().next().value);
      return page;
    }

    async go(url, push = true, restoreScroll = null) {
      const root = document.documentElement;
      if (root.classList.contains('gg-lab-on') || document.getElementById('srlm-composer')) { window.location.href = url; return; }
      this.abort?.abort();
      const abort = (this.abort = new AbortController());
      // a page nobody is looking at runs no animation (its `finished` never comes): it changes at once
      const motion = !reducedMotion.matches && document.visibilityState === 'visible';
      const results = this.querySelector('[data-cc-target="results"]');
      if (motion && results) this.leaving = results.animate([{ opacity: 1 }, { opacity: 0.18 }], { duration: 180, easing: 'ease-out', fill: 'forwards' });
      this.classList.add('is-loading');
      try {
        const html = await GGCollection.prefetch(url);
        if (abort.signal.aborted) return;
        let doc = new DOMParser().parseFromString(html, 'text/html');
        this.cardPagingFailed = false;
        doc = await this.cardPage(doc, url, abort.signal);
        if (abort.signal.aborted) return;
        const next = doc.querySelector('gg-collection');
        if (!next) throw new Error('the page has no collection');
        // where this page was, so Back can come back to it
        if (push) try { history.replaceState({ ...(history.state || {}), ggCollection: true, ggScroll: window.scrollY }, ''); } catch {}
        const toolbarWasAbove = (this.querySelector('[data-cc-toolbar]')?.getBoundingClientRect().top ?? 0) < 0;

        // 1 · the chip row re-forms; a key held by the keyboard is held again in the new row
        const oldRow = this.querySelector('[data-cc-target="categories"]');
        const newRow = next.querySelector('[data-cc-target="categories"]');
        const heldKey = oldRow?.contains(document.activeElement) ? document.activeElement.closest('[data-chip-key]')?.dataset.chipKey : null;
        const heldSwitch = !!document.activeElement?.closest?.('[data-dept-switch]');
        if (oldRow && newRow) await this.morphRow(oldRow, document.adoptNode(newRow), motion);
        if (abort.signal.aborted) return;
        if (heldKey) this.querySelector(`[data-cc-target="categories"] [data-chip-key="${CSS.escape(heldKey)}"]`)?.focus({ preventScroll: true });

        // 2 · the rest of the collection is the new page's: the breadcrumb row (the name, what is applied, the count),
        // Filter · Sort (they name the new page's sheet), the sheet, the words under the wall — gallery-gothic.css
        // "THE CONSOLE": each row is one height, so none of it moves the chip row
        const homes = { '.gg-ctoolbar__tools': '.gg-ctoolbar__row' };
        for (const sel of ['.gg-chead', '.gg-ctoolbar__tools', '[data-cc-facets-panel]', '[data-cc-target="about"]']) {
          const cur = this.querySelector(sel), fresh = next.querySelector(sel);
          if (cur && fresh) cur.replaceWith(document.adoptNode(fresh));
          else if (cur && !fresh) cur.remove();
          else if (!cur && fresh) (homes[sel] && this.querySelector(homes[sel]) || this).appendChild(document.adoptNode(fresh));
        }
        if (heldSwitch) this.querySelector('[data-dept-switch] > summary')?.focus({ preventScroll: true });
        const oldResults = this.querySelector('[data-cc-target="results"]'), newResults = next.querySelector('[data-cc-target="results"]');
        if (oldResults && newResults) {
          const adopted = document.adoptNode(newResults);
          oldResults.replaceWith(adopted);
          if (motion) adopted.animate([{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }], { duration: 340, easing: 'cubic-bezier(.2,.7,.2,1)' });
        }
        for (const a of next.getAttributeNames()) if (a.startsWith('data-')) this.setAttribute(a, next.getAttribute(a));
        for (const a of this.getAttributeNames()) if (a.startsWith('data-') && !next.hasAttribute(a)) this.removeAttribute(a);
        this.form = this.querySelector('[data-cc-form]');

        // 3 · the sections around it: the new template's, in its order
        this.swapSiblings(next);
        this.adoptScripts(doc);
        document.title = doc.title;
        const canon = doc.querySelector('link[rel="canonical"]'), myCanon = document.querySelector('link[rel="canonical"]');
        if (canon && myCanon) myCanon.href = canon.href;

        if (push) history.pushState({ ggCollection: true }, '', url);
        try { const cols = localStorage.getItem('gg:grid-cols'); if (cols && this.querySelector('[data-cc-cols]')) this.setCols(cols, false); } catch {}
        this.classList.remove('is-loading');
        if (restoreScroll != null) window.scrollTo({ top: restoreScroll, behavior: 'auto' });
        else if (toolbarWasAbove) this.scrollToToolbar();
        requestAnimationFrame(() => { this.fill(); this.markRow(); });
        setTimeout(() => this.markRow(), 420);
        this.dispatchEvent(new CustomEvent('gg-collection-changed', { bubbles: true, detail: { url } }));
      } catch (err) {
        if (err.name === 'AbortError' || abort.signal.aborted) return;
        console.error('[gallery-gothic] collection change failed:', err);
        window.location.href = url;
      } finally {
        this.leaving?.cancel(); this.leaving = null;
      }
    }

    // the chip row, re-formed in place: FLIP for the chips both rows share (by data-chip-key), a fade for the ones
    // leaving, and the new ones coming in from their department; the underline grows under the new choice
    async morphRow(oldRow, newRow, motion) {
      const keyOf = (el) => el.dataset.chipKey;
      const chipsOf = (row) => [...row.querySelectorAll('[data-chip-key]')];
      const before = new Map(chipsOf(oldRow).map((el) => [keyOf(el), el.getBoundingClientRect()]));
      const staying = new Set(chipsOf(newRow).map(keyOf));
      const lit = '.gg-categories__links [aria-current]';
      const wasCurrent = oldRow.querySelector(lit)?.dataset.chipKey;
      if (motion) {
        const leaving = chipsOf(oldRow).filter((el) => !staying.has(keyOf(el)));
        // never waited on for longer than the fade: a tab hidden mid-change pauses its animations, never the change
        if (leaving.length) await Promise.race([Promise.all(leaving.map((el) => el.animate([{ opacity: 1 }, { opacity: 0, transform: 'translateY(-3px)' }], { duration: 140, easing: 'ease-in', fill: 'forwards' }).finished.catch(() => {}))), new Promise((r) => setTimeout(r, 180))]);
      }
      // the line keeps where it was scrolled — only within the same collection
      const at = this.scrollsOf(oldRow);
      if (oldRow.querySelector('[data-dept]')?.dataset.dept !== newRow.querySelector('[data-dept]')?.dataset.dept) delete at['.gg-categories__links'];
      oldRow.replaceWith(newRow);
      this.keepLit(newRow, at, motion);
      if (!motion) return;
      let incoming = 0;
      for (const el of chipsOf(newRow)) {
        const was = before.get(keyOf(el));
        if (was) {
          const now = el.getBoundingClientRect(); const dx = was.left - now.left, dy = was.top - now.top;
          if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], { duration: 360, easing: 'cubic-bezier(.2,.7,.2,1)' });
        } else {
          el.animate([{ opacity: 0, transform: 'translateX(-12px)' }, { opacity: 1, transform: 'none' }], { duration: 300, delay: 90 + incoming * 36, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'backwards' });
          incoming++;
        }
      }
      const current = newRow.querySelector(lit);
      if (current && current.dataset.chipKey !== wasCurrent) {
        try { current.animate([{ transform: 'scaleX(0)', transformOrigin: 'left' }, { transform: 'scaleX(1)', transformOrigin: 'left' }], { duration: 360, delay: 120, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'backwards', pseudoElement: '::after' }); } catch {}
      }
    }

    // the template's other sections (the 3D piece, a rack, the atelier story): the new page's, in its order
    swapSiblings(next) {
      const mine = this.closest('.shopify-section'), theirs = next.closest('.shopify-section');
      if (!mine || !theirs || !mine.parentElement || !theirs.parentElement) return;
      const isTemplate = (el) => el.classList?.contains('shopify-section') && /^shopify-section-template--/.test(el.id);
      [...mine.parentElement.children].filter((el) => el !== mine && isTemplate(el)).forEach((el) => el.remove());
      let after = mine; let beforeMine = true;
      for (const el of [...theirs.parentElement.children]) {
        if (el === theirs) { beforeMine = false; continue; }
        if (!isTemplate(el)) continue;
        const adopted = document.adoptNode(el);
        if (beforeMine) mine.before(adopted); else { after.after(adopted); after = adopted; }
      }
      mine.id = theirs.id;
    }

    // a script the new page loads that this one never did (the 3D viewer when a 3D piece comes in)
    adoptScripts(doc) {
      const have = new Set([...document.scripts].map((sc) => sc.getAttribute('src')).filter(Boolean));
      doc.querySelectorAll('script[src]').forEach((sc) => {
        const src = sc.getAttribute('src');
        if (have.has(src)) return;
        const el = document.createElement('script');
        for (const a of sc.getAttributeNames()) el.setAttribute(a, sc.getAttribute(a));
        el.async = false;
        document.body.appendChild(el);
      });
    }

    scrollToToolbar() {
      const toolbar = this.querySelector('[data-cc-toolbar]');
      if (!toolbar) return;
      const headerH =
        parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 72;
      const results = this.querySelector('[data-cc-target="results"]');
      if (!results) return;
      const top = results.getBoundingClientRect().top + window.scrollY - headerH - toolbar.offsetHeight;
      window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
    }

    setCols(cols, persist) {
      const grid = this.querySelector('[data-cc-grid]');
      if (grid) grid.style.setProperty('--cc-cols', cols);
      this.querySelectorAll('[data-cc-cols]').forEach((b) => {
        b.setAttribute('aria-pressed', String(b.dataset.ccCols === String(cols)));
      });
      if (persist) {
        try {
          localStorage.setItem('gg:grid-cols', cols);
        } catch {}
      }
    }

    togglePanel(force) {
      const panel = this.querySelector('[data-cc-facets-panel]');
      if (!panel) return;
      const isOpen = panel.classList.contains('is-open');
      const open = force !== undefined ? force : !isOpen;
      if (open === isOpen) return;
      this.querySelectorAll('[data-cc-filter-toggle]').forEach((b) =>
        b.setAttribute('aria-expanded', String(open))
      );
      if (open) {
        clearTimeout(this.hideTimer);
        panel.removeAttribute('hidden');
        void panel.offsetHeight; // reflow so the transition runs
        panel.classList.add('is-open');
        document.body.classList.add('gg-facets-open');
      } else {
        panel.classList.remove('is-open');
        document.body.classList.remove('gg-facets-open');
        clearTimeout(this.hideTimer);
        this.hideTimer = setTimeout(() => panel.setAttribute('hidden', ''), 280);
      }
    }
  }
  customElements.define('gg-collection', GGCollection);
  // the Shop menu's doors (snippets/pillar-links): on a page showing the departments, a door is taken in place —
  // the menu closes and the collection changes as a department chip would change it
  document.addEventListener('click', (e) => {
    const a = e.target.closest?.('a[data-dept-link]');
    if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    const gc = document.querySelector('gg-collection');
    if (!gc || !gc.querySelector('.gg-deptrow') || typeof gc.go !== 'function') return;
    e.preventDefault();
    a.closest('details[open]')?.removeAttribute('open');
    document.querySelector('[data-menu-toggle][aria-expanded="true"]')?.click();
    gc.go(a.href);
  });
  document.addEventListener('pointerover', (e) => {
    const a = e.target.closest?.('a[data-dept-link]');
    if (a && document.querySelector('gg-collection .gg-deptrow')) GGCollection.prefetch(a.href);
  });

  /* ----------------------------------------------------------
     The advanced search (sections/main-search.liquid; docs/SEARCH.md). The whole catalogue is one live index
     (templates/search.index.liquid): a piece's facets are the tags the catalogue scan writes ("Metal: Sterling
     silver"), its card the same snippet as every wall's. The page answers where it stands — the words, then
     Department · Metal · Gemstone · Style · Colour, then a sort. Several values of one facet are either; values of
     different facets are all; every value is counted by what it would leave given the rest of the question, and a
     value that would leave nothing is dimmed, never hidden, so the lists never reshuffle under the hand. The
     address keeps the question; the index is read once a visit.
     ---------------------------------------------------------- */
  const AS_FACETS = [
    { key: 'dept', label: 'Department' },
    { key: 'metal', label: 'Material', tag: 'Metal' },
    { key: 'stone', label: 'Gemstone', tag: 'Stone' },
    { key: 'style', label: 'Style', tag: 'Style' },
    { key: 'colour', label: 'Colour', tag: 'Colour' },
  ];
  const AS_DEPTS = { jewelry: 'Jewelry', couture: 'Couture', 'collectors-items': 'Art & Collectibles', 'art-sculpture': 'Art & Collectibles' };
  const AS_SORTS = [['relevance', 'Relevance'], ['featured', 'Featured'], ['price-asc', 'Price, low to high'], ['price-desc', 'Price, high to low'], ['newest', 'Newest']];
  // what a colour looks like on its chip — a metal's colour as the metal, never a flat yellow
  const AS_SWATCH = {
    Black: '#0b0b0c', White: '#efece5', Grey: '#8b8b8b', Brown: '#6d4c34', Beige: '#d8c7a6', Red: '#a3212a', Pink: '#d98ca0',
    Orange: '#d27a2e', Yellow: '#d6b72e', Green: '#3f6b46', Blue: '#2f4f8f', Purple: '#6b3f8f',
    Silver: 'linear-gradient(135deg, #f4f3ef, #9d9b96 55%, #e3e2dd)', Gold: 'linear-gradient(135deg, #f5d57c, #b3822a 55%, #f0cc68)',
    Bronze: 'linear-gradient(135deg, #c98c5c, #7c5230 55%, #b77a4b)', Multicolour: 'conic-gradient(#a3212a, #d6b72e, #3f6b46, #2f4f8f, #6b3f8f, #a3212a)',
  };
  const AS_PAGE = 48;
  const AS_STOP = new Set(['a', 'an', 'the', 'of', 'and', 'in', 'with', 'for', 'on']);

  // one piece of the index, ready to be asked: its facets by key, its words lower-cased by weight
  function asProductFacets(x) {
    const tags = x.g || [];
    const options = x.o || [];
    const values = (pattern) => options.filter(o => pattern.test(o.name)).flatMap(o => o.values || []).map(v => typeof v === 'object' ? v.name || '' : v).join(' ');
    const text = [x.t, x.s, ...tags].join(' ').toLowerCase();
    const namedColour = [x.t, ...tags, values(/colou?r/i)].join(' ').toLowerCase();
    const materialText = [x.t, x.s, values(/material|metal|fabric/i)].join(' ').toLowerCase().replace(/(?:gold|silver|bronze|copper)[ -](?:tone[d]?|colou?red|finish)/g, '');
    const whole = (source, term) => new RegExp('(?:^|[^a-z])' + term + '(?:$|[^a-z])', 'i').test(source);
    const find = (source, entries) => entries.filter(([, words]) => words.some(word => whole(source, word))).map(([label]) => label);
    const jewelry = (x.d || []).includes('jewelry');
    const art = /art|print|painting/i.test(x.k || '');
    const metal = art ? [] : find(materialText, [
      ['Silver', ['silver']], ['Gold plated', ['gold plated', 'gold-plated', 'vermeil']],
      ['Gold', ['solid gold', '14k gold', '18k gold', '24k gold']], ['Bronze', ['bronze']], ['Brass', ['brass']],
      ['Steel', ['stainless steel']], ['Cotton', ['cotton']], ['Silk', ['silk']], ['Wool', ['wool']],
      ['Linen', ['linen']], ['Leather', ['leather']], ['Viscose', ['viscose']], ['Cashmere', ['cashmere']],
      ['Plaster', ['plaster']], ['Resin', ['resin']], ['Ceramic', ['ceramic']], ['Glass', ['glass']]
    ]);
    if (art && /paper|giclée|giclee|archival print/i.test(text)) metal.push('Paper');
    const stone = jewelry ? find(text, ['Pearl','Amethyst','Quartz','Opal','Onyx','Obsidian','Emerald','Diamond','Pyrite','Meteorite','Abalone','Amber','Garnet','Turquoise'].map(v => [v,[v.toLowerCase()]])) : [];
    const style = find(text, [
      ['Gothic',['gothic','blackletter','gargoyle']], ['Medieval',['medieval','templar','chainmail']],
      ['Renaissance & Baroque',['renaissance','baroque','rococo']], ['Antiquity',['ancient','roman','greek','egyptian']],
      ['Botanical',['floral','botanical','flower']], ['Celestial',['celestial','cosmic','lunar']],
      ['Oceanic',['oceanic','nautical','sea monster']], ['Heraldic',['heraldic','heraldry','signet']],
      ['Avant-garde',['avant-garde','avant garde','deconstructed']], ['Minimal',['minimalist','minimal']]
    ]).slice(0,3);
    const colour = find(namedColour, [
      ['Black',['black','noir']], ['Cream',['cream','ivory','ecru']], ['White',['white']], ['Silver',['silver']],
      ['Gold',['gold','golden']], ['Bronze',['bronze']], ['Grey',['grey','gray']], ['Brown',['brown']],
      ['Beige',['beige']], ['Red',['red']], ['Pink',['pink','ashpink']], ['Blue',['blue']],
      ['Green',['green']], ['Purple',['purple','lilac','lavender']], ['Yellow',['yellow']], ['Orange',['orange']]
    ]).slice(0,4);
    return {metal,stone,style,colour};
  }
  function asItem(x, i) {
    const f = { dept: (x.d || []).filter((d) => AS_DEPTS[d]), metal: [], stone: [], style: [], colour: [] };
    for (const t of x.f || []) {
      const at = String(t).indexOf(': ');
      const facet = AS_FACETS.find((a) => a.tag && (a.tag === String(t).slice(0, at) || (a.key === 'metal' && String(t).slice(0, at) === 'Material')));
      if (facet && !f[facet.key].includes(t.slice(at + 2))) f[facet.key].push(t.slice(at + 2));
    }
    const derived = asProductFacets(x);
    for (const key of ['metal', 'stone', 'style', 'colour']) if (!f[key].length) f[key] = derived[key];
    const lc = (v) => String(v || '').toLowerCase();
    return {
      i, h: x.h, t: x.t || '', p: x.p || 0, a: !!x.a, n: x.n || 0, nw: !!x.nw, f, collections: Array.isArray(x.cl) ? x.cl : [], card: x.c || '',
      w: { t: lc(x.t), k: lc(x.k), f: lc(['metal', 'stone', 'style', 'colour'].flatMap((k) => f[k]).join(' | ')), d: lc(f.dept.map((d) => AS_DEPTS[d]).join(' ')), g: lc((x.g || []).join(' | ')) },
    };
  }
  // how well a piece answers the words: every word must answer somewhere — its name most, then what it is and its
  // facets, its department, its other tags; a plural asks for its singular too
  function asScore(it, tokens) {
    let score = 0;
    for (const tok of tokens) {
      const forms = tok.length > 3 && /s$/.test(tok) ? [tok, tok.replace(/(ie)?s$/, (m, ie) => (ie ? 'y' : ''))] : [tok];
      let s = 0;
      for (const w of forms) {
        const matches = (text) => text.split(/[^\p{L}\p{N}]+/u).some(word => word.startsWith(w));
        if (matches(it.w.t)) s = Math.max(s, 6);
        if (matches(it.w.k) || matches(it.w.f)) s = Math.max(s, 3);
        if (matches(it.w.d)) s = Math.max(s, 2);
        if (matches(it.w.g)) s = Math.max(s, 1);
      }
      if (!s) return 0;
      score += s;
    }
    return score || 1;
  }
  const asTokens = (q) => String(q || '').toLowerCase().split(/[\s,]+/).map((t) => t.trim()).filter((t) => t && !AS_STOP.has(t));
  // JEWELRY'S NEW PIECES, EVERY OTHER PLACE (2026-10-04: "make all search results prioritize every other being a new
  // product for jewelry if category matches"). A search is about jewelry when Jewelry is the department chosen, when a
  // word names jewelry (ring, necklace, earring…), or when nearly all it finds is jewelry; then its answer alternates a
  // new jewelry piece (New Arrivals, or tagged "new") with the rest, each side keeping its own order. Only the default
  // orders do this — a shopper's own sort (price, newest) is kept as asked.
  const AS_JEWEL_WORDS = ['jewel', 'ring', 'necklace', 'chain', 'pendant', 'choker', 'collar', 'bracelet', 'cuff', 'bangle', 'earring', 'brooch', 'talisman', 'signet', 'charm', 'anklet'];
  const asIsNewJewel = (it) => !!(it && it.nw && it.f.dept.includes('jewelry'));
  function asJewelryAsked(tokens, dept, found) {
    if (dept && dept.size === 1 && dept.has('jewelry')) return true;
    if (tokens.some((t) => AS_JEWEL_WORDS.some((w) => t.startsWith(w)))) return true;
    return found.length >= 4 && found.filter((it) => it.f.dept.includes('jewelry')).length / found.length >= 0.8;
  }
  function asEveryOtherNew(list, isNew = asIsNewJewel) {
    const fresh = list.filter((x) => isNew(x)), rest = list.filter((x) => !isNew(x));
    if (!fresh.length || !rest.length) return list;
    const out = [];
    while (fresh.length || rest.length) { if (fresh.length) out.push(fresh.shift()); if (rest.length) out.push(rest.shift()); }
    return out;
  }

  // the index, once a visit (and for ten minutes of the tab): page 1 says how many pages there are; the rest come together.
  // { fresh: true } reads the live index past both keeps and the browser's cache — the Lab's picker asks for it each time
  // it opens, so a piece published a moment ago is in it — and what it reads becomes the kept copy for everyone else
  function loadSearchIndex(url, opts) {
    const fresh = !!(opts && opts.fresh);
    if (loadSearchIndex.memo && !fresh) return loadSearchIndex.memo;
    const key = 'gg:search-index:v3:' + url;
    const job = (async () => {
      if (!fresh) { try { const hit = JSON.parse(sessionStorage.getItem(key) || 'null'); if (hit && Date.now() - hit.at < 600000 && Array.isArray(hit.items)) return hit.items.map(asItem); } catch {} }
      const bust = fresh ? `&_=${Date.now()}` : '';
      const page = async (n) => { const r = await fetch(`${url}&page=${n}${bust}`, { credentials: 'same-origin', cache: fresh ? 'no-store' : 'default' }); if (!r.ok) throw new Error(`search index ${r.status}`); return r.json(); };
      const first = await page(1);
      const rest = await Promise.all(Array.from({ length: Math.max(0, (first.pages || 1) - 1) }, (_, i) => page(i + 2)));
      const raw = [first, ...rest].flatMap((pg) => pg.items || []);
      try { sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), items: raw })); } catch {}
      return raw.map(asItem);
    })();
    loadSearchIndex.memo = job;
    job.catch(() => { if (loadSearchIndex.memo === job) loadSearchIndex.memo = null; });
    return job;
  }
  window.ggSearchIndex = { load: loadSearchIndex, facets: AS_FACETS, depts: AS_DEPTS, swatch: AS_SWATCH, tokens: asTokens, score: asScore, jewelryAsked: asJewelryAsked, everyOtherNew: asEveryOtherNew, isNewJewel: asIsNewJewel };

  class GGAdvancedSearch extends HTMLElement {
    connectedCallback() {
      if (this.armed) return;
      this.armed = true;
      this.q = this.querySelector('[data-as-q]');
      this.keys = this.querySelector('[data-as-keys]');
      this.panel = this.querySelector('[data-as-panel]');
      this.results = this.querySelector('[data-as-results]');
      this.countEl = this.querySelector('[data-as-count]');
      this.active = this.querySelector('[data-as-active]');
      this.sortBtn = this.querySelector('[data-as-sort]');
      this.embedded = this.hasAttribute('data-as-embedded');
      this.zero = this.querySelector('[data-as-zero]');
      this.refinements = this.querySelector('[data-as-refinements]');
      this.refineButton = this.querySelector('[data-as-refine]');
      this.body = this.querySelector('[data-as-body]');
      this.summary = this.querySelector('[data-as-summary]');
      this.viewportHost = this.closest('gg-search') || this;
      this.viewportWidth = window.innerWidth;
      this.viewportBaseline = window.innerHeight;
      this.onViewport = () => {
        cancelAnimationFrame(this.viewportFrame);
        this.viewportFrame = requestAnimationFrame(() => this.updateViewport());
      };
      window.visualViewport?.addEventListener('resize', this.onViewport);
      window.visualViewport?.addEventListener('scroll', this.onViewport);
      window.addEventListener('resize', this.onViewport);
      window.addEventListener('scroll', this.onViewport, {passive:true});
      this.addEventListener('focusin', this.onViewport);
      this.addEventListener('focusout', this.onViewport);
      this.updateViewport();
      this.state = this.embedded ? {q:'', collection:'', sel:this.blank(), sort:''} : this.readUrl();
      this.open = this.embedded ? null : new URL(location.href).searchParams.get('refine');
      if (!AS_FACETS.some(f => f.key === this.open)) this.open = null;
      this.shown = AS_PAGE;
      this.browsing = !!(this.state.q || this.state.collection || Object.values(this.state.sel).some(set => set.size));
      this.refinements.hidden = !this.open;
      this.refineButton.setAttribute('aria-expanded', String(!!this.open));
      if (!this.embedded) this.load();

      this.q.form.addEventListener('submit', (e) => { if (!this.items) return; e.preventDefault(); this.browsing = true; this.ask({ q: this.q.value.trim() }, true); this.q.blur(); });
      this.q.addEventListener('input', () => { clearTimeout(this.typing); this.typing = setTimeout(() => this.ask({ q: this.q.value.trim() }), 180); });
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-as-refine]')) {
          this.q.blur();
          this.toggle(this.open && this.open !== 'sort' ? null : 'metal');
          return;
        }
        const term = e.target.closest('[data-as-term]');
        if (term) { this.toggleBadge(term); return; }
        const key = e.target.closest('[data-as-key]');
        if (key) { this.toggle(key.dataset.asKey); return; }
        if (e.target.closest('[data-as-done]')) { this.browsing = true; this.toggle(null); this.refinements.hidden = true; this.refineButton.setAttribute('aria-expanded', 'false'); this.render(); this.refineButton.focus({preventScroll:true}); return; }
        if (e.target.closest('[data-as-sort]')) { this.q.blur(); this.toggle('sort'); return; }
        const val = e.target.closest('[data-as-value]');
        if (val && !val.disabled) { const value = val.dataset.asValue; this.pick(this.open, value); [...this.panel.querySelectorAll('[data-as-value]')].find(button => button.dataset.asValue === value)?.focus({ preventScroll: true }); return; }
        const sort = e.target.closest('[data-as-sort-to]');
        if (sort) { this.ask({ sort: sort.dataset.asSortTo }, true); this.toggle(null); return; }
        const drop = e.target.closest('[data-as-drop]');
        if (drop) { const [k, ...v] = drop.dataset.asDrop.split('|'); this.pick(k, v.join('|')); return; }
        if (e.target.closest('[data-as-clear]')) { this.ask({ sel: this.blank() }, true); return; }
        if (e.target.closest('[data-as-more]')) { this.shown += AS_PAGE; this.render(false); return; }
      });
      this.onOutside = (e) => { if (this.open && !this.contains(e.target)) this.toggle(null); else if (this.open && !e.target.closest('[data-as-panel], [data-as-key], [data-as-sort], [data-as-refine]')) this.toggle(null); };
      document.addEventListener('pointerdown', this.onOutside);
      this.onKey = (e) => { if (e.key === 'Escape' && this.open) { const was = this.open; this.toggle(null); this.querySelector(was === 'sort' ? '[data-as-sort]' : `[data-as-key="${was}"]`)?.focus(); } };
      document.addEventListener('keydown', this.onKey);
      this.onPop = () => { this.state = this.readUrl(); this.q.value = this.state.q; this.browsing = !!(this.state.q || this.state.collection); this.shown = AS_PAGE; this.render(); };
      if (!this.embedded) window.addEventListener('popstate', this.onPop);
      this.onRailScroll = () => this.markRails();
      this.addEventListener('scroll', this.onRailScroll, true);
      this.onRailFocus = event => { if (event.target.closest('.gg-search__chips,.gg-active__list,.gg-asearch__keys')) event.target.scrollIntoView({block:'nearest', inline:'nearest'}); };
      this.addEventListener('focusin', this.onRailFocus);
      this.railObserver = new ResizeObserver(this.onRailScroll);
      this.querySelectorAll('.gg-search__chips,.gg-active__list,.gg-asearch__keys').forEach(rail => this.railObserver.observe(rail));
    }

    load() {
      if (this.loadingJob) return this.loadingJob;
      this.setAttribute('aria-busy', 'true');
      this.loadingJob = loadSearchIndex(this.dataset.indexUrl).then((items) => {
        this.items = items;
        // a value's place in its list is fixed by the whole catalogue, most pieces first
        this.order = {};
        for (const { key } of AS_FACETS) {
          const c = new Map();
          for (const it of items) for (const v of it.f[key]) c.set(v, (c.get(v) || 0) + 1);
          this.order[key] = key === 'dept' ? Object.keys(AS_DEPTS).filter((d) => c.has(d)) : [...c.keys()].sort((a, b) => c.get(b) - c.get(a) || a.localeCompare(b));
        }
        this.removeAttribute('aria-busy');
        this.querySelector('[data-as-loading]')?.remove();
        this.sortBtn.hidden = false;
        this.render();
      }).catch((e) => { console.error('[gallery-gothic] search index failed:', e); this.removeAttribute('aria-busy'); const note = this.querySelector('[data-as-loading]'); if (note) { note.hidden = false; note.textContent = 'Filters are temporarily unavailable. You can still search by name.'; } });

      return this.loadingJob;
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.onOutside);
      document.removeEventListener('keydown', this.onKey);
      window.removeEventListener('popstate', this.onPop);
      this.removeEventListener('scroll', this.onRailScroll, true);
      this.removeEventListener('focusin', this.onRailFocus);
      this.railObserver?.disconnect();
      window.visualViewport?.removeEventListener('resize', this.onViewport);
      window.visualViewport?.removeEventListener('scroll', this.onViewport);
      window.removeEventListener('resize', this.onViewport);
      window.removeEventListener('scroll', this.onViewport);
      cancelAnimationFrame(this.viewportFrame);
      this.removeEventListener('focusin', this.onViewport);
      this.removeEventListener('focusout', this.onViewport);
    }

    updateViewport() {
      const viewport = window.visualViewport;
      const height = viewport?.height || window.innerHeight;
      const focused = document.activeElement === this.q;
      if (window.innerWidth !== this.viewportWidth) {
        this.viewportWidth = window.innerWidth;
        this.viewportBaseline = window.innerHeight;
      }
      if (!focused) this.viewportBaseline = Math.max(window.innerHeight, height, this.viewportBaseline);
      const keyboard = focused && this.viewportBaseline - height > 120 && (viewport?.scale || 1) < 1.1;
      this.viewportHost.style.setProperty('--search-viewport-height', `${height}px`);
      this.viewportHost.style.setProperty('--search-viewport-top', `${viewport?.offsetTop || 0}px`);
      this.viewportHost.toggleAttribute('data-search-keyboard', keyboard);
      if (this.embedded && !this.viewportHost.hidden) {
        const host = this.viewportHost;
        const box = host.getBoundingClientRect();
        const scale = box.width / host.offsetWidth || 1;
        const origin = box.top - (parseFloat(getComputedStyle(host).top) || 0) * scale;
        const viewportTop = viewport?.offsetTop || 0;
        const headerBottom = document.querySelector('.gg-header')?.getBoundingClientRect().bottom || viewportTop;
        const top = Math.max(viewportTop, headerBottom);
        host.style.setProperty('--search-shell-top', `${Math.max(0,(top - origin) / scale)}px`);
        host.style.setProperty('--search-shell-height', `${Math.max(0,(viewportTop + height - top) / scale)}px`);
      }

    }

    markRails() {
      this.querySelectorAll('.gg-search__chips,.gg-active__list,.gg-asearch__keys').forEach(rail => {
        rail.toggleAttribute('data-more-left', rail.scrollLeft > 2);
        rail.toggleAttribute('data-more-right', rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 2);
      });
    }

    blank() { return Object.fromEntries(AS_FACETS.map(({ key }) => [key, new Set()])); }

    readUrl() {
      const u = new URL(window.location.href);
      const sel = this.blank();
      for (const { key } of AS_FACETS) for (const v of u.searchParams.getAll(key)) if (v) sel[key].add(v);
      return { q: (u.searchParams.get('q') || '').trim(), collection: u.searchParams.get('collection') || '', sel, sort: u.searchParams.get('sort') || '' };
    }

    writeUrl(push) {
      if (this.embedded) return;
      const u = new URL(this.dataset.searchUrl || '/search', window.location.origin);
      if (this.state.q) { u.searchParams.set('q', this.state.q); u.searchParams.set('type', 'product'); }
      for (const { key } of AS_FACETS) for (const v of this.state.sel[key]) u.searchParams.append(key, v);
      if (this.state.collection) u.searchParams.set('collection', this.state.collection);
      if (this.state.sort) u.searchParams.set('sort', this.state.sort);
      const next = u.pathname + u.search;
      if (next === window.location.pathname + window.location.search) return;
      try { history[push ? 'pushState' : 'replaceState']({ ggSearch: true }, '', next); } catch {}
    }

    // the question changes: the words, the values, the sort — the wall answers from the top
    ask(change, push = false, browse = push) {
      this.state = { ...this.state, ...change };
      if (Object.prototype.hasOwnProperty.call(change, "q")) this.browsing = !!change.q || !!browse;
      this.shown = AS_PAGE;
      this.writeUrl(push);
      this.render();
    }

    badgeActive(badge) {
      if (badge.dataset.asCollection) return this.state.collection === badge.dataset.asCollection;
      return !this.state.collection && this.state.q.trim().toLowerCase() === badge.dataset.asTerm.trim().toLowerCase();
    }

    toggleBadge(badge) {
      clearTimeout(this.typing);
      const active = this.badgeActive(badge);
      const collection = active ? '' : badge.dataset.asCollection || '';
      const q = active || collection ? '' : badge.dataset.asTerm;
      this.q.value = q;
      this.q.blur();
      this.ask({q, collection}, true, !active);
    }

    pick(key, value) {
      if (!key || !this.state.sel[key]) return;
      const sel = { ...this.state.sel, [key]: new Set(this.state.sel[key]) };
      if (sel[key].has(value)) sel[key].delete(value); else sel[key].add(value);
      this.ask({ sel }, true);
    }

    toggle(which) {
      if (!this.items) this.load();
      this.open = which && which !== this.open ? which : null;
      this.refinements.hidden = !this.open || this.open === 'sort';
      this.refineButton.setAttribute('aria-expanded', String(!this.refinements.hidden));
      this.keys.querySelectorAll('[data-as-key]').forEach((k) => k.setAttribute('aria-expanded', String(k.dataset.asKey === this.open)));
      this.sortBtn.setAttribute('aria-expanded', String(this.open === 'sort'));
      this.renderPanel();
    }

    sortOf() { return this.state.sort || (asTokens(this.state.q).length ? 'relevance' : 'featured'); }

    // the answer: the pieces, and what every value would leave given the rest of the question
    compute() {
      const tokens = asTokens(this.state.q);
      const scored = [];
      for (const it of this.items) {
        if (this.state.collection && !it.collections.includes(this.state.collection)) continue;
        const s = tokens.length ? asScore(it, tokens) : 1;
        if (s) scored.push([it, s]);
      }
      const sel = this.state.sel;
      const passes = (it, skip) => AS_FACETS.every(({ key }) => key === skip || !sel[key].size || it.f[key].some((v) => sel[key].has(v)));
      const counts = {};
      for (const { key } of AS_FACETS) {
        const c = new Map();
        for (const [it] of scored) if (passes(it, key)) for (const v of it.f[key]) c.set(v, (c.get(v) || 0) + 1);
        counts[key] = c;
      }
      const found = scored.filter(([it]) => passes(it, null));
      const sort = this.sortOf();
      found.sort((a, b) => (sort === 'relevance' ? b[1] - a[1] || a[0].i - b[0].i
        : sort === 'price-asc' ? a[0].p - b[0].p || a[0].i - b[0].i
        : sort === 'price-desc' ? b[0].p - a[0].p || a[0].i - b[0].i
        : sort === 'newest' ? b[0].n - a[0].n || a[0].i - b[0].i
        : a[0].i - b[0].i));
      let list = found.map(([it]) => it);
      if ((sort === 'relevance' || sort === 'featured') && asJewelryAsked(tokens, sel.dept, list)) list = asEveryOtherNew(list);
      return { found: list, counts };
    }

    label(key, v) { return key === 'dept' ? AS_DEPTS[v] || v : v; }

    render(fresh = true) {
      this.querySelectorAll('[data-as-term]').forEach(badge => badge.setAttribute('aria-pressed', String(this.badgeActive(badge))));
      if (!this.items) {
        const searching = !!(this.state.q || this.state.collection);
        const note = this.querySelector('[data-as-loading]');
        if (note) note.hidden = !searching;
        if (this.zero) this.zero.hidden = searching;
        return;
      }
      this.answer = this.compute();
      const { found } = this.answer;
      const n = found.length;
      this.countEl.textContent = `${n} ${n === 1 ? 'piece' : 'pieces'}`;
      // the keys: a facet the catalogue has no values for yet is not offered (the scan has not filed it)
      this.keys.innerHTML = AS_FACETS.filter(({ key }) => this.order[key].length).map(({ key, label }) => {
        const k = this.state.sel[key].size;
        return `<button type="button" class="gg-asearch__key${k ? ' is-on' : ''}" data-as-key="${key}" aria-expanded="${this.open === key}" aria-controls="${this.panel.id}">${label}${k ? `<span class="gg-asearch__chosen">${k}</span>` : ''}<span class="gg-dd__chevron" aria-hidden="true"></span></button>`;
      }).join('');
      this.querySelector('[data-as-sort-label]').textContent = (AS_SORTS.find(([v]) => v === this.sortOf()) || [])[1] || '';
      // what is asked, beside the count: a chip a value, ✕ to let it go
      const chosen = AS_FACETS.flatMap(({ key }) => [...this.state.sel[key]].map((v) => [key, v]));
      this.active.hidden = !chosen.length;
      const showResults = !!(this.browsing || this.state.q || this.state.collection || chosen.length);
      if (this.zero) this.zero.hidden = showResults;
      this.results.hidden = !showResults;
      this.querySelector('[data-as-filter-count]').textContent = chosen.length ? String(chosen.length) : '';
      this.countEl.textContent = showResults ? `${n} ${n === 1 ? 'piece' : 'pieces'}` : '';
      if (this.summary) this.summary.hidden = !showResults;
      this.toggleAttribute('data-search-results', showResults);
      this.active.querySelector('.gg-active__list').innerHTML = chosen.map(([k, v]) => `<button type="button" class="gg-cchip" data-as-drop="${esc(k)}|${esc(v)}">${esc(this.label(k, v))} <span aria-hidden="true">✕</span></button>`).join('') + (chosen.length > 1 ? '<button type="button" class="gg-cchips__clear" data-as-clear>Clear all</button>' : '');
      this.renderPanel();
      // the wall
      if (!showResults) { this.results.innerHTML = ''; } else if (!n) {
        this.results.innerHTML = `<div class="gg-empty gg-container gg-asearch__none"><p>No pieces match these filters.</p>${chosen.length ? '<p class="gg-muted">Remove a filter or try a different search.</p>' : ''}</div>`;
      } else {
        const cells = found.slice(0, this.shown).map((it) => `<div class="gg-cgrid__cell">${it.card}</div>`).join('');
        const more = n > this.shown ? `<div class="gg-asearch__more"><button type="button" class="gg-btn" data-as-more>More pieces</button></div>` : '';
        this.results.innerHTML = `<div class="gg-cgrid-clip gg-container"><div class="gg-cgrid" data-as-grid>${cells}</div></div>${more}`;
      }
      if (fresh && this.body) this.body.scrollTop = 0;
      this.markRails();
      this.adoptModels();
    }

    renderPanel() {
      const drawer = this.querySelector('[data-as-drawer]');
      if (drawer) drawer.hidden = !this.items || !this.open;
      if (!this.items || !this.open) { this.panel.hidden = true; this.panel.innerHTML = ''; return; }
      if (this.open === 'sort') {
        const cur = this.sortOf();
        this.panel.innerHTML = `<div class="gg-asearch__values" role="group" aria-label="Sort">${AS_SORTS.filter(([v]) => v !== 'relevance' || asTokens(this.state.q).length).map(([v, l]) => `<button type="button" class="gg-asearch__value" data-as-sort-to="${v}" aria-pressed="${v === cur}">${l}</button>`).join('')}</div>`;
      } else {
        const key = this.open, counts = this.answer.counts[key], sel = this.state.sel[key];
        const facet = AS_FACETS.find((f) => f.key === key);
        this.panel.innerHTML = `<div class="gg-asearch__values" role="group" aria-label="${facet.label}">${this.order[key].map((v) => {
          const c = counts.get(v) || 0, on = sel.has(v);
          const sw = key === 'colour' && AS_SWATCH[v] ? `<i class="gg-asearch__swatch" style="--sw: ${AS_SWATCH[v]}" aria-hidden="true"></i>` : '';
          return `<button type="button" class="gg-asearch__value" data-as-value="${esc(v)}" aria-pressed="${on}"${!c && !on ? ' disabled' : ''}>${sw}${esc(this.label(key, v))}<span class="gg-asearch__n">${c}</span></button>`;
        }).join('')}</div>`;
      }
      if (this.open !== 'sort') this.panel.innerHTML += `<button type="button" class="gg-asearch__done" data-as-done>Show pieces <span aria-hidden="true">→</span></button>`;
      this.panel.hidden = false;
    }

    scrollToWall() {
      const bar = this.querySelector('.gg-asearch__bar');
      const top = this.results.getBoundingClientRect().top + window.scrollY - (bar ? bar.offsetHeight : 0) - (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0);
      window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
    }

    // a card drawn in 3D needs the viewer, which a page loads only when it prints one
    adoptModels() {
      if (!this.results.querySelector('gg-model-viewer') || customElements.get('gg-model-viewer') || document.querySelector('script[data-model-src]')) return;
      const s = document.createElement('script');
      s.src = this.dataset.modelLoader; s.dataset.modelSrc = this.dataset.modelSrc; s.defer = true;
      if (s.src) document.head.appendChild(s);
    }
  }
  // (esc: the one escaper, declared with the cart below — it is only ever called after the script has run)
  customElements.define('gg-asearch', GGAdvancedSearch);

  /* ----------------------------------------------------------
     The plate — a card's framing on its black plate (snippets/plate-vars
     resolves rack override → master → kind default server-side). A card
     fetched for a rail panel does not know the wall it will hang on, so the
     wall re-applies its rack's override from the card's own plate document
     (data-plate) once the card is in the track. Field by field, like the
     snippet: an override that only says `s` keeps the resolved x and y.
     ---------------------------------------------------------- */
  const ggPlate = {
    read(media) {
      try { return media.dataset.plate ? JSON.parse(media.dataset.plate) : null; } catch { return null; }
    },
    // the vars as they stand on the box, as numbers
    vars(media) {
      const g = (k, d) => { const v = parseFloat(media.style.getPropertyValue(k)); return Number.isFinite(v) ? v : d; };
      let grade = null; try { grade = media.dataset.plateGrade ? JSON.parse(media.dataset.plateGrade) : null; } catch { grade = null; }
      return { s: g('--plate-s', 1), x: g('--plate-x', 0), y: g('--plate-y', 0), grain: g('--plate-grain', 0), r: g('--plate-r', 0), flipX: g('--plate-fx', 1) < 0, flipY: g('--plate-fy', 1) < 0, grade };
    },
    set(media, f) {
      if (!media) return;
      if (f.s != null) media.style.setProperty('--plate-s', f.s);
      if (f.x != null) media.style.setProperty('--plate-x', f.x);
      if (f.y != null) media.style.setProperty('--plate-y', f.y);
      if (f.grain != null) { media.style.setProperty('--plate-grain', f.grain); media.classList.toggle('gg-plate--grain', f.grain > 0); }
      if (f.r != null) media.style.setProperty('--plate-r', f.r);
      if (typeof f.flipX === 'boolean') media.style.setProperty('--plate-fx', f.flipX ? -1 : 1);
      if (typeof f.flipY === 'boolean') media.style.setProperty('--plate-fy', f.flipY ? -1 : 1);
      // the grade (assets/plate-grade.js): a frame that states one — or states null — sets it; one that says nothing
      // about it (an older record) leaves the picture as it is
      if ('grade' in f) {
        if (f.grade) media.dataset.plateGrade = JSON.stringify(f.grade); else delete media.dataset.plateGrade;
        if (window.ggGrade) window.ggGrade.apply(media, f.grade || null);
      }
    },
    // hang a card on a wall: its rack override, if it has one, over what the
    // server resolved (master, else the kind's default)
    apply(media, rack) {
      if (!media || !rack) return;
      media.dataset.plateRack = rack;
      const doc = ggPlate.read(media);
      const r = doc && doc.racks && doc.racks[rack];
      if (!r) return;
      ggPlate.set(media, r);
      media.dataset.plateFrom = 'rack';
    },
    applyAll(root, rack) {
      if (!root || !rack) return;
      const list = root.matches && root.matches('.gg-card__media.gg-plate') ? [root] : root.querySelectorAll('.gg-card__media.gg-plate');
      list.forEach((m) => ggPlate.apply(m, rack));
    },
  };
  window.ggPlate = ggPlate;

  /* ---- a card's film or 3D piece, as the wall chose to play it: by itself (Autoplay: while a quarter is in view;
     Always on: starts at a fifth), under the pointer, or on a press of the button. Automatic films continue until
     less than fifteen percent is visible; separate return thresholds avoid flickering playback at the edge. ---- */
  const cardPlay = () => {
    const saveCardMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches || !!(navigator.connection && navigator.connection.saveData);
    document.querySelectorAll('video.gg-card__film[data-card-film]:not([data-card-armed])').forEach((v) => {
      v.dataset.cardArmed = '';
      const cell = v.closest('.gg-card') || v.parentElement; const mode = v.dataset.cardFilm;
      const btn = cell.querySelector('[data-card-play]');
      const play = () => { v.__cardWant = true; v.play().catch(() => {}); cell.classList.add('is-playing'); };
      const stop = () => { v.__cardWant = false; v.pause(); cell.classList.remove('is-playing'); };
      if (mode === 'hover') { cell.addEventListener('pointerenter', play); cell.addEventListener('pointerleave', () => { stop(); v.currentTime = 0; }); }
      if (mode === 'button' && btn) btn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); v.paused ? play() : stop(); });
      if ((mode === 'autoplay' || mode === 'always') && !saveCardMotion) {
        const enter = mode === 'always' ? ALWAYS_SHARE : FILM_ENTER_SHARE;
        if ('IntersectionObserver' in window) new IntersectionObserver((es) => es.forEach((en) => (en.isIntersecting && en.intersectionRatio >= (v.__cardWant ? FILM_EXIT_SHARE : enter) ? play() : stop())), { threshold: [0, FILM_EXIT_SHARE, enter, 1] }).observe(v); else play();
      }
    });
    document.querySelectorAll('.gg-card__model[data-card-rotate]:not([data-card-armed])').forEach((box) => {
      box.dataset.cardArmed = '';
      const cell = box.closest('.gg-card') || box.parentElement; const mode = box.dataset.cardRotate;
      const mv = () => box.querySelector('gg-model-viewer, model-viewer'); const btn = cell.querySelector('[data-card-play]');
      // the house's viewer turns by its `turn` (the pace the Lab saved); Shopify's by auto-rotate
      const on = () => { const m = mv(); if (!m) return; if (m.localName === 'gg-model-viewer') m.setAttribute('turn', m.dataset.turn || '1'); else { m.setAttribute('auto-rotate', ''); m.setAttribute('camera-controls', ''); } cell.classList.add('is-playing'); };
      const off = () => { const m = mv(); if (!m) return; if (m.localName === 'gg-model-viewer') m.setAttribute('turn', '0'); else m.removeAttribute('auto-rotate'); cell.classList.remove('is-playing'); };
      if (mode === 'rotate') on();
      if (mode === 'hover') { cell.addEventListener('pointerenter', on); cell.addEventListener('pointerleave', off); }
      if (mode === 'button' && btn) btn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); cell.classList.contains('is-playing') ? off() : on(); });
    });
  };
  cardPlay();
  new MutationObserver(() => cardPlay()).observe(document.body, { childList: true, subtree: true });
  window.addEventListener('gg-page-scroll-end', () => document.querySelectorAll('video.gg-card__film[data-card-armed]').forEach((v) => { if (v.__cardWant && v.paused && v.isConnected) v.play().catch(() => {}); }));

  /* ----------------------------------------------------------
     <gg-rail> — a room's wall: quick-filter chips switch between
     server-rendered panels (nothing is fetched, nothing leaves the
     page); the arrows page the active track one cell at a time.
     ---------------------------------------------------------- */
  function rackCardMatches(cell, kind, value) {
    const expected = String(value || '').trim().toLocaleLowerCase();
    if (!expected) return true;
    const data = cell.dataset;
    if (kind === 'category' || kind === 'google_category') {
      const actual = String(kind === 'category' ? data.rackCategory || '' : data.rackGoogleCategory || '').split('/').pop().trim().toLocaleLowerCase();
      return expected.split(',').map((id) => id.trim()).filter(Boolean).some((id) => actual === id || kind === 'category' && actual.startsWith(id + '-'));
    }
    if (kind === 'product_type') return String(data.rackProductType || '').trim().toLocaleLowerCase() === expected;
    let tags; try { tags = JSON.parse(data.rackTags || '[]').map((tag) => String(tag).trim().toLocaleLowerCase()); } catch { tags = []; }
    return expected.split(',').some((group) => group.split('+').every((word) => tags.includes(word.trim())));
  }
  function rackSortedCells(cells, order) {
    const list = [...cells];
    if (order === 'title') list.sort((a, b) => (a.dataset.rackProductTitle || '').localeCompare(b.dataset.rackProductTitle || '', undefined, { sensitivity: 'base', numeric: true }));
    if (order === 'newest') list.sort((a, b) => Number(b.dataset.rackCreated || 0) - Number(a.dataset.rackCreated || 0));
    if (order === 'price_asc' || order === 'price_desc') list.sort((a, b) => (Number(a.dataset.rackPrice || 0) - Number(b.dataset.rackPrice || 0)) * (order === 'price_desc' ? -1 : 1));
    return list;
  }
  class GGRail extends HTMLElement {
    connectedCallback() {
      if (this.events) this.events.abort();
      this.events = new AbortController();
      this.loads ||= new Map();
      const options = { signal: this.events.signal };
      this.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-rail-chip]');
        if (chip) return this.select(chip.dataset.railChip);
        const more = e.target.closest('[data-rack-load-more]');
        if (more) return this.fill(more.closest('[data-rail-panel]')?.querySelector('[data-rail-track]'));
        const prev = e.target.closest('[data-rail-prev]');
        const next = e.target.closest('[data-rail-next]');
        if (prev || next) this.page(prev ? -1 : 1);
      }, options);
      this.querySelectorAll('[data-rail-track]').forEach((t) => t.addEventListener('scroll', () => this.syncArrows(), { ...options, passive: true }));
      window.addEventListener('resize', () => this.syncArrows(), { ...options, passive: true });
      window.addEventListener('gg-rack-layout-change', () => this.syncArrows(), options);
      this.syncArrows();
      const raise = () => {
        this.querySelectorAll('[data-reveal]').forEach((el) => {
          try { revealObserver.unobserve(el); } catch {}
          el.classList.add('is-revealed'); el.removeAttribute('data-reveal');
        });
        this.fill(this.track);
      };
      if ('IntersectionObserver' in window) {
        this.revealIO?.disconnect();
        this.revealIO = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { raise(); this.revealIO.disconnect(); } }, { rootMargin: '0px 0px 300px 0px' });
        this.revealIO.observe(this);
      } else raise();
    }
    disconnectedCallback() {
      this.events?.abort(); this.events = null;
      this.revealIO?.disconnect();
      for (const state of this.loads?.values() || []) state.controller?.abort();
    }
    get track() { return this.querySelector('[data-rail-panel]:not([hidden]) [data-rail-track]'); }
    select(id) {
      for (const [track, state] of this.loads || []) if (track.closest('[data-rail-panel]')?.dataset.railPanel !== id) state.controller?.abort();
      this.querySelectorAll('[data-rail-chip]').forEach((c) => { const on = c.dataset.railChip === id; c.classList.toggle('is-active', on); c.setAttribute('aria-pressed', String(on)); });
      this.querySelectorAll('[data-rail-panel]').forEach((p) => {
        const on = p.dataset.railPanel === id; p.hidden = !on; p.classList.toggle('is-active', on);
        if (on) { p.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed')); this.fill(p.querySelector('[data-rail-track]')); }
      });
      this.syncArrows();
    }
    loadState(track) {
      let state = this.loads.get(track);
      if (!state) {
        const sort = { newest: 'created-descending', title: 'title-ascending', price_asc: 'price-ascending', price_desc: 'price-descending' }[track.dataset.rackSort];
        const raw = (track.dataset.railSrcs || track.dataset.railSrc || '').split('|').map((src) => src.trim()).filter(Boolean);
        const sources = raw.map((src) => { const url = new URL(src, location.origin); if (url.origin !== location.origin) return null; url.searchParams.set('view', 'cards'); if (sort) url.searchParams.set('sort_by', sort); return { next: url.href, path: url.pathname, count: 0, visited: new Set() }; }).filter(Boolean);
        state = { sources, cells: [], seen: new Set(), controller: null, complete: false, sorted: !!sort };
        this.loads.set(track, state);
      }
      return state;
    }
    renderLoaded(track, state, status) {
      const limit = Math.min(50, Math.max(1, Number(track.dataset.railLimit) || 8));
      const more = track.querySelector('.gg-rail__more');
      track.querySelectorAll(':scope > [data-rack-item], :scope > [data-rack-empty-state], :scope > [data-rack-promo-block]').forEach((cell) => cell.remove());
      const promos = [...(track.querySelector('template[data-rack-promos]')?.content.children || [])];
      const insertPromos = (position) => promos.filter((promo) => Number(promo.dataset.rackPromoPosition) === position).forEach((promo) => track.insertBefore(promo.cloneNode(true), more));
      const visible = rackSortedCells(state.cells, track.dataset.rackSort).slice(0, limit);
      for (const [i, saved] of visible.entries()) {
        insertPromos(i + 1);
        const cell = saved.cloneNode(true);
        if (track.classList.contains('gg-rack__track')) { cell.classList.remove('gg-rail__cell', 'gg-cgrid__cell'); cell.classList.add('gg-rack__cell'); }
        const plaque = cell.querySelector('.gg-card__plaque'); if (plaque) plaque.textContent = String(i + 1).padStart(2, '0');
        track.insertBefore(cell, more);
      }
      let notice = track.parentElement.querySelector(':scope > [data-rack-load-status]');
      if (!notice) { notice = document.createElement('div'); notice.className = 'gg-rack__load-status'; notice.dataset.rackLoadStatus = ''; notice.setAttribute('role', 'status'); track.after(notice); }
      notice.replaceChildren(); notice.hidden = false;
      if (status === 'loading') notice.textContent = track.dataset.rackLoadingLabel;
      else if (status === 'error' || !state.complete) {
        const text = document.createElement('span'); text.textContent = status === 'error' ? track.dataset.rackErrorLabel : track.dataset.rackContinueLabel;
        const button = document.createElement('button'); button.type = 'button'; button.className = 'gg-rail__chip'; button.dataset.rackLoadMore = ''; button.textContent = status === 'error' ? track.dataset.rackRetryLabel : track.dataset.rackMoreLabel;
        notice.append(text, button);
      } else if (!visible.length) notice.textContent = track.dataset.rackEmptyLabel;
      else notice.hidden = true;
      if (track.dataset.rack && visible.length) ggPlate.applyAll(track, track.dataset.rack);
      track.dispatchEvent(new CustomEvent('gg-rack-products-loaded', { bubbles: true }));
      this.syncArrows();
    }
    async fill(track) {
      if (!track?.dataset.railSrc || !this.isConnected) return;
      const state = this.loadState(track);
      if (state.complete || state.controller && !state.controller.signal.aborted) return;
      const controller = new AbortController(); state.controller = controller;
      const signal = controller.signal;
      const limit = Math.min(50, Math.max(1, Number(track.dataset.railLimit) || 8));
      track.dataset.railLoaded = 'loading'; track.setAttribute('aria-busy', 'true');
      this.renderLoaded(track, state, 'loading');
      try {
        let requests = 0;
        while (requests < 10) {
          if (signal.aborted || !track.isConnected) return;
          if (!state.sorted && state.cells.length >= limit) { state.complete = true; break; }
          const source = state.sources.find((source) => source.next && (!state.sorted || source.count < limit));
          if (!source) { state.complete = true; break; }
          const requested = source.next;
          if (source.visited.has(requested)) throw new Error('Repeated catalog page');
          const response = await fetch(requested, { headers: { Accept: 'text/html' }, signal }); requests++;
          if (!response.ok) throw new Error('Catalog request failed');
          const returned = new URL(response.url || requested, location.origin);
          if (returned.origin !== location.origin || returned.pathname !== source.path) { source.next = null; continue; }
          const html = await response.text();
          if (signal.aborted || !track.isConnected) return;
          const documentPage = new DOMParser().parseFromString(html, 'text/html');
          for (const cell of documentPage.querySelectorAll('[data-rack-item]')) {
            if (!rackCardMatches(cell, track.dataset.rackFilterKind, track.dataset.rackFilterValue)) continue;
            const handle = cell.dataset.rackItem; source.count++;
            if (!handle || state.seen.has(handle)) continue;
            state.seen.add(handle); state.cells.push(document.adoptNode(cell));
          }
          source.visited.add(requested);
          const next = documentPage.querySelector('[data-rack-next]')?.getAttribute('href');
          source.next = null;
          if (next) {
            const url = new URL(next, requested);
            if (url.origin !== location.origin || url.pathname !== source.path) throw new Error('Invalid catalog page');
            url.searchParams.set('view', 'cards');
            const sort = new URL(requested).searchParams.get('sort_by'); if (sort) url.searchParams.set('sort_by', sort);
            source.next = url.href;
          }
        }
        state.complete = state.complete || (!state.sorted && state.cells.length >= limit) || state.sources.every((source) => !source.next || state.sorted && source.count >= limit);
        if (!signal.aborted && track.isConnected) { track.dataset.railLoaded = state.complete ? 'done' : 'partial'; this.renderLoaded(track, state, state.complete ? 'done' : 'partial'); }
      } catch (error) {
        if (!signal.aborted && track.isConnected) { delete track.dataset.railLoaded; this.renderLoaded(track, state, 'error'); }
      } finally {
        if (state.controller === controller) { state.controller = null; track.removeAttribute('aria-busy'); if (signal.aborted) delete track.dataset.railLoaded; }
      }
    }
    page(dir) {
      const track = this.track; if (!track) return;
      const cell = track.querySelector('[data-rack-item], .gg-rail__cell');
      const step = cell ? cell.getBoundingClientRect().width : track.clientWidth / 4;
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      const left = dir < 0 && track.scrollLeft <= 2 ? max : dir > 0 && track.scrollLeft >= max - 2 ? 0 : Math.max(0, Math.min(max, track.scrollLeft + dir * step * 2));
      track.scrollTo({ left, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }
    syncArrows() {
      const track = this.track; if (!track) return;
      this.querySelector('[data-rail-prev]')?.removeAttribute('disabled');
      this.querySelector('[data-rail-next]')?.removeAttribute('disabled');
      track.toggleAttribute('data-fade-left', track.scrollLeft > 8);
      track.toggleAttribute('data-fade-right', track.scrollLeft + track.clientWidth < track.scrollWidth - 8);
    }
  }
  customElements.define('gg-rail', GGRail);

  /* ----------------------------------------------------------
     <gg-scroll-film> — the stage stays while the page moves.
     q = clamp((scrollY − top) / (height − stage), 0, 1) → --q on the
     element (CSS opens the frame, deepens the plume, brings the
     words); a film's currentTime follows q; the rail shows the time.
     ---------------------------------------------------------- */
  class GGScrollFilm extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.code = this.querySelector('[data-scrub-code]');
      this.inView = false;
      this.io = new IntersectionObserver((es) => { for (const e of es) { this.inView = e.isIntersecting; if (e.isIntersecting) this.sync(); } }, { rootMargin: '120px' });
      this.io.observe(this);
      let queued = false;
      this.onScroll = () => {
        if (!this.inView || queued) return;
        queued = true;
        requestAnimationFrame(() => { queued = false; this.sync(); });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      if (this.video) this.video.addEventListener('loadedmetadata', () => this.sync(), { once: true });
      this.sync();
    }
    disconnectedCallback() {
      this.io?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }
    sync() {
      const r = this.getBoundingClientRect();
      const stage = Math.min(this.querySelector(".gg-scrub__sticky")?.offsetHeight || window.innerHeight, window.innerHeight);
      const travel = Math.max(1, r.height - stage);
      const q = clamp(-r.top / travel, 0, 1);
      this.style.setProperty('--q', q.toFixed(4));
      // a film with a scene (the section's Scene, the Lab's Trim) is wound only across it: the scroll runs from its in
      // to its out, and the time shown counts through the scene
      const tr = this.video && this.video.duration && window.ggTrimOf ? window.ggTrimOf(this.video) : null;
      const span = tr ? Math.max(0, (tr.out || this.video.duration) - tr.in) : 0;
      if (this.video && this.video.duration && !reducedMotion.matches) {
        const t = tr ? tr.in + q * span : q * this.video.duration;
        if (Math.abs(this.video.currentTime - t) > 0.04) this.video.currentTime = t;
      }
      if (this.code) {
        const secs = tr ? q * span : this.video?.duration ? q * this.video.duration : q * 24;
        const m = Math.floor(secs / 60), sec = Math.floor(secs % 60);
        this.code.textContent = `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
      }
    }
  }
  customElements.define('gg-scroll-film', GGScrollFilm);

  /* ----------------------------------------------------------
     <gg-house-overlay> — seamless subpage overlays for the
     House of Surrealium page. Cards carry their article in an
     inert <template data-overlay-content>; clicking lifts it
     into a full-screen room over the page. The card's href is
     the article's REAL standalone URL: we pushState onto it so
     the address bar, back button and deep links all agree —
     without JS the card is a plain link to that page.
     Motion follows the house rule: exit fades as one unit,
     entry washes in (the cloned [data-reveal] children stagger
     via the shared reveal observer). Reduced motion = instant.
     ---------------------------------------------------------- */
  const houseOverlay = (() => {
    let shell = null;
    let contentEl = null;
    let labelEl = null;
    let closeBtn = null;
    let isOpen = false;
    let pushed = false;
    let trigger = null;

    function build() {
      if (shell) return;
      shell = document.createElement('div');
      shell.className = 'gg-hoverlay';
      shell.setAttribute('role', 'dialog');
      shell.setAttribute('aria-modal', 'true');
      shell.hidden = true;
      shell.innerHTML =
        '<div class="gg-hoverlay__bar">' +
        '<span class="gg-hoverlay__label"></span>' +
        '<button type="button" class="gg-hoverlay__close" data-hoverlay-close>' +
        '<span class="gg-hoverlay__close-label">Close</span>' +
        '<span aria-hidden="true">✕</span>' +
        '</button>' +
        '</div>' +
        '<div class="gg-hoverlay__scroll"><div class="gg-hoverlay__content"></div></div>';
      document.body.appendChild(shell);
      contentEl = shell.querySelector('.gg-hoverlay__content');
      labelEl = shell.querySelector('.gg-hoverlay__label');
      closeBtn = shell.querySelector('.gg-hoverlay__close');
      shell.addEventListener('click', (e) => {
        if (e.target.closest('[data-hoverlay-close]')) requestClose();
      });
      shell.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab') return;
        const focusables = shell.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      });
    }

    function open(template, href, label, card) {
      build();
      trigger = card;
      contentEl.innerHTML = '';
      contentEl.appendChild(template.content.cloneNode(true));
      labelEl.textContent = label || '';
      shell.setAttribute('aria-label', label || 'Article');
      shell.hidden = false;
      shell.querySelector('.gg-hoverlay__scroll').scrollTop = 0;
      // reflow so the is-open transition actually runs
      void shell.offsetHeight;
      shell.classList.add('is-open');
      document.body.classList.add('gg-hoverlay-open');
      if (href && href !== '#') {
        history.pushState({ ggHouseOverlay: true }, '', href);
        pushed = true;
      }
      isOpen = true;
      closeBtn.focus();
    }

    function requestClose() {
      if (!isOpen) return;
      // closing via the UI pops the URL we pushed, so back/forward
      // and the ✕ button walk the same history
      if (pushed) history.back();
      else closeNow();
    }

    function closeNow() {
      if (!isOpen) return;
      isOpen = false;
      pushed = false;
      const finish = () => {
        shell.classList.remove('is-open', 'is-closing');
        shell.hidden = true;
        contentEl.innerHTML = '';
        document.body.classList.remove('gg-hoverlay-open');
        trigger?.focus?.();
        trigger = null;
      };
      if (reducedMotion.matches) {
        finish();
      } else {
        shell.classList.add('is-closing');
        setTimeout(finish, 380);
      }
    }

    window.addEventListener('popstate', () => {
      if (isOpen) {
        pushed = false; // the state is already gone
        closeNow();
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isOpen) requestClose();
    });

    return { open };
  })();

  class GGHouseOverlay extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const card = e.target.closest('[data-overlay-card]');
        if (!card || !this.contains(card)) return;
        const tpl = card.parentElement?.querySelector('template[data-overlay-content]');
        if (!tpl) return; // no article — behave as a plain link
        e.preventDefault();
        houseOverlay.open(tpl, card.getAttribute('href'), tpl.dataset.overlayLabel, card);
      });
      // role="button" cards (no href yet) need keyboard activation
      this.addEventListener('keydown', (e) => {
        const card = e.target.closest('[data-overlay-card][role="button"]');
        if (!card) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          card.click();
        }
      });
    }
  }
  customElements.define('gg-house-overlay', GGHouseOverlay);

  /* ----------------------------------------------------------
     House gates — chapter bands (the Atelier, the Laboratorium)
     start closed and open on approach: gated bands collapse to a
     zero-height point in the flow, and an observer swings the door
     well before the visitor scrolls to it, so the band is standing
     open by the time they arrive — the theatre lives in the
     [data-reveal] children washing in via the shared observer, not
     in content going missing. Any anchor pointing at a
     [data-house-gate] id still opens the band instantly and glides
     to it (index nav, pillar doors). Progressive: without JS
     nothing is ever hidden. Direct visits with a matching #hash
     arrive open. Disabled in the theme editor so merchants always
     see every band.
     ---------------------------------------------------------- */
  const inDesignMode = window.Shopify?.designMode;

  let gateObserver = null;
  function ensureGateObserver() {
    if (!gateObserver && 'IntersectionObserver' in window) {
      gateObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            gateObserver.unobserve(entry.target);
            entry.target.classList.remove('is-gated');
          });
        },
        // open when the closed door is within ~60% of a viewport below
        // the fold — expanded and settled before it can be seen
        { rootMargin: '0px 0px 60% 0px' }
      );
    }
    return gateObserver;
  }

  function initGates(root = document) {
    if (inDesignMode) return;
    root.querySelectorAll('[data-house-gate]').forEach((gate) => {
      const openByHash = gate.id && window.location.hash === '#' + gate.id;
      gate.classList.toggle('is-gated', !openByHash);
      if (openByHash) return;
      const observer = ensureGateObserver();
      if (observer) observer.observe(gate);
      else gate.classList.remove('is-gated');
    });
  }
  initGates();
  document.addEventListener('shopify:section:load', (e) => initGates(e.target));

  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;
    const id = link.getAttribute('href').slice(1);
    if (!id) return;
    const gate = document.getElementById(id);
    if (!gate || !gate.hasAttribute('data-house-gate')) return;
    e.preventDefault();
    gate.classList.remove('is-gated');
    history.replaceState({}, '', '#' + id);
    // let layout settle before gliding to the newly opened band
    requestAnimationFrame(() => {
      gate.scrollIntoView({
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
        block: 'start',
      });
    });
  });

  /* favorites — the pieces a visitor has set aside with the heart. The list lives in this browser (localStorage
     `gg-favorites`), so it is there on every visit, and it is shown in the bag (drawer and page) under the pieces
     being bought. An account is what would carry it beyond this browser; until that sync exists a signed-in
     customer is simply not promised it. The heart: a mauve outline at rest, coral and filled once the piece is kept. */
  const FAV_KEY = 'gg-favorites';
  const favStore = {
    list() { try { const v = JSON.parse(localStorage.getItem(FAV_KEY) || '[]'); return Array.isArray(v) ? v.filter((x) => x && x.handle) : []; } catch { return []; } },
    save(list) { try { localStorage.setItem(FAV_KEY, JSON.stringify(list.slice(0, 60))); } catch {} window.dispatchEvent(new CustomEvent('gg-favorites-change', { detail: { list } })); },
    has(handle) { return this.list().some((x) => x.handle === handle); },
    toggle(item) { const list = this.list(); const i = list.findIndex((x) => x.handle === item.handle); if (i >= 0) list.splice(i, 1); else list.unshift({ ...item, at: Date.now() }); this.save(list); return i < 0; },
    remove(handle) { this.save(this.list().filter((x) => x.handle !== handle)); },
  };
  window.ggFavorites = favStore;
  const favItem = (el) => ({ handle: el.dataset.favHandle || '', title: el.dataset.favTitle || '', url: el.dataset.favUrl || '', image: el.dataset.favImage || '', price: el.dataset.favPrice || '', kind: el.dataset.favKind || '' });
  const paintHearts = () => document.querySelectorAll('[data-gg-wishlist]').forEach((el) => { const on = !!el.dataset.favHandle && favStore.has(el.dataset.favHandle); el.setAttribute('aria-pressed', String(on)); el.classList.toggle('is-saved', on); });
  const esc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const HEART = '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M12 20.5 C5.5 16.2 2.5 12.6 2.5 8.9 C2.5 6.3 4.5 4.4 6.9 4.4 C8.7 4.4 10.3 5.4 11.2 6.9 L12 8.2 L12.8 6.9 C13.7 5.4 15.3 4.4 17.1 4.4 C19.5 4.4 21.5 6.3 21.5 8.9 C21.5 12.6 18.5 16.2 12 20.5 Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>';
  const paintFavs = () => {
    const list = favStore.list();
    document.querySelectorAll('[data-gg-favs]').forEach((slot) => {
      slot.hidden = list.length === 0;
      if (!list.length) { slot.innerHTML = ''; return; }
      const count = String(list.length).padStart(2, '0');
      const guest = !slot.dataset.customer;
      slot.innerHTML =
        `<div class="gg-favs__head"><span class="gg-kicker">${esc(slot.dataset.label || 'Favorites')}</span><span class="gg-favs__count">${count}</span></div>` +
        `<ul class="gg-favs__list">${list.map((f) => `<li class="gg-favs__item"><a class="gg-favs__media" href="${esc(f.url)}" tabindex="-1" aria-hidden="true">${f.image ? `<img src="${esc(f.image)}" alt="" loading="lazy" width="96" height="120">` : ''}</a><div class="gg-favs__info"><a class="gg-favs__name" href="${esc(f.url)}">${esc(f.title)}</a><span class="gg-favs__meta">${[f.kind, f.price].filter(Boolean).map(esc).join(' · ')}</span></div><button type="button" class="gg-favs__remove" data-fav-remove="${esc(f.handle)}" aria-label="${esc(slot.dataset.remove || 'Remove from favorites')} — ${esc(f.title)}">${HEART}</button></li>`).join('')}</ul>` +
        `<p class="gg-favs__note">${guest ? `Saved in this browser. <a href="${esc(slot.dataset.register || '/account/register')}">Create an account</a> to keep them, or <a href="${esc(slot.dataset.login || '/account/login')}">sign in</a>.` : 'Saved in this browser.'}</p>`;
    });
  };
  document.addEventListener('click', (e) => {
    const heart = e.target.closest('[data-gg-wishlist]');
    if (heart) { const item = favItem(heart); if (!item.handle) return; favStore.toggle(item); return; }
    const rm = e.target.closest('[data-fav-remove]');
    if (rm) { e.preventDefault(); favStore.remove(rm.dataset.favRemove); }
  });
  window.addEventListener('gg-favorites-change', () => { paintHearts(); paintFavs(); });
  window.addEventListener('storage', (e) => { if (e.key === FAV_KEY) { paintHearts(); paintFavs(); } });
  // the bag re-renders its inner on every cart change: a fresh, empty slot arrives and is filled again
  { const drawer = document.getElementById('GGCartDrawer'); if (drawer && 'MutationObserver' in window) new MutationObserver(() => { if (drawer.querySelector('[data-gg-favs]:empty')) paintFavs(); }).observe(drawer, { childList: true, subtree: true }); }
  // a heart kept under the old per-page flag moves into the list once
  try { const old = 'gg-wishlist:' + window.location.pathname; if (localStorage.getItem(old)) { const h = document.querySelector('[data-gg-wishlist][data-fav-handle]'); if (h && !favStore.has(h.dataset.favHandle)) favStore.toggle(favItem(h)); localStorage.removeItem(old); } } catch {}
  paintHearts(); paintFavs();

  /* mobile PDP: surface the sticky Add-to-bag bar only while the inline
     buy-row is off-screen, so the two controls never coexist. A scroll
     check (not IntersectionObserver) keeps this in step with the header
     dock and avoids any observer timing gaps. */
  (function () {
    const bar = document.querySelector('.gg-pdp__sticky-bar');
    const inline = document.querySelector('.gg-pdp__buy-row');
    if (!bar || !inline) return;
    let queued = false;
    const sync = () => {
      queued = false;
      const r = inline.getBoundingClientRect();
      const inView = r.bottom > 48 && r.top < window.innerHeight - 56;
      bar.classList.toggle('is-visible', !inView);
    };
    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(sync);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    sync();
  })();
})();

/* Lookbook sequences — "Play sequence" plays the look's film inside its own
   frame. Without a film (the design pass) the drifting plume runs as a
   twelve-second stand-in with a timecode, so the control can be tested.
   Delegated, so a re-rendered chapter needs no re-binding. */
(function () {
  const DURATION = 12;
  const fmt = (s) => { s = Math.max(0, Math.floor(s)); return String(Math.floor(s / 60)).padStart(2, '0') + ':' + String(s % 60).padStart(2, '0'); };
  const players = new WeakMap();
  function setup(media) {
    if (players.has(media)) return players.get(media);
    const scope = media.closest('.srlm-chapter__primary, .srlm-chapter__secondary, .srlm-chapter__cell') || media.parentElement || media;
    const btn = scope.querySelector('[data-sequence-play]');
    const label = scope.querySelector('[data-sequence-label]');
    const code = media.querySelector('[data-sequence-code]');
    const fill = media.querySelector('[data-sequence-fill]');
    // the film that plays lives in the sequence layer; a poster copy may sit in the frame
    const video = media.querySelector('.srlm-sequence video') || media.querySelector('video');
    const st = { playing: false, paused: false, raf: 0, start: 0, elapsed: 0 };
    const mark = (cls, on) => { media.classList.toggle(cls, on); scope.classList.toggle(cls, on); };
    const say = (pressed, text, aria) => { if (btn) { btn.setAttribute('aria-pressed', String(pressed)); btn.setAttribute('aria-label', aria); } if (label) label.textContent = text; };
    const paint = (t, d) => { if (code) code.textContent = fmt(t); if (fill) fill.style.transform = 'scaleX(' + (d ? Math.min(1, t / d) : 0) + ')'; };
    // back to rest: the poster returns, the next press starts from the top
    st.stop = () => {
      st.playing = false; st.paused = false; st.elapsed = 0; cancelAnimationFrame(st.raf);
      if (video) { try { video.pause(); } catch {} }
      mark('is-playing', false); mark('is-paused', false); say(false, 'Play sequence', 'Play the sequence'); paint(0, DURATION);
    };
    // pause HOLDS the frame: the sequence layer stays up, the clock stops, the next press carries on from here
    st.pause = () => {
      if (!st.playing) return;
      st.playing = false; st.paused = true; cancelAnimationFrame(st.raf);
      if (video) { try { video.pause(); } catch {} } else st.elapsed = (performance.now() - st.start) / 1000;
      mark('is-paused', true); say(false, 'Resume', 'Resume the sequence');
    };
    const tick = () => {
      if (!st.playing) return;
      const tr = video ? window.ggTrimOf(video) : null;
      const t = video ? Math.max(0, video.currentTime - tr.in) : (performance.now() - st.start) / 1000;
      const d = video && isFinite(video.duration) && video.duration > 0 ? (tr.out || video.duration) - tr.in : DURATION;
      paint(t, d);
      if (t >= d - (video ? 0.03 : 0)) { st.stop(); return; }
      st.raf = requestAnimationFrame(tick);
    };
    st.play = () => {
      const resume = st.paused;
      st.playing = true; st.paused = false; st.start = performance.now() - (resume ? st.elapsed * 1000 : 0);
      mark('is-playing', true); mark('is-paused', false); say(true, 'Pause', 'Pause the sequence');
      if (video) { try { if (!resume) video.currentTime = window.ggTrimOf(video).in; video.muted = true; const p = video.play(); if (p && p.catch) p.catch(() => {}); } catch {} }
      st.raf = requestAnimationFrame(tick);
    };
    if (video) video.addEventListener('ended', () => st.stop());
    // like every film: under half in view it pauses at once, holding its place; it waits for the visitor to carry on
    if ('IntersectionObserver' in window) new IntersectionObserver((es) => es.forEach((en) => {
      const full = Math.min(en.boundingClientRect.height || 1, window.innerHeight || 1);
      const share = en.isIntersecting ? en.intersectionRect.height / full : 0;
      if (share < 0.5 && st.playing) st.pause();
    }), { threshold: [0, .1, .2, .3, .4, .5, .6, .7, .8, .9, 1] }).observe(media);
    players.set(media, st);
    return st;
  }
  // a frame whose film changed (the Cover Lab) forgets its player
  document.addEventListener('gg-sequence-reset', (e) => { const m = e.target.closest && e.target.closest('.srlm-media'); if (m && players.has(m)) { const st = players.get(m); if (st.playing || st.paused) st.stop(); players.delete(m); } });
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-sequence-play]');
    if (!btn) {
      const m = e.target.closest && e.target.closest('.srlm-media.is-playing'); const st = m && players.get(m);
      if (st && !e.target.closest('button, a, input, select, label, [contenteditable="true"], .gg-lab, .gg-lab-hist, .gg-lab-frame')) { if (st.playing) st.pause(); else st.play(); }
      return;
    }
    const media = btn.closest('.srlm-media') || btn.parentElement.querySelector('.srlm-media');
    if (!media) return;
    e.preventDefault(); e.stopPropagation();
    const st = setup(media);
    if (st.playing) st.pause(); else st.play();
  });
})();

/* ---- framed media carry the picture's natural aspect (--ar) so the sheet can lay it at
   cover size under the frame; Liquid sets it where it knows, this fills the rest on load ---- */
(() => {
  const arOf = (m) => (m.tagName === 'VIDEO' ? (m.videoWidth && m.videoHeight ? m.videoWidth / m.videoHeight : 0) : (m.naturalWidth && m.naturalHeight ? m.naturalWidth / m.naturalHeight : 0));
  const tag = (m) => { const ar = arOf(m); if (ar) m.style.setProperty('--ar', ar.toFixed(4)); };
  const watch = (m) => { if (m.__arWatched || m.style.getPropertyValue('--ar')) return; m.__arWatched = true; if (arOf(m)) tag(m); else m.addEventListener(m.tagName === 'VIDEO' ? 'loadedmetadata' : 'load', () => tag(m), { once: true }); };
  const sweep = (root) => { if (root.querySelectorAll) root.querySelectorAll('.gg-framed img, .gg-framed video').forEach(watch); };
  sweep(document);
  // pictures that arrive later, and frames that become framed later, are swept too
  new MutationObserver((muts) => muts.forEach((mu) => {
    if (mu.type === 'attributes') { if (mu.target.classList.contains('gg-framed')) sweep(mu.target); return; }
    mu.addedNodes.forEach((n) => { if (n.nodeType === 1) { sweep(n); if (n.matches && n.matches('.gg-framed img, .gg-framed video')) watch(n); } });
  })).observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
})();

/* ---- the look card: "In this look" turns the frame over to its pieces; Escape or "Back to the look" turns it back ---- */
(() => {
  const hostOf = (t) => t.closest('.srlm-chapter__primary, .srlm-chapter__secondary, .srlm-chapter__cell');
  const turn = (host, on) => {
    host.classList.toggle('is-flipped', on);
    host.querySelectorAll('[data-look-flip][aria-expanded]').forEach((b) => b.setAttribute('aria-expanded', String(on)));
    const back = host.querySelector('.srlm-look-card__back');
    if (back) back.setAttribute('aria-hidden', String(!on));
    if (on) setTimeout(() => back?.querySelector('a, button')?.focus({ preventScroll: true }), 650);
    else host.querySelector('.srlm-look-tag, .srlm-look-open')?.focus({ preventScroll: true });
  };
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-look-flip]'); if (!t) return;
    const host = hostOf(t); if (!host) return;
    e.preventDefault(); turn(host, !host.classList.contains('is-flipped'));
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') document.querySelectorAll('.is-flipped:is(.srlm-chapter__primary, .srlm-chapter__secondary, .srlm-chapter__cell)').forEach((h) => turn(h, false)); });
})();

/* ---- the intro enters as the look does: with the film's first frame when there is one, else as the row comes into view; it leaves with the row and returns ---- */
(() => {
  // a frame's own words (a home pair's, snippets/experience-frame-words) enter with their own frame and its own film
  const hostOf = (intro) => (intro.classList.contains('srlm-intro--frame') && intro.closest('.srlm-media')) || intro.closest('[data-experience-chapter]');
  const arm = (intro) => {
    if (intro.__armed) return; intro.__armed = true;
    const chapter = hostOf(intro); if (!chapter || !intro.closest('[data-experience-chapter]')) return;
    let t = 0;
    const show = () => { clearTimeout(t); intro.classList.add('is-in'); };
    // a frame's words stand at its foot: they are watched themselves — in once they are well in view, out only once they
    // have wholly left it (measured on the whole frame they faded while still on screen, and came late scrolling back up)
    const own = intro.classList.contains('srlm-intro--frame');
    const target = own ? (intro.querySelector('.srlm-intro__stack') || chapter) : chapter;
    new IntersectionObserver((es) => es.forEach((en) => {
      if (own ? en.intersectionRatio === 0 : !en.isIntersecting) { clearTimeout(t); intro.classList.remove('is-in'); return; }
      if (own && en.intersectionRatio < 0.6) return;
      const film = chapter.querySelector('gg-film video, video.gg-lab__media--base');
      if (film && film.paused) { t = setTimeout(show, 1400); film.addEventListener('playing', () => { clearTimeout(t); t = setTimeout(show, 180); }, { once: true }); }
      else t = setTimeout(show, 120);
    }), { threshold: own ? [0, 0.6] : 0.35 }).observe(target);
  };
  // every look carries a card in the captions layout; otherwise only the looks that switched theirs on. The layout is
  // the page's (the lookbook's <html>) or a chapter's own (the home's, .gg-hchapter.gg-named): a home chapter's name,
  // shown by the CSS and never armed, stood invisible on the draft (2026-09-30)
  const CARDS = () => (document.documentElement.classList.contains('gg-named') ? '.srlm-intro' : '.srlm-intro.is-on, .gg-named .srlm-intro');
  const sweep = () => document.querySelectorAll(CARDS()).forEach(arm);
  sweep();
  new MutationObserver(sweep).observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
  // and by the scroll itself, so the title card never waits on an observer: in view by a third, it enters
  let raf = 0;
  const check = () => { raf = 0; document.querySelectorAll(CARDS()).forEach((intro) => { const ch = hostOf(intro); if (!ch || !intro.closest('[data-experience-chapter]')) return; const own = intro.classList.contains('srlm-intro--frame'); const r = (own ? (intro.querySelector('.srlm-intro__stack') || ch) : ch).getBoundingClientRect(); const vis = Math.max(0, Math.min(r.bottom, innerHeight) - Math.max(r.top, 0)); const ratio = r.height ? vis / r.height : 0; if (ratio >= (own ? 0.6 : 0.35) && !intro.classList.contains('is-in')) { const film = ch.querySelector('gg-film video, video.gg-lab__media--base'); if (!film || !film.paused || intro.__waited) intro.classList.add('is-in'); else { intro.__waited = true; setTimeout(check, 1400); } } else if (ratio === 0 && intro.classList.contains('is-in')) { intro.classList.remove('is-in'); intro.__waited = false; } }); };
  const ask = () => { if (!raf) raf = setTimeout(check, 80); };
  addEventListener('scroll', ask, { passive: true }); addEventListener('resize', ask); setTimeout(check, 600);
})();

/* ---- BESIDE: a look that stands beside its words. Its door, the tag on its picture and "Back to the look" all do one
   thing: turn BOTH halves — the picture a beat before the words on the way in, the words first on the way back. Caught
   in the capture phase so the two single-card handlers below never see these presses. ---- */
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const set = (host, on) => { if (!host) return; host.classList.toggle('is-flipped', on); host.querySelectorAll('[data-narr-flip][aria-expanded], [data-look-flip][aria-expanded]').forEach((b) => b.setAttribute('aria-expanded', String(on))); host.querySelectorAll('.srlm-narr__back, .srlm-look-card__back').forEach((b) => b.setAttribute('aria-hidden', String(!on))); host.dispatchEvent(new CustomEvent('gg-flip', { bubbles: true })); };
  const turn = (ch, on) => {
    const pic = ch.querySelector(':scope > .srlm-chapter__primary'); const words = ch.querySelector(':scope > .srlm-chapter__narrative');
    ch.classList.toggle('is-beside-open', on);
    const first = on ? pic : words; const second = on ? words : pic;
    set(first, on); setTimeout(() => set(second, on), reduced.matches ? 0 : 160);
    if (on) setTimeout(() => (words.querySelector('[data-beside-close]') || {}).focus?.({ preventScroll: true }), 900); else setTimeout(() => (words.querySelector('[data-narr-flip]') || {}).focus?.({ preventScroll: true }), 700);
  };
  document.addEventListener('click', (e) => {
    const t = e.target.closest && e.target.closest('[data-narr-flip], [data-look-flip], [data-beside-close]'); if (!t) return;
    const ch = t.closest('.srlm-chapter--beside'); if (!ch) return;
    e.preventDefault(); e.stopPropagation();
    turn(ch, t.hasAttribute('data-beside-close') ? false : !ch.classList.contains('is-beside-open'));
  }, true);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') document.querySelectorAll('.srlm-chapter--beside.is-beside-open').forEach((ch) => turn(ch, false)); }, true);
  window.ggBeside = { turn };
})();

/* ---- the words card: "Featured pieces" turns the story over to the look's frames and pieces; Escape or "Back to the words" turns it back ---- */
(() => {
  const turn = (host, on) => { host.classList.toggle('is-flipped', on); host.querySelectorAll('[data-narr-flip][aria-expanded]').forEach((b) => b.setAttribute('aria-expanded', String(on))); const back = host.querySelector('.srlm-narr__back'); if (back) back.setAttribute('aria-hidden', String(!on)); if (on) setTimeout(() => back?.querySelector('a, button')?.focus({ preventScroll: true }), 550); if (on && document.documentElement.classList.contains('gg-notes')) setTimeout(() => { const r = host.getBoundingClientRect(); const over = r.bottom - innerHeight + 24; if (over > 0) scrollBy({ top: Math.min(over, Math.max(0, r.top - 120)), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }); }, 920); };
  document.addEventListener('click', (e) => { const t = e.target.closest('[data-narr-flip]'); if (!t) return; const host = t.closest('.srlm-chapter__narrative'); if (!host) return; e.preventDefault(); turn(host, !host.classList.contains('is-flipped')); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') document.querySelectorAll('.srlm-chapter__narrative.is-flipped').forEach((h) => turn(h, false)); });
})();

/* ---- the card's number and name follow the page: chapters get renumbered and renamed in the Studio ---- */
(() => {
  const sync = () => document.querySelectorAll('#experience-chapters [data-experience-chapter]').forEach((ch) => {
    const stack = ch.querySelector('.srlm-intro__stack'); if (!stack) return;
    const name = ch.querySelector('.srlm-chapter__narrative [data-cp-edit$=".kicker"]') || ch.querySelector('[data-cp-edit$=".kicker"]');
    const n = ch.dataset.chapterNumber || ''; const t = (name ? name.textContent : ch.dataset.chapterTitle || '').trim();
    if (stack.dataset.lookNumber !== n) stack.dataset.lookNumber = n;
    if (stack.dataset.lookName !== t) stack.dataset.lookName = t;
    const num = stack.querySelector('.srlm-intro__num'); if (num && num.textContent !== n) num.textContent = n;
    const tl = ch.querySelector('.srlm-chapter__tagline'); if (tl && tl.dataset.n !== n) tl.dataset.n = n;   // the number that opens the descriptor follows the page's numbering
    const nm = stack.querySelector('.srlm-intro__name'); if (nm && nm !== document.activeElement && t && nm.textContent.trim() !== t) nm.textContent = t;
  });
  let t = 0; const ask = () => { clearTimeout(t); t = setTimeout(sync, 120); };
  sync();
  const host = document.getElementById('experience-chapters'); if (!host) return;
  host.addEventListener('input', ask);
  new MutationObserver(ask).observe(host, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-chapter-number'] });
})();

/* Featured looks: a whole-card turn into an isolated, two-row collection. */
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const states = new WeakMap();
  const animate = async (node, from, to, duration) => {
    if (reduced.matches || !node.animate) return;
    const entering = to === 0;
    const isDialog = node.matches('dialog');
    const resting = { transform: 'translate3d(0,0,0) scale(1)', opacity: 1 };
    const offset = { transform: isDialog ? 'translate3d(0,24px,0) scale(.985)' : 'translate3d(0,0,0) scale(.985)', opacity: 0 };
    const animation = node.animate(entering ? [offset, resting] : [resting, offset], {
      duration: entering ? (isDialog ? 680 : 420) : (isDialog ? 300 : 220),
      easing: 'cubic-bezier(.22,1,.36,1)', fill: 'forwards'
    });
    try { await animation.finished; } catch (_) {} finally { animation.cancel(); }
  };
  function setup(chapter) {
    if (states.has(chapter)) return states.get(chapter);
    const back = chapter.querySelector('.srlm-narr__back');
    const trigger = chapter.querySelector('[data-featured-flip]');
    const grid = back?.querySelector('.srlm-featured__grid');
    if (!back || !trigger || !grid) return;
    const home = document.createComment('featured look reverse face');
    back.before(home);
    const dialog = document.createElement('dialog');
    dialog.className = 'srlm-featured-focus';
    dialog.setAttribute('aria-label', chapter.querySelector('.srlm-featured__heading')?.textContent.trim() || 'Explore the look');
    chapter.append(dialog);
    const state = { chapter, back, trigger, grid, home, dialog, busy: false, open: false };
    states.set(chapter, state);
    const footer = back.querySelector('.srlm-featured__top');
    const count = footer.querySelector('span');
    count.className = 'srlm-featured-focus__count';
    count.setAttribute('aria-live', 'polite');
    const heading = document.createElement('h2');
    heading.className = 'srlm-featured-focus__title';
    heading.id = 'focus-title-' + chapter.id;
    heading.textContent = dialog.getAttribute('aria-label');
    const identity = document.createElement('header');
    identity.className = 'srlm-featured-focus__identity';
    const signature = document.createElement('p');
    signature.className = 'srlm-featured-focus__signature';
    signature.textContent = 'BY SURREALIUM';
    identity.append(heading, signature, count);
    dialog.prepend(identity);
    dialog.setAttribute('aria-labelledby', heading.id);
    const sourceVideo = chapter.querySelector('.srlm-chapter__primary video');
    if (sourceVideo) {
      const film = document.createElement('div');
      film.className = 'srlm-featured-focus__film';
      film.setAttribute('aria-label', heading.textContent + ' — film');
      const video = document.createElement('video');
      video.muted = true; video.loop = true; video.playsInline = true;
      video.setAttribute('muted', ''); video.setAttribute('playsinline', '');
      video.preload = 'metadata'; video.poster = sourceVideo.poster;
      video.src = sourceVideo.currentSrc || sourceVideo.src || sourceVideo.querySelector('source')?.src || '';
      film.append(video); grid.prepend(film);
      state.focusVideo = video; state.sourceVideo = sourceVideo;
      const mobile = matchMedia('(max-width: 749px)');
      const placeFilm = () => {
        if (mobile.matches) { identity.prepend(film); film.setAttribute('aria-hidden', 'true'); }
        else { grid.prepend(film); film.removeAttribute('aria-hidden'); }
        state.mobileFilm = mobile.matches;
        if (dialog.open) { grid.scrollLeft = 0; state.update?.(); }
      };
      mobile.addEventListener('change', placeFilm);
      placeFilm();
    }
    const playFilm = () => {
      const video = state.focusVideo;
      if (!video) return;
      const box = video.getBoundingClientRect(), rail = grid.getBoundingClientRect();
      const visible = dialog.open && !document.hidden && (state.mobileFilm || (box.right > rail.left + 20 && box.left < rail.right - 20));
      if (visible && !reduced.matches) video.play().catch(() => {});
      else video.pause();
    };
    state.playFilm = playFilm;
    document.addEventListener('visibilitychange', playFilm);
    const pageStep = () => {
      const column = grid.querySelector('.srlm-featured__item')?.getBoundingClientRect().width || grid.clientWidth;
      return column * Math.max(1, Math.floor((grid.clientWidth + 1) / column));
    };
    const nav = (direction, label, glyph) => {
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'srlm-featured-focus__nav gg-rail__arrow';
      button.setAttribute('aria-label', label);
      button.innerHTML = '<svg class="gg-sparkle" viewBox="0 0 42 84" fill="none" aria-hidden="true"><path d="' + (direction < 0 ? 'M37 8 Q30 30 5 42 Q30 54 37 76' : 'M5 8 Q12 30 37 42 Q12 54 5 76') + '" stroke="currentColor" stroke-width="1.5" vector-effect="non-scaling-stroke"/></svg>';
      button.addEventListener('click', () => {
        const max = Math.max(0, grid.scrollWidth - grid.clientWidth);
        const left = direction < 0 && grid.scrollLeft <= 2 ? max : direction > 0 && grid.scrollLeft >= max - 2 ? 0 : Math.max(0, Math.min(max, grid.scrollLeft + direction * pageStep()));
        grid.scrollTo({ left, behavior: reduced.matches ? 'instant' : 'smooth' });
      });
      return button;
    };
    const prev = nav(-1, 'Previous pieces', '←');
    const next = nav(1, 'Next pieces', '→');
    footer.append(prev, next);
    const update = () => {
      const max = grid.scrollWidth - grid.clientWidth;
      prev.disabled = false;
      next.disabled = false;
      const step = pageStep();
      const total = Math.max(1, Math.ceil(Math.max(0, max - 2) / step) + 1);
      const current = grid.scrollLeft >= max - 2 ? total : Math.min(total, Math.round(grid.scrollLeft / step) + 1);
      const lookNumber = [...document.querySelectorAll('.srlm-chapter--featured-look')].indexOf(chapter) + 1;
      count.textContent = matchMedia('(min-width: 750px)').matches
        ? String(lookNumber).padStart(2, '0')
        : `Piece ${current} of ${grid.querySelectorAll('.srlm-featured__item').length}`;
      dialog.toggleAttribute('data-carousel-overflow', total > 1);
      playFilm();
    };
    state.update = update;
    grid.addEventListener('scroll', update, { passive: true });
    new ResizeObserver(() => { if (dialog.open) update(); }).observe(grid);
    grid.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault(); (event.key === 'ArrowLeft' ? prev : next).click();
    });
    dialog.addEventListener('cancel', event => { event.preventDefault(); close(state); });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const r = dialog.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) close(state);
    });
    dialog.addEventListener('close', () => restore(state));
    return state;
  }
  function restore(state) {
    state.focusVideo?.pause();
    state.home.after(state.back);
    state.back.setAttribute('inert', ''); state.back.setAttribute('aria-hidden', 'true');
    state.trigger.setAttribute('aria-expanded', 'false');
    state.chapter.classList.remove('is-featured-open', 'is-featured-turning');
    if (state.open) {
      document.documentElement.style.overflow = state.overflow;
      document.documentElement.classList.remove('srlm-featured-focus-active');
      state.playing?.forEach(video => { if (video.isConnected) video.play().catch(() => {}); });
    }
    state.open = false;
    state.trigger.focus({ preventScroll: true });
  }
  async function open(state) {
    if (state.busy || state.open) return;
    state.busy = true;
    const { chapter, back, dialog, trigger, grid } = state;
    dialog.querySelector('.srlm-featured-focus__title').textContent = chapter.querySelector('.srlm-featured__heading')?.textContent.trim() || dialog.getAttribute('aria-label');
    try {
      const images = [...back.querySelectorAll('img')];
      images.forEach(img => { img.loading = 'eager'; });
      await Promise.race([
        Promise.allSettled(images.map(img => img.decode?.())),
        new Promise(resolve => setTimeout(resolve, 350))
      ]);
      dialog.append(back);
      back.removeAttribute('inert'); back.setAttribute('aria-hidden', 'false');
      trigger.setAttribute('aria-expanded', 'true');
      state.overflow = document.documentElement.style.overflow;
      state.playing = [...chapter.querySelectorAll('video')].filter(video => video !== state.focusVideo && !video.paused);
      if (state.focusVideo) {
        try { state.focusVideo.currentTime = state.sourceVideo.currentTime; } catch (_) {}
      }
      state.playing.forEach(video => video.pause());
      state.open = true;
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.classList.add('srlm-featured-focus-active');
      dialog.style.opacity = '0';
      dialog.showModal(); grid.scrollLeft = 0;
      const motions = [];
      if (!reduced.matches) {
        const reveal = (node, delay, lift = 8) => motions.push(node.animate([
          { opacity: 0, transform: `translateY(${lift}px)` },
          { opacity: 1, transform: 'translateY(0)' }
        ], { duration: 700, delay, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'both' }));
        reveal(dialog.querySelector('.srlm-featured-focus__identity'), 60, 5);
        [...grid.children].forEach((item, index) => reveal(item, 150 + Math.min(index, 8) * 65));
        reveal(back.querySelector('.srlm-featured__top'), 240, 0);
        motions.push(dialog.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: 420, easing: 'ease-out', fill: 'both'
        }));
      }
      dialog.style.removeProperty('opacity');
      state.update();
      back.querySelector('[data-featured-close]')?.focus({ preventScroll: true });
      state.motions = motions;
      Promise.allSettled(motions.map(motion => motion.finished)).then(() => {
        motions.forEach(motion => motion.cancel());
      });
    } catch (error) {
      dialog.style.removeProperty('opacity');
      if (dialog.open) dialog.close(); else restore(state);
      console.warn('Featured look could not open', error);
    } finally { state.busy = false; }
  }
  async function close(state) {
    if (state.busy || !state.open) return;
    state.busy = true;
    try {
      if (!reduced.matches) {
        const fade = state.dialog.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 240, easing: 'ease-in', fill: 'forwards' });
        try { await fade.finished; } catch (_) {}
        state.dialog.close(); fade.cancel();
      } else state.dialog.close();
      state.motions?.forEach(motion => motion.cancel());
    } finally { state.busy = false; }
  }
  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-featured-flip], [data-featured-close]');
    const chapter = button?.closest('.srlm-chapter--featured-look');
    if (!chapter) return;
    const state = setup(chapter); if (!state) return;
    event.preventDefault(); event.stopPropagation();
    if (button.hasAttribute('data-featured-flip')) open(state); else close(state);
  }, true);
})();

/* ---- a look's wall past two rows is a two-row carousel (editorial-experience.css, THE WALL SCROLLS PAST TWO ROWS): the wall
   says where it is (data-wall-scroll = start · mid · end; absent while everything fits), its edge keys step a page, the
   arrow keys step while focus is in it, and a mouse may drag it (a drag never opens the piece it started on) ---- */
(() => {
  const grids = new WeakSet();
  const gridOf = (wall) => wall && wall.querySelector('[data-narr-grid]');
  const state = (wall) => {
    const g = gridOf(wall); if (!g) return;
    const max = g.scrollWidth - g.clientWidth;
    if (max <= 2) { wall.removeAttribute('data-wall-scroll'); return; }
    const x = Math.abs(g.scrollLeft);
    wall.setAttribute('data-wall-scroll', x <= 2 ? 'start' : x >= max - 2 ? 'end' : 'mid');
  };
  const colOf = (g) => { const c = g.querySelector('.gg-cgrid__cell'); return c ? c.getBoundingClientRect().width : g.clientWidth / 4; };
  const step = (wall, dir) => {
    const g = gridOf(wall); if (!g) return;
    const col = colOf(g); const page = Math.max(col, Math.round(g.clientWidth / col) * col);
    const max = Math.max(0, g.scrollWidth - g.clientWidth);
    const left = dir < 0 && g.scrollLeft <= 2 ? max : dir > 0 && g.scrollLeft >= max - 2 ? 0 : Math.max(0, Math.min(max, g.scrollLeft + dir * page));
    g.scrollTo({ left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };
  const watch = (wall) => {
    const g = gridOf(wall); if (!g) return;
    if (grids.has(g)) { state(wall); return; }
    grids.add(g);
    g.addEventListener('scroll', () => state(wall), { passive: true });
    if (window.ResizeObserver) new ResizeObserver(() => state(wall)).observe(g);
    if (window.MutationObserver) new MutationObserver(() => state(wall)).observe(g, { childList: true });
    // a mouse drag: the snap is lifted while the hand holds it and given back on release, at the nearest column
    let drag = null;
    g.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse' || e.button !== 0 || !wall.hasAttribute('data-wall-scroll')) return; drag = { x: e.clientX, left: g.scrollLeft, moved: false, id: e.pointerId }; });
    g.addEventListener('pointermove', (e) => {
      if (!drag || e.pointerId !== drag.id) return;
      const dx = e.clientX - drag.x;
      if (!drag.moved && Math.abs(dx) < 6) return;
      if (!drag.moved) { drag.moved = true; g.classList.add('is-dragging'); try { g.setPointerCapture(e.pointerId); } catch (_) { /* no capture */ } }
      g.scrollLeft = drag.left - dx;
    });
    const end = () => {
      if (!drag) return; const moved = drag.moved; drag = null; if (!moved) return;
      const x = g.scrollLeft; g.classList.remove('is-dragging'); g.scrollLeft = x;
      const col = colOf(g); if (col) g.scrollTo({ left: Math.round(x / col) * col, behavior: 'smooth' });
      g.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); }, { capture: true, once: true });
      setTimeout(() => g.classList.contains('is-dragging') || state(wall), 400);
    };
    g.addEventListener('pointerup', end); g.addEventListener('pointercancel', end);
    // a link or picture held by the mouse would otherwise be picked up by the browser's own drag, and a link dropped back on the page opens it
    g.addEventListener('dragstart', (e) => { if (wall.hasAttribute('data-wall-scroll')) e.preventDefault(); });
    state(wall);
  };
  const all = () => document.querySelectorAll('[data-narr-wall]').forEach(watch);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', all); else all();
  document.addEventListener('click', (e) => {
    const t = e.target.closest ? e.target : null; if (!t) return;
    const k = t.closest('[data-narr-flip]'); if (k) { const w = k.closest('.srlm-chapter__narrative')?.querySelector('[data-narr-wall]'); if (w) requestAnimationFrame(() => watch(w)); }
    const b = t.closest('[data-wall-step]'); if (!b) return;
    e.preventDefault(); const wall = b.closest('[data-narr-wall]'); if (!wall) return; watch(wall); step(wall, Number(b.dataset.wallStep) || 1);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    const wall = e.target.closest && e.target.closest('[data-narr-wall]'); if (!wall || !wall.hasAttribute('data-wall-scroll')) return;
    e.preventDefault(); step(wall, e.key === 'ArrowRight' ? 1 : -1);
  });
})();

/* ----------------------------------------------------------
   The cover as a title sequence (sections/home-hero, layout `sequence`): its opening, armed by theme.liquid's head
   (html.gg-seq-opening) before the first paint. Here the letters are laid exactly on the open wordmark (the dock has
   placed it by now), the visit is marked so it plays once, and it is ended once it has played, or at once if the page
   is scrolled away. Without a sequence cover the class is dropped at once.
   ---------------------------------------------------------- */
(() => {
  const root = document.documentElement;
  const run = () => {
    const cover = document.querySelector('[data-gg-hero][data-cover-sequence]');
    // Maison, wordmark: one line that turns — the lookbook's, then a promise from the announcement (its one-at-a-time
    // messages), the lookbook's again, the next promise… (6s on the lookbook, 4s on a promise; held while the pointer or
    // the focus is on it). The lookbook's line keeps its heading readable; a promise not shown is out of reach.
    const rotor = cover && cover.querySelector('[data-cover-rotor]');
    const promises = document.querySelector('.gg-section-marquee [data-gg-announce]');
    if (rotor && promises && !rotor.__ggTurn) {
      rotor.__ggTurn = true;
      const lead = rotor.querySelector('.gg-cover__slide');
      const slides = [...promises.querySelectorAll('.gg-marquee__slide')].map((s) => {
        const slide = document.createElement('div');
        slide.className = 'gg-cover__slide';
        slide.setAttribute('aria-hidden', 'true');
        slide.inert = true;
        slide.append(...s.childNodes);
        rotor.appendChild(slide);
        return slide;
      });
      if (slides.length) {
        const order = slides.flatMap((s) => [lead, s]);
        let at = 0;
        let held = false;
        const show = (el) => {
          [lead, ...slides].forEach((s) => s.classList.toggle('is-on', s === el));
          slides.forEach((s) => { s.inert = s !== el; s.setAttribute('aria-hidden', s === el ? 'false' : 'true'); });
          lead.querySelectorAll('a, button').forEach((a) => (el === lead ? a.removeAttribute('tabindex') : a.setAttribute('tabindex', '-1')));
        };
        rotor.addEventListener('pointerenter', () => { held = true; });
        rotor.addEventListener('pointerleave', () => { held = false; });
        rotor.addEventListener('focusin', () => { held = true; });
        rotor.addEventListener('focusout', () => { held = false; });
        const next = () => {
          if (!rotor.isConnected) return;
          if (!held && !document.hidden) { at = (at + 1) % order.length; show(order[at]); }
          setTimeout(next, order[at] === lead ? 6000 : 4000);
        };
        setTimeout(next, 6000);
      }
    }
    // only the Nordic style opens through the wordmark: the others keep it in the bar
    if (!cover || (cover.dataset.coverStyle || 'nordic') !== 'nordic') { root.classList.remove('gg-seq-opening'); return; }
    if (!root.classList.contains('gg-seq-opening')) return;
    try { sessionStorage.setItem('ggSeqSeen', '1'); } catch (_) { /* private mode: it plays again */ }
    const stage = cover.querySelector('.gg-cover__stage');
    const align = () => {
      const logo = document.querySelector('[data-dock-logo]');
      if (!logo || !stage) return;
      const l = logo.getBoundingClientRect();
      const s = stage.getBoundingClientRect();
      if (!l.width || !l.height) return;
      cover.style.setProperty('--seq-mx', `${(l.left - s.left).toFixed(1)}px`);
      cover.style.setProperty('--seq-my', `${(l.top - s.top).toFixed(1)}px`);
      cover.style.setProperty('--seq-mw', `${l.width.toFixed(1)}px`);
      cover.style.setProperty('--seq-cx', `${(l.left - s.left + l.width / 2).toFixed(1)}px`);
      cover.style.setProperty('--seq-cy', `${(l.top - s.top + l.height / 2).toFixed(1)}px`);
      if (logo.currentSrc) cover.style.setProperty('--seq-mask', `url("${logo.currentSrc}")`);
    };
    align();
    requestAnimationFrame(() => requestAnimationFrame(align));
    document.querySelector('[data-dock-logo]')?.addEventListener('load', align, { once: true });
    window.addEventListener('resize', align, { passive: true });
    let ended = false;
    const away = () => { if (window.scrollY > window.innerHeight * .5) end(); };
    const end = () => {
      if (ended) return;
      ended = true;
      root.classList.remove('gg-seq-opening');
      window.removeEventListener('resize', align);
      window.removeEventListener('scroll', away);
    };
    window.addEventListener('scroll', away, { passive: true });
    setTimeout(end, 3800);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
})();

/* ----------------------------------------------------------
   The announcement, one message at a time (sections/announcement-marquee, mode `rotate`): each of the house's promises
   in turn, every 4.5 seconds — held while the pointer or the focus is on it, and while the tab is hidden. The message
   not shown is out of reach (inert) and out of the reading order (aria-hidden).
   ---------------------------------------------------------- */
(() => {
  const arm = (root) => {
    if (root.__ggAnnounce) return;
    root.__ggAnnounce = true;
    const slides = [...root.querySelectorAll('.gg-marquee__slide')];
    if (slides.length < 2) return;
    let at = 0;
    let held = false;
    const show = (n) => slides.forEach((slide, k) => {
      const on = k === n;
      slide.classList.toggle('is-on', on);
      slide.toggleAttribute('inert', !on);
      slide.setAttribute('aria-hidden', on ? 'false' : 'true');
    });
    root.addEventListener('pointerenter', () => { held = true; });
    root.addEventListener('pointerleave', () => { held = false; });
    root.addEventListener('focusin', () => { held = true; });
    root.addEventListener('focusout', () => { held = false; });
    const turn = setInterval(() => {
      if (!root.isConnected) { clearInterval(turn); return; }
      if (held || document.hidden) return;
      at = (at + 1) % slides.length;
      show(at);
    }, 4500);
  };
  const run = () => document.querySelectorAll('[data-gg-announce]').forEach(arm);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
  document.addEventListener('shopify:section:load', run);
})();
